import type { AttackDef, Boss, SpriteKey } from './types'
import { AWOKEN_RUSH_STUDY_TIMELINE, awokenChaseStudyTimeline } from './awoken_rush_study'
import { FAJIN_MOTION_TIMELINES, FAJIN_RECOVERY_MS, type FajinMotion } from './executioner_fajin_motion'
import { getAttack } from './attacks'
import { houndKitV2Enabled } from './hound_kit'
import { HOUND_PASS_TURN_FRONT_AT, HOUND_PASS_TURN_LOOK_AT } from './hound_pass_through'
import { CANNON_FAJIN_TIMELINE } from './executioner_cannon_fajin'
import { ROAM, CHOREO_CHARGE_MS, CHOREO_ACTIVE_MS, CHOREO_ACTIVE_START_SEC, CHOREO_ROAR_SEC, BITE_STRIKE_PROGRESS, WALL_L, WALL_R, wallPoseVariant, wallPoseUsesKickArt, swoopLook, pitchLook } from './awoken_rampage_choreo'

export type BossAnimPhase =
  | 'telegraph' | 'active' | 'comboGap' | 'recovery' | 'idle' | 'staggered' | 'defeat'

export type BossPoseTag = 'anticipation' | 'contact' | 'followThrough' | 'recover'

// 手続きチャンネルの補間カーブ (2026-07-29 追加)。 画像のクロスフェードとは別軸。
export type BossPoseEase = 'step' | 'linear' | 'easeIn' | 'easeOut' | 'easeInOut'

// 1 キーポーズ。 image = 既存 SpriteKey か追加ポーズのキー (sprites.ts cache が解決)。
//
// ★2026-07-29 追加: offset / scale / rot / ease の「手続きチャンネル」。
//   設計の要点 = **絵はパッと切り替わってよい。切り替わったその1枚が空間を動く。**
//   旧方式は補間手段が「2枚のαディゾルブ」しか無く、輪郭の違う2枚が溶けて幽霊絵になるため
//   実データの 72% が hold:true で補間を自ら切っていた (= 絵を差し替えるだけで動きが出ない)。
//   画像はハードカットのまま、位置・拡縮・回転だけをカーブ補間して足すことで、
//   「キーが主・手続きが従」を保ったまま動きを出せる。
//
//   ⚠️ 全フィールド optional。 既存 301 キーは 1 文字も変えずに従来どおり動く。
//   ⚠️ offset は**描画専用**。 boss.x / strike 判定 / 影は動かさない (既存の lean・recoil と同じ規約)。
//      判定と見た目の契約は contact マーカーが持つ。ここを動かして当たり判定をズラさないこと。
export type BossPoseKey = {
  phase: BossAnimPhase
  at: number            // phase 内 progress 0..1
  image: string
  hold?: boolean        // 次キーへ「画像を」補間せず保持 (= 手続きチャンネルは別途 ease で動く)
  tag?: BossPoseTag
  hitIndex?: number     // 連撃: 未指定=全ヒット共通 / 連撃は 0,1,2…

  // --- 手続きチャンネル (加算。省略時は無変位) ---
  ease?: BossPoseEase   // このキー→次キーの補間カーブ (既定 'linear')
  offset?: { x?: number; y?: number }   // px。 x は「ボスの前方向」が正 (facing で自動反転)
  scale?: { x?: number; y?: number }    // 乗算。 squash & stretch (非一様でよい)
  rot?: number          // deg。 正 = 前方向へ倒れる。 ピボットは足元 (接地点)
}

export type BossAttackTimeline = BossPoseKey[]

// タイムラインが解決した手続き変位。 sprites.ts が既存の shake/atkScale へ**加算**する。
export type BossPoseMotion = { offsetX: number; offsetY: number; scaleX: number; scaleY: number; rot: number }
export const NO_POSE_MOTION: BossPoseMotion = { offsetX: 0, offsetY: 0, scaleX: 1, scaleY: 1, rot: 0 }

function applyEase(t: number, ease: BossPoseEase | undefined): number {
  switch (ease) {
    case 'step':      return 0
    case 'easeIn':    return t * t
    case 'easeOut':   return 1 - (1 - t) * (1 - t)
    case 'easeInOut': return t < 0.5 ? 2 * t * t : 1 - 2 * (1 - t) * (1 - t)
    default:          return t   // 'linear' と未指定
  }
}

function hasMotion(k: BossPoseKey): boolean {
  return k.offset !== undefined || k.scale !== undefined || k.rot !== undefined
}

// phase を 1 次元の順序値に。 攻撃タイムラインは telegraph→active→comboGap→recovery の順で進む。
const PHASE_RANK: Record<BossAnimPhase, number> = {
  telegraph: 0, active: 1, comboGap: 2, recovery: 3, idle: 0, staggered: 0, defeat: 0,
}
function clamp01(x: number): number { return Math.max(0, Math.min(1, x)) }
function pos(phase: BossAnimPhase, at: number): number { return PHASE_RANK[phase] + clamp01(at) }

export type TimelineFrame = { current: BossPoseKey; next?: BossPoseKey; blend: number }

// 現在の (phase, progress, hitIndex) から、 表示すべき現在キー + 補間先 + 比率を返す。
//   - hitIndex に合致するキーだけ対象 (未指定キーは全 hit 共通)。
//   - current = pos が現在位置以下で最大のキー (無ければ先頭)。
//   - blend = current→next の線形補間比 (current.hold なら 0)。
export function selectTimelineFrame(
  keys: BossAttackTimeline, phase: BossAnimPhase, progress: number, hitIndex: number | undefined,
): TimelineFrame | null {
  const ks = keys.filter((k) => k.hitIndex === undefined || k.hitIndex === hitIndex)
  if (ks.length === 0) return null
  const sorted = [...ks].sort((a, b) => pos(a.phase, a.at) - pos(b.phase, b.at))
  const here = pos(phase, progress)
  let idx = 0
  for (let i = 0; i < sorted.length; i++) { if (pos(sorted[i]!.phase, sorted[i]!.at) <= here + 1e-9) idx = i }
  const current = sorted[idx]!
  const next = sorted[idx + 1]
  if (!next || current.hold) return { current, next, blend: 0 }
  const a = pos(current.phase, current.at)
  const b = pos(next.phase, next.at)
  const blend = b > a ? clamp01((here - a) / (b - a)) : 0
  return { current, next, blend }
}

// 手続きチャンネルの解決 (2026-07-29)。 現在キー→次キーの offset/scale/rot を ease で補間して返す。
//
//   ★`hold` は無視する。 hold は「画像を溶かさない」指示であって「動かさない」指示ではない。
//     ここが案Aの核心 = 絵はパッと切り替わり、その1枚が滑らかに動く。
//     動かしたくないキーは ease:'step' を書く (明示)。
//
//   ★motion を1つも持たないタイムライン (既存の全ボス) は即 NO_POSE_MOTION を返す = 実質ゼロコスト、
//     かつ描画結果が1画素も変わらないことが型と経路の両方で保証される。
export function resolveTimelineMotion(
  keys: BossAttackTimeline, phase: BossAnimPhase, progress: number, hitIndex: number | undefined,
): BossPoseMotion {
  // The candidate's render motion belongs to the release switch as well.
  if (keys === BOSS_ATTACK_TIMELINES['dream-hound']?.houndPounce && !houndKitV2Enabled()) return NO_POSE_MOTION
  const ks = keys.filter((k) => k.hitIndex === undefined || k.hitIndex === hitIndex)
  if (ks.length === 0 || !ks.some(hasMotion)) return NO_POSE_MOTION
  const sorted = [...ks].sort((a, b) => pos(a.phase, a.at) - pos(b.phase, b.at))
  const here = pos(phase, progress)
  let idx = 0
  for (let i = 0; i < sorted.length; i++) { if (pos(sorted[i]!.phase, sorted[i]!.at) <= here + 1e-9) idx = i }
  const cur = sorted[idx]!
  const nxt = sorted[idx + 1]

  const curM = motionOf(cur)
  if (!nxt) return curM
  const a = pos(cur.phase, cur.at)
  const b = pos(nxt.phase, nxt.at)
  const raw = b > a ? clamp01((here - a) / (b - a)) : 0
  const t = applyEase(raw, cur.ease)
  const nxtM = motionOf(nxt)
  return {
    offsetX: curM.offsetX + (nxtM.offsetX - curM.offsetX) * t,
    offsetY: curM.offsetY + (nxtM.offsetY - curM.offsetY) * t,
    scaleX: curM.scaleX + (nxtM.scaleX - curM.scaleX) * t,
    scaleY: curM.scaleY + (nxtM.scaleY - curM.scaleY) * t,
    rot: curM.rot + (nxtM.rot - curM.rot) * t,
  }
}

// キー1つの実効変位。 未指定チャンネルは「無変位」= 隣のキーが動かしていれば自然に 0 へ戻る。
function motionOf(k: BossPoseKey): BossPoseMotion {
  return {
    offsetX: k.offset?.x ?? 0,
    offsetY: k.offset?.y ?? 0,
    scaleX: k.scale?.x ?? 1,
    scaleY: k.scale?.y ?? 1,
    rot: k.rot ?? 0,
  }
}

// safe divide: 分母 0/極端で NaN/Infinity を出さない → 0 へ。
function safeProg(elapsedMs: number, durMs: number): number {
  if (!durMs || durMs <= 0) return 0
  return clamp01(elapsedMs / durMs)
}

export function phaseProgressOf(boss: Boss): { phase: BossAnimPhase; progress: number; hitIndex: number } {
  const atk = boss.currentAttack
  const elapMs = boss.stateElapsed * 1000
  const hitIndex = boss.comboHitIndex ?? 0
  switch (boss.state) {
    // string の中間拍は boss.ts と同じ実 chargeOverrideMs で進める。
    // 元 AttackDef の chargeMs で割ると、短縮拍だけキーポーズが途中までしか進まず、
    // 「同じ絵のまま早く当たる」逆転が起きる (騎士の連撃突きでは strike 絵が未表示だった)。
    case 'telegraph': {
      const beat = boss.currentString?.beats[boss.beatIndex ?? 0]
      const chargeMs = beat?.chargeOverrideMs ?? atk?.chargeMs ?? 0
      if (atk?.id === 'houndPounce') {
        // Keep the last 320ms for the hop even in the shorter string beat.
        const holdMs = chargeMs - 320
        const elapsed = elapMs <= holdMs ? elapMs / holdMs * 1100 : 1100 + elapMs - holdMs
        return { phase: 'telegraph', progress: safeProg(elapsed, 1420), hitIndex }
      }
      const from = beat?.telegraphFromProgress ?? 0
      return { phase: 'telegraph', progress: from + (1 - from) * safeProg(elapMs, chargeMs), hitIndex }
    }
    case 'active':    return { phase: 'active', progress: safeProg(elapMs, atk?.activeMs ?? 0), hitIndex }
    // comboGap の進行度は「その段の実 gap」で割る (2026-07-11 修正)。 旧=一律 comboGapMs で割ると、
    //   終盤の長い gap (裾乱れ hit6=440/hit7=640ms) でも進行度が短い gap 長で 1.0 へ振り切り、
    //   gap 内の絵切替 (feint@0.4 / slam@0.5) が数十msで発火 = 「溜めの絵にならず結果の絵が出っぱなし」。
    case 'comboGap':  return { phase: 'comboGap', progress: safeProg(elapMs, atk?.comboGapsMs?.[hitIndex] ?? atk?.comboGapMs ?? 1), hitIndex }
    case 'recovery':  return { phase: 'recovery', progress: safeProg(elapMs, atk?.recoveryMs ?? 0), hitIndex }
    // 連 (string) の拍と拍の間: idle に戻さず、前拍の振り終わり (recovery 先頭=followThrough) を保持する。
    //   K4 / design_combo_no_neutral_pose「連が続く間はニュートラルに戻さない」。 beatGap 中の
    //   boss.currentAttack は直前に振り終えた技なので、その recovery@0.0 (例=bite_follow) が committed 絵。
    //   progress=0 固定で idle 隣接の recover 絵 (recovery@0.5) へは進めない。
    case 'beatGap':   return { phase: 'recovery', progress: 0, hitIndex }
    default:          return { phase: 'idle', progress: 0, hitIndex }
  }
}

// exact (hitIndex 完全一致) を wildcard (hitIndex 未指定) より優先する。 配列内の並び順に依存しない
//   (contactProgressFromKeys と同じ優先規則・契約2026-07-16 §1)。
export function findContactKey(keys: BossAttackTimeline, hitIndex: number | undefined): BossPoseKey | undefined {
  const exact = keys.find((k) => k.tag === 'contact' && k.hitIndex === hitIndex)
  if (exact) return exact
  return keys.find((k) => k.tag === 'contact' && k.hitIndex === undefined)
}

// 契約2026-07-16 (武器contact同期 §A): 与えられた1攻撃分のキー列から、 指定 hitIndex の
//   contact 進捗 (active 内 0<=p<1) を解決する純関数。 exact (hitIndex 完全一致) を wildcard
//   (hitIndex 未指定) より優先する。 データ不整合 (重複・欠落・非active・範囲外) は例外を投げて
//   テストで固定する (実行時に静かに 0 へ倒さない = 見た目と判定がズレたまま出荷される事故を防ぐ)。
export function contactProgressFromKeys(keys: BossAttackTimeline, hitIndex: number): number {
  const contactKeys = keys.filter((k) => k.tag === 'contact')
  const exact = contactKeys.filter((k) => k.hitIndex === hitIndex)
  if (exact.length > 1) {
    throw new Error(`[contact] duplicate exact contact marker for hitIndex=${hitIndex}`)
  }
  const wildcard = contactKeys.filter((k) => k.hitIndex === undefined)
  if (wildcard.length > 1) {
    throw new Error(`[contact] duplicate wildcard contact marker (hitIndex=${hitIndex} has no exact marker)`)
  }
  const key = exact[0] ?? wildcard[0]
  if (!key) {
    throw new Error(`[contact] missing contact marker for hitIndex=${hitIndex}`)
  }
  if (key.phase !== 'active') {
    throw new Error(`[contact] contact marker is not in 'active' phase (hitIndex=${hitIndex}, phase=${key.phase})`)
  }
  if (!Number.isFinite(key.at) || key.at < 0 || key.at >= 1) {
    throw new Error(`[contact] contact marker 'at' out of range: ${key.at} (hitIndex=${hitIndex})`)
  }
  return key.at
}

// 攻撃定義/hitIndex から contact 進捗を引く spawnMeleeHit の実入口。
//   authored melee は timeline contact または contactAtActiveStart の明示を必須にする (fail-closed)。
//   未登録→0 を許すのは、移行中であることを明示した collisionDraft だけ。
export function resolveContactProgress(atk: AttackDef, hitIndex: number): number {
  const timeline = atk.sourceBossId ? BOSS_ATTACK_TIMELINES[atk.sourceBossId]?.[atk.id] : undefined
  if (!timeline) {
    if (atk.contactAtActiveStart === true || atk.collisionDraft === true) return 0
    throw new Error(`[contact] missing authored contact contract: ${atk.id} hitIndex=${hitIndex}`)
  }
  return contactProgressFromKeys(timeline, hitIndex)
}

// 連 (string) の 2 発目以降 (beatIndex>0) の telegraph 冒頭で、タイムラインが 'idle' 絵を返す場面
//   (各技の telegraph@0.0 は棒立ち導入) を、その技の構え (anticipation) 絵へ差し替える。
//   K4 / design_combo_no_neutral_pose: 連中はニュートラルに戻さない。
//   該当しない (idle でない / 連中でない / 1 発目 / telegraph 以外 / 構え絵なし) は undefined = 従来どおり。
export function comboNoNeutralImage(
  tl: BossAttackTimeline, resolvedImage: string, boss: Boss,
): string | undefined {
  if (boss.state !== 'telegraph') return undefined
  if (!boss.currentString || (boss.beatIndex ?? 0) <= 0) return undefined
  // 騎士の短縮拍は、攻撃種の槍線を守ったまま単発とは違う専用の低姿勢を「打点の直前まで」保つ。
  //   薙ぎ=深い斜め担ぎ / 突き=水平槍。
  //   ⚠️ 2026-07-25 ユーザー実機「若干違うけど花嫁レベルじゃない」の正体:
  //     旧実装は resolvedImage==='idle' の間だけ差し替えていたため、telegraph 後半
  //     (薙ぎ 0.45〜 = 残り231ms) で単発と同じ lanceSweep_windup へ戻っていた。
  //     プレイヤーが実際に読むのは「打つ直前」なので、そこが単発と1画素も違わない = 差が消える。
  //     花嫁 veilSweepLink は専用絵を hold で接触まで保っており、そこが差の本体だった。
  //     → anticipation 帯まで差し替えを広げる。
  //   ただし打点直前の絵 (lanceThrust_strike = anticipation タグなし) は通す。
  //     「もう来る」を伝える情報なので潰さない (連撃で strike へ到達させた修正を守る)。
  if (boss.id === 'vigil-knight') {
    // DEV `?sweepLink=windup` (2026-09-27 夜・比較): 連の2拍目以降も単発と同じ構え (後ろ上に担ぐ) = 毎拍、頭上を越える大きな弧。
    //   溜めの冒頭の棒立ちだけを構えへ差し替える (以降はタイムラインどおり windup)。
    if (boss.currentAttack?.id === 'lanceSweep' && knightSweepLink === 'windup') {
      return resolvedImage === 'idle' ? 'lanceSweep_windup' : undefined
    }
    const link = boss.currentAttack?.id === 'lanceSweep' ? 'lanceSweep_linkWindup'
      : boss.currentAttack?.id === 'lanceThrust' ? 'lanceThrust_linkWindup'
        : undefined
    if (link) {
      const isAnticipation = tl.some((k) => k.image === resolvedImage && k.tag === 'anticipation')
      return (resolvedImage === 'idle' || isAnticipation) ? link : undefined
    }
  }
  // 騎士以外 (および騎士の他技) は従来どおり: idle の時だけ構え絵へ差し替える。
  if (resolvedImage !== 'idle') return undefined
  const hitIndex = boss.comboHitIndex ?? 0
  const antic = tl.find((k) => k.tag === 'anticipation'
    && (k.hitIndex === undefined || k.hitIndex === hitIndex))
  return antic?.image
}

// 騎士の string 拍間だけを「張ったまま」の専用絵へ差し替える。
// beatGap は中間拍にしか入らないが、最後の拍で嘘を出さない契約をここでも明示的に守る。
// 他ボス・単発・最終拍は undefined = recovery タイムラインを従来どおり使う。
export function knightComboContinuationImage(boss: Boss): string | undefined {
  if (boss.id !== 'vigil-knight' || boss.state !== 'beatGap') return undefined
  const beats = boss.currentString?.beats
  const beatIndex = boss.beatIndex ?? 0
  if (!beats || beatIndex >= beats.length - 1) return undefined
  // 2026-09-27 キーポーズ化: 拍間も振り終わり (contact) を保つ。09-12 の生成構え (ready) は ?knightSweep=0912 だけ。
  // 2026-09-27 夜 (S-4「固まって見える」): 次も薙ぎの時だけ、拍間の後半を振った向きの続きの絵へ送る (knightSweepGapImage)。
  if (boss.currentAttack?.id === 'lanceSweep') {
    if (knightSweepVariant === '0912') return 'lanceSweep_ready'
    const next = beats[beatIndex + 1]!
    if (next.attackId !== 'lanceSweep') return 'lanceSweep_contact'
    return knightSweepGapImage(boss.stateElapsed * 1000, next.gapMs ?? 0, beatIndex)
  }
  if (boss.currentAttack?.id === 'lanceThrust') return 'lanceDouble_gap'
  return undefined
}

// active の接触シルエットを濁らせないため、 接触キーの前後と telegraph→active 境界はブレンド禁止 (Codex)。
export function shouldCrossfade(from: BossPoseKey, to: BossPoseKey): boolean {
  if (from.tag === 'contact' || to.tag === 'contact') return false
  if (from.phase === 'telegraph' && to.phase === 'active') return false
  return true
}

// 描画レイヤー解決 (Codex 2026-06-09): selectTimelineFrame の next/blend を実際に使う。
//   非接触キー間 (idle→windup / followThrough→recover 等) だけ from→to を blend でディゾルブし、
//   接触周辺・telegraph→active・hold キー・次キー未ロードは安全側でハードカット (接触の読みを死守 + 不可視化防止)。
export type TimelineDrawLayers =
  | { mode: 'hardcut'; current: string }
  | { mode: 'crossfade'; from: string; to: string; blend: number }

export function resolveTimelineDraw(
  f: TimelineFrame,
  isLoaded: (image: string) => boolean,
): TimelineDrawLayers {
  if (f.next && f.blend > 0 && shouldCrossfade(f.current, f.next) && isLoaded(f.next.image)) {
    return { mode: 'crossfade', from: f.current.image, to: f.next.image, blend: f.blend }
  }
  return { mode: 'hardcut', current: f.current.image }
}

// dream-hound 攻撃タイムライン (2026-06-09 キーポーズ作画反映)。
//   houndBite (近接): 即時打点 → contact at active 0.0。 溜め=低く伏せる↓ (houndBite_windup)。
//   houndLunge (突進): 踏み込み途中で届く → contact at active 0.25。 溜め=高く立つ↑ (houndLunge_windup)。
//   ★溜めを telegraph 0.4 で出して保持 = 長い読みやすい予兆 (souls 系)。 高い↑/低い↓ で攻撃種が一目。
// ===== 請われぬ介錯 斬り上げ (exec_maulHold) の候補タイムライン (2026-09-02) =====
// 設計 = docs/candidates/2026-09-01-executioner-drag-scoop-motion-spec.md §5 (ユーザーが ChatGPT と壁打ちした
//   「引きずり→離地→急加速」)。拍は不変 (charge 1650 / active 190 / recovery 1350)。
//   実時間: 0ms KEY01 (scoop_twist・逆ひねりの入り) → 1100ms KEY02 (scoop_touch・切っ先が接地) →
//   1400ms KEY03 (scoop_lag・遅れ最大＝象徴) → 1550ms KEY04 (scoop_lift・自然離地) → 1650ms 急加速 →
//   1707ms 接触 (scoop_smear 流用) → KEY05 (scoop_through・振り抜き・刃は頭の後ろまで抜ける)。
//   ⚠️ 中割りは無い。offset.x の前進 (絵の位置ずらしだけ・判定は lungeDistance が担う) と ease で
//     「遅い→抜ける→急に速い」の速度差を作る。ガリガリ中 (0.667〜0.848) も身体は止めない。
// 🔴 候補は実プレイで見比べて決める (ユーザー 2026-09-02「動きを見ないと判らない・完成扱いにしない」)。
//   切替 = ?scoop=a|a2|a3|b|old (src/game.ts)。既定 = **a3** (2026-09-02 夜 ユーザー裁定。それまでは a)。
//   a   = 5キー + 振り抜き (KEY05) を硬直の頭に置く
//   b   = 5キー、振り抜き無し (接触の絵を据え置く＝08-26 裁定「振り切りの絵は作らない」を保つ版)
//   old = 08-26 版そのまま (後ろへ引く・約170ms の完全静止あり) ＝比較の基準
//   🔵 2026-09-02 夜 ユーザー: 「振り抜きあり (a) を採りたいが、接触の絵 (1733〜1933ms) と振り抜きの絵 (1950ms〜) の
//     2フレームでカックンと引っかかる感じがある」→ 正体の仮説 = 刃が前上→後ろ上へ約120°飛ぶ1カットの直後に体が止まる
//     (rot 3→1 を 380ms かけるだけ) ので「振り抜いた」ではなく「引っかかって止まった」に見える。言語化させず候補で選ばせる:
//   a2  = 振り抜きの絵へ早く切り替え (active 0.68 ≒1779ms) + 切り替え後も勢いを残す (前へ倒れ込む rot 6 → 戻す)
//   a3  = a2 + 接触の刃先から振り抜きの刃先まで頭上を通る短い弧 FX (render.ts drawExecutionerScoopFollowArc)
export type ExecutionerScoopVariant = 'a' | 'a2' | 'a3' | 'b' | 'old'

const EXEC_MAULHOLD_TELEGRAPH_ACTIVE_5KEY: BossAttackTimeline = [
  // 0〜1100ms: 逆ひねりを育てる。骨盤が先に沈んで前へ、胸郭と刃は後ろに残る = 少し後ろへ傾いて沈み、
  //   そこからゆっくり前へ滲み出す (「長い溜め」ではなく「動き続けている」)。
  { phase: 'telegraph', at: 0.0, image: 'scoop_twist', hold: true, tag: 'anticipation', ease: 'easeInOut' },
  { phase: 'telegraph', at: 0.22, image: 'scoop_twist', hold: true, ease: 'easeInOut',
    offset: { x: -4, y: 2 }, rot: -2 },
  // 1100ms: 切っ先が接地。ここから前へ踏み込み始める (火花 = render.ts drawExecutionerDragSparks)。
  { phase: 'telegraph', at: 0.667, image: 'scoop_touch', hold: true, ease: 'linear', offset: { x: 0, y: 0 }, rot: -1 },
  // 1100→1400ms: ガリガリ区間 (約300ms)。身体は前へ進み続け、刃だけ地面に遅れる。
  { phase: 'telegraph', at: 0.848, image: 'scoop_lag', hold: true, ease: 'easeIn', offset: { x: 12, y: 0 }, rot: 0 },
  // 1400→1550ms: 位置はほぼ動かさず、回転だけ進む。切っ先が接地から地面すれすれへ (絵の差し替えで出す)。
  { phase: 'telegraph', at: 0.939, image: 'scoop_lift', hold: true, ease: 'easeIn', offset: { x: 15, y: 0 }, rot: 1 },
  // 1550→1650ms: 急加速の入り。easeIn で立ち上がりを急にする (静止は作らない)。
  { phase: 'telegraph', at: 0.995, image: 'scoop_lift', ease: 'easeIn', offset: { x: 19, y: -1 }, rot: 2 },
  { phase: 'active', at: 0.0, image: 'scoop_lift', hold: true, ease: 'easeIn', offset: { x: 19, y: -1 }, rot: 2 },
  // 1707ms: 接触 (既存 scoop_smear を流用。値は 08-26 版を継ぐ = 接触スメア FX の実測アンカーが乗っている)。
  { phase: 'active', at: 0.30, image: 'scoop_smear', hold: true, tag: 'contact',
    offset: { x: 14, y: -6 }, scale: { x: 1.03, y: 1.02 } },
  { phase: 'active', at: 0.75, image: 'scoop_smear', hold: true, ease: 'easeOut', offset: { x: 16, y: -10 } },
]

// a2/a3 用: 接触までは同じ。接触 (active 0.30) の直後から振り抜きの絵へ移り、勢いを残して収束する。
//   接触の絵の保持は 0.30〜0.68 (約72ms) = 当たった瞬間は見せるが、そこで止めない。
const EXEC_MAULHOLD_TELEGRAPH_TO_CONTACT_5KEY: BossAttackTimeline =
  EXEC_MAULHOLD_TELEGRAPH_ACTIVE_5KEY.filter((k) => !(k.phase === 'active' && k.at > 0.30))
const EXEC_MAULHOLD_SWING_THROUGH_TAIL: BossAttackTimeline = [
  // 接触の絵は勢いのまま前へ (ease linear = 減速させない)
  { phase: 'active', at: 0.68, image: 'scoop_through', hold: true, ease: 'easeOut', offset: { x: 19, y: -10 }, rot: 4 },
  // 硬直の頭 = 慣性がまだ残っている (前へ倒れ込みが最大)。脚で持ち上げない = y は上げない
  { phase: 'recovery', at: 0.0, image: 'scoop_through', hold: true, ease: 'easeOut', offset: { x: 22, y: -9 }, rot: 6 },
  { phase: 'recovery', at: 0.16, image: 'scoop_through', hold: true, ease: 'easeOut', offset: { x: 16, y: -4 }, rot: 3 },
  { phase: 'recovery', at: 0.40, image: 'scoop_through', hold: true, ease: 'easeOut', offset: { x: 9, y: 2 }, rot: 0 },
  { phase: 'recovery', at: 0.60, image: 'scoop_through', ease: 'easeOut', offset: { x: 6, y: 1 } },
  { phase: 'recovery', at: 0.78, image: 'idle', ease: 'easeOut' },
]

export const EXEC_MAULHOLD_TIMELINE_VARIANTS: Record<ExecutionerScoopVariant, BossAttackTimeline> = {
  a2: [...EXEC_MAULHOLD_TELEGRAPH_TO_CONTACT_5KEY, ...EXEC_MAULHOLD_SWING_THROUGH_TAIL],
  a3: [...EXEC_MAULHOLD_TELEGRAPH_TO_CONTACT_5KEY, ...EXEC_MAULHOLD_SWING_THROUGH_TAIL],
  a: [
    ...EXEC_MAULHOLD_TELEGRAPH_ACTIVE_5KEY,
    // 振り抜き: 慣性を少し流して収束。上体が起きる = 胸郭の回転の結果 (脚で伸び上がらない = y は上げない)。
    { phase: 'recovery', at: 0.0, image: 'scoop_through', hold: true, ease: 'easeOut', offset: { x: 16, y: -8 }, rot: 3 },
    { phase: 'recovery', at: 0.28, image: 'scoop_through', hold: true, ease: 'easeOut', offset: { x: 10, y: -2 }, rot: 1 },
    { phase: 'recovery', at: 0.55, image: 'scoop_through', ease: 'easeOut', offset: { y: 4 } },
    { phase: 'recovery', at: 0.75, image: 'idle', ease: 'easeOut' },
  ],
  b: [
    ...EXEC_MAULHOLD_TELEGRAPH_ACTIVE_5KEY,
    // 08-26 版と同じ硬直 (接触の絵を据え置く)。
    { phase: 'recovery', at: 0.0, image: 'scoop_smear', hold: true },
    { phase: 'recovery', at: 0.45, image: 'scoop_smear', ease: 'easeOut', offset: { y: 6 } },
    { phase: 'recovery', at: 0.75, image: 'idle', ease: 'easeOut' },
  ],
  // 🔴 08-26 版 (退避・比較の基準)。設計メモと真逆な点 = 溜め中に後ろへ引く / 離地後に約170ms の完全静止。
  old: [
    { phase: 'telegraph', at: 0.0, image: 'set_backstep', hold: true, tag: 'anticipation', ease: 'easeOut' },
    { phase: 'telegraph', at: 0.14, image: 'set_backstep', hold: true, ease: 'easeOut', offset: { x: -6 } },
    { phase: 'telegraph', at: 0.30, image: 'scoop_drag', hold: true, ease: 'easeOut', offset: { x: -14 } },
    { phase: 'telegraph', at: 0.55, image: 'scoop_drag', hold: true, ease: 'easeInOut', offset: { x: -24 } },
    { phase: 'telegraph', at: 0.80, image: 'scoop_drag', hold: true, ease: 'easeIn', offset: { x: -30 }, rot: -3 },
    { phase: 'telegraph', at: 0.90, image: 'scoop_drag', ease: 'easeIn', offset: { x: -30, y: -4 }, rot: -3 },
    { phase: 'telegraph', at: 0.945, image: 'scoop_drag', ease: 'easeIn', offset: { x: -30, y: -4 }, rot: -3 },
    { phase: 'telegraph', at: 0.995, image: 'scoop_drag', ease: 'easeIn', offset: { x: -30, y: -4 }, rot: -3 },
    { phase: 'active', at: 0.0, image: 'scoop_drag', hold: true, ease: 'easeIn', offset: { x: -30, y: -4 }, rot: -3 },
    { phase: 'active', at: 0.30, image: 'scoop_smear', hold: true, tag: 'contact',
      offset: { x: 10, y: -6 }, scale: { x: 1.03, y: 1.02 } },
    { phase: 'active', at: 0.75, image: 'scoop_smear', hold: true, ease: 'easeOut', offset: { x: 14, y: -10 } },
    { phase: 'recovery', at: 0.0, image: 'scoop_smear', hold: true },
    { phase: 'recovery', at: 0.45, image: 'scoop_smear', ease: 'easeOut', offset: { y: 6 } },
    { phase: 'recovery', at: 0.75, image: 'idle', ease: 'easeOut' },
  ],
}

// 🔵 2026-09-02 夜 ユーザー裁定「a3 でいこう」= 既定は a3。a / a2 / b / old は比較用に ?scoop= で残す。
let executionerScoopVariant: ExecutionerScoopVariant = 'a3'
export function getExecutionerScoopVariant(): ExecutionerScoopVariant { return executionerScoopVariant }
// ?scoop=<a|b|old> から呼ぶ。プリロード前 (モジュール初期化時) に差し替えるので、絵の自動収集にも乗る。
export function setExecutionerScoopVariant(variant: ExecutionerScoopVariant): void {
  executionerScoopVariant = variant
  BOSS_ATTACK_TIMELINES['the-executioner']!.exec_maulHold = EXEC_MAULHOLD_TIMELINE_VARIANTS[variant]
}

// 介錯・砲身薙ぎ (exec_cannonSweep) の構え候補 (2026-09-03 第8便)。
//   telegraph だけが違う: art = 本番絵 sweep_windup (候補 low_a/low_b/twist_a/twist_b は絵ファイルの差し替え・
//     src/sprites.ts の SPRITE_FILE_OVERRIDE が担う。ここでは同じ sweep_windup キーを指すだけ) /
//     mortar = 旧仮 (榴弾の構え mortar_aim を流用・比較用に残す)。
//   切替 = ?sweepWindup=low_a|low_b|twist_a|twist_b|mortar (src/game.ts) / 実行中 = __setSweepWindup (src/main.ts)。
//   active/recovery は両方とも同一 (2026-09-03 第7便で直した rot 2/2/1 版)。
export type ExecutionerSweepWindupLook = 'low_a' | 'low_b' | 'twist_a' | 'twist_b' | 'mortar'

const EXEC_CANNONSWEEP_ACTIVE_RECOVERY: BossAttackTimeline = [
  // 🔵 2026-09-03 第7便「足が浮いてる」: sweep_contact は後ろ足がピボットの 100px 後ろ (足の開き 148px・
  //   換算 k=192/1354×1.15) なので rot 10 で後ろ足が 17px 浮いていた (idle は 55px 後ろ = 9.5px)。
  //   rot 2 = 3.5px に落とし、前へ出る勢いは offset.x で出す。第8便 B-2 OK (ユーザー)。
  { phase: 'active', at: 0.0, image: 'sweep_contact', hold: true, tag: 'contact',
    ease: 'easeOut', offset: { x: 26 }, rot: 2, scale: { x: 1.06, y: 0.96 } },
  { phase: 'active', at: 0.6, image: 'sweep_contact', hold: true, offset: { x: 28 }, rot: 2 },
  { phase: 'recovery', at: 0.0, image: 'sweep_contact', hold: true, ease: 'easeOut', offset: { x: 20 }, rot: 1 },
  { phase: 'recovery', at: 0.45, image: 'aim_hold', hold: true },
]

export const EXEC_CANNONSWEEP_TIMELINE_VARIANTS: Record<'art' | 'mortar', BossAttackTimeline> = {
  mortar: [
    { phase: 'telegraph', at: 0.0, image: 'idle', hold: true },
    { phase: 'telegraph', at: 0.2, image: 'mortar_aim', hold: true, tag: 'anticipation',
      ease: 'easeIn', offset: { x: -10, y: 4 }, rot: -6 },
    { phase: 'telegraph', at: 0.9, image: 'mortar_aim', hold: true, offset: { x: -14, y: 4 }, rot: -8 },
    ...EXEC_CANNONSWEEP_ACTIVE_RECOVERY,
  ],
  art: [
    { phase: 'telegraph', at: 0.0, image: 'idle', hold: true },
    { phase: 'telegraph', at: 0.2, image: 'sweep_windup', hold: true, tag: 'anticipation', ease: 'easeIn', offset: { x: -10 }, rot: -4 },
    { phase: 'telegraph', at: 0.9, image: 'sweep_windup', hold: true, offset: { x: -14 }, rot: -6 },
    ...EXEC_CANNONSWEEP_ACTIVE_RECOVERY,
  ],
}

// 既定 = 'twist_a' (🔵 2026-09-03 第8便 ユーザー採用。cannon/sweep_windup.png は twist_a のコピー。
//   L 案は「武器が左になってる」・T-b は「右向いてる」で不採用。候補ファイルと切替は比較用に残す)。
let executionerSweepWindupLook: ExecutionerSweepWindupLook = 'twist_a'
export function getExecutionerSweepWindupLook(): ExecutionerSweepWindupLook { return executionerSweepWindupLook }
// ?sweepWindup=<look> / __setSweepWindup(look) から呼ぶ。'mortar' だけ mortar タイムラインへ、それ以外は
//   art タイムラインへ切り替える (絵ファイルの差し替えは呼び出し側が setSpriteFileOverride で行う)。
export function setExecutionerSweepWindupTimeline(look: ExecutionerSweepWindupLook): void {
  executionerSweepWindupLook = look
  BOSS_ATTACK_TIMELINES['the-executioner']!.exec_cannonSweep =
    look === 'mortar' ? EXEC_CANNONSWEEP_TIMELINE_VARIANTS.mortar : EXEC_CANNONSWEEP_TIMELINE_VARIANTS.art
}

// 介錯・据えの突き (exec_maulSetPierce) の絵の候補 (2026-09-04)。
//   巻き (sweep_coil・親技 exec_maulSet の溜め) → 引き絞り (pierce_draw・切っ先を相手へ向けて腰だめ) → 突き出し (pierce_thrust・ランジ)。
//   拍 (350/140/1500) は1msも変えていない。a/b は絵ファイルだけの違い。old = 専用絵が入る前の流用版 (比較用)。
//   切替 = ?pierceArt=a|b|old (src/game.ts) / 実行中 = __setPierceArt (src/main.ts)。
//   正本 = docs/candidates/2026-09-04-executioner-cannon-tempo-design.md §4
export type ExecutionerPierceArt = 'a' | 'b' | 'old'
function pierceTimeline(draw: SpriteKey, thrust: SpriteKey): BossAttackTimeline {
  return [
    { phase: 'telegraph', at: 0.0, image: 'sweep_coil', hold: true, offset: { x: -26 }, rot: -4 },
    { phase: 'telegraph', at: 0.3, image: draw, hold: true, tag: 'anticipation', ease: 'easeIn', offset: { x: -30 } },
    { phase: 'telegraph', at: 0.995, image: draw, offset: { x: -38 } },
    { phase: 'active', at: 0.0, image: draw, hold: true, offset: { x: -38 } },
    { phase: 'active', at: 0.35, image: thrust, hold: true, tag: 'contact', ease: 'easeOut', offset: { x: 30 } },
    { phase: 'recovery', at: 0.0, image: thrust, hold: true, offset: { x: 30 } },
    { phase: 'recovery', at: 0.4, image: thrust, hold: true, ease: 'easeOut', offset: { x: 22 } },
    { phase: 'recovery', at: 0.7, image: 'idle', ease: 'easeOut' },
  ]
}
// 介錯・突き当て (exec_maulJab) の絵の候補 (2026-09-04)。
//   'old' = 蹴りの溜め (kick_windup・膝を上げる) → 突き飛ばし (shove_contact) の既存シーケンス。
//     2026-09-11: shove_contact は刃の幅を戻した候補Aへ更新済み。溜めの膝上げは従来どおり。
//   'thrust' = 引き絞り (pierce_draw) → 突き出し (pierce_thrust)。拍もオフセットも 'old' と1つも変えず、絵だけ替える。
//   切替 = ?jabArt=old|thrust (src/game.ts) / 実行中 = __setJabArt (src/main.ts) / ラボ = #jabArtSelect
//   'lunge' = 2026-09-22 ユーザー裁定「A-1」＝『隻狼』の「危」の突きのシルエットで、距離は今のまま
//     (後ろ足を残して前足だけ踏み出し体ごと倒す。本体の前進は lungeDistance 44 のまま＝密着の罰の役目を残す)。
//     絵＝溜め jab_coil → 刺し jab_lunge (2026-09-22 本番絵・票 docs/handoffs/2026-09-22-executioner-jab-lunge-art-ticket.md)。
//     仮の絵 (pierce_draw → fajin_thrust) でユーザー「拍はいい感じ」→ 拍はそのまま絵だけ替えた。
//     拍は old と同じ (chargeMs 650 / active 130 / recovery 620)。足したのは「刺し残り」= 隙の頭 0.2 を刺した絵のまま止める。
export type ExecutionerJabArt = 'old' | 'thrust' | 'lunge'
// image は BossPoseKey と同じ string 型で受ける (kick_windup は SpriteKey の並びに無い旧キー)。
function jabTimeline(windup: string, contact: string): BossAttackTimeline {
  return [
    { phase: 'telegraph', at: 0.0, image: 'idle', hold: true },
    { phase: 'telegraph', at: 0.22, image: 'idle' },
    { phase: 'telegraph', at: 0.3, image: windup, hold: true, tag: 'anticipation',
      ease: 'easeIn', offset: { x: -8 } },   // 一度うしろへ引く = 短いのに「入り」が見える
    // easeOut = 出だしが最速。骨格Cの語彙
    { phase: 'active', at: 0.0, image: contact, hold: true, ease: 'easeOut', offset: { x: -8 } },
    { phase: 'active', at: 0.35, image: contact, hold: true, tag: 'contact', offset: { x: 16 } },
    { phase: 'recovery', at: 0.0, image: contact, hold: true, offset: { x: 16 } },
    { phase: 'recovery', at: 0.3, image: 'idle', hold: true, ease: 'easeOut' },
  ]
}
export const EXEC_JAB_TIMELINE_VARIANTS: Record<ExecutionerJabArt, BossAttackTimeline> = {
  old: jabTimeline('kick_windup', 'shove_contact'),
  thrust: jabTimeline('pierce_draw', 'pierce_thrust'),
  lunge: [
    { phase: 'telegraph', at: 0.0, image: 'idle', hold: true },
    { phase: 'telegraph', at: 0.2, image: 'idle' },
    // 沈んで後ろ足へ重心を預ける = 溜めが深く見える
    { phase: 'telegraph', at: 0.3, image: 'jab_coil', hold: true, tag: 'anticipation',
      ease: 'easeIn', offset: { x: -14 } },
    { phase: 'active', at: 0.0, image: 'jab_coil', hold: true, ease: 'easeOut', offset: { x: -14 } },
    { phase: 'active', at: 0.35, image: 'jab_lunge', hold: true, tag: 'contact', offset: { x: 20 } },
    // 刺し残り: 刺したまま一瞬止める → 少し抜いて戻る
    { phase: 'recovery', at: 0.0, image: 'jab_lunge', hold: true, offset: { x: 20 } },
    { phase: 'recovery', at: 0.2, image: 'jab_lunge', hold: true, ease: 'easeIn', offset: { x: 20 } },
    { phase: 'recovery', at: 0.45, image: 'idle', hold: true, ease: 'easeOut' },
  ],
}
export function setExecutionerJabArt(art: ExecutionerJabArt): void {
  BOSS_ATTACK_TIMELINES['the-executioner']!.exec_maulJab = EXEC_JAB_TIMELINE_VARIANTS[art]
}

export const EXEC_PIERCE_TIMELINE_VARIANTS: Record<ExecutionerPierceArt, BossAttackTimeline> = {
  a: pierceTimeline('pierce_draw', 'pierce_thrust'),
  b: pierceTimeline('pierce_draw_b', 'pierce_thrust_b'),
  old: [
    { phase: 'telegraph', at: 0.0, image: 'sweep_coil', hold: true, offset: { x: -26 }, rot: -4 },
    { phase: 'telegraph', at: 0.45, image: 'sweep_coil', hold: true, tag: 'anticipation',
      ease: 'easeIn', offset: { x: -34 }, rot: -5 },
    { phase: 'telegraph', at: 0.995, image: 'sweep_coil', offset: { x: -34 }, rot: -5 },
    { phase: 'active', at: 0.0, image: 'sweep_coil', hold: true, offset: { x: -34 }, rot: -5 },
    { phase: 'active', at: 0.35, image: 'sweep_slash', hold: true, tag: 'contact',
      ease: 'easeOut', offset: { x: 26 } },
    { phase: 'recovery', at: 0.0, image: 'sweep_slash', hold: true, offset: { x: 26 } },
    { phase: 'recovery', at: 0.5, image: 'idle', ease: 'easeOut' },
  ],
}
export function setExecutionerPierceArt(art: ExecutionerPierceArt): void {
  BOSS_ATTACK_TIMELINES['the-executioner']!.exec_maulSetPierce = EXEC_PIERCE_TIMELINE_VARIANTS[art]
}

// 待宵の花嫁の動きの手本合わせ (2026-10-21 質問表の答え・台帳 docs/ledger/bosses.md「待宵の花嫁」)。
//   'a' = 配布版の既定。横薙ぎは正面の全開 (veil_open) を抜き「溜め → 前へ払う → 振り抜く」(手本 DS3 冷たい谷の踊り子の横薙ぎ)。
//     返しの一閃と瞬き一打は横向きで身を縮める side_crouch、三重の返礼の溜めは網目をやめた blink_triple_charge_1021。
//   'old' = 10-21 より前 (DEV ?brideMotion=old で並べて比べる)。喪の翻し (veilSweepPerilous) は両方同じ。
export type BrideMotionArt = 'a' | 'old'
type BrideMotionAttack = 'veilSweep' | 'veilSweepLink' | 'veilBlinkTriple'
const brideBlinkTripleTimeline = (charge: 'blink_triple_charge' | 'blink_triple_charge_1021'): BossAttackTimeline => [
  { phase: 'telegraph', at: 0.0, image: charge, hold: true, tag: 'anticipation' },
  { phase: 'active', at: 0.0, image: 'flurry_swing_right', hold: true, tag: 'contact', hitIndex: 0 },
  { phase: 'comboGap', at: 0.0, image: 'flurry_swing_right', hold: true, hitIndex: 0 },
  { phase: 'active', at: 0.0, image: 'flurry_swing_cross', hold: true, tag: 'contact', hitIndex: 1 },
  { phase: 'comboGap', at: 0.0, image: 'flurry_swing_cross', hold: true, hitIndex: 1 },
  { phase: 'active', at: 0.0, image: 'flurry_swing_left', hold: true, tag: 'contact', hitIndex: 2 },
  { phase: 'recovery', at: 0.0, image: 'veil_follow', hold: true, tag: 'followThrough' },
]
export const BRIDE_MOTION_TIMELINE_VARIANTS: Record<BrideMotionArt, Record<BrideMotionAttack, BossAttackTimeline>> = {
  a: {
    // 畳む → 体をひねってヴェールを巻き付ける (溜めの山・hold) → ほどけて前へ払う (contact) → 振り抜いて収まる。
    veilSweep: [
      { phase: 'telegraph', at: 0.0, image: 'idle' },
      { phase: 'telegraph', at: 0.10, image: 'veil_coil' },
      { phase: 'telegraph', at: 0.45, image: 'veil_twist', hold: true, tag: 'anticipation' },
      { phase: 'active', at: 0.0, image: 'veil_sweep_contact', hold: true, tag: 'contact' },
      { phase: 'recovery', at: 0.0, image: 'veil_sweep_contact', hold: true, tag: 'followThrough' },
      { phase: 'recovery', at: 0.45, image: 'veil_follow', tag: 'followThrough' },
    ],
    // 350ms しかないので idle を挟まず、開始フレームから縮めた体を見せて弾ける。
    veilSweepLink: [
      { phase: 'telegraph', at: 0.0, image: 'side_crouch', hold: true, tag: 'anticipation' },
      { phase: 'active', at: 0.0, image: 'veil_sweep_contact', hold: true, tag: 'contact' },
      { phase: 'recovery', at: 0.0, image: 'veil_sweep_contact', hold: true, tag: 'followThrough' },
      { phase: 'recovery', at: 0.4, image: 'veil_follow', tag: 'followThrough' },
    ],
    veilBlinkTriple: brideBlinkTripleTimeline('blink_triple_charge_1021'),
  },
  old: {
    veilSweep: [
      { phase: 'telegraph', at: 0.0, image: 'idle' },
      { phase: 'telegraph', at: 0.10, image: 'veil_coil', tag: 'anticipation' },
      { phase: 'active', at: 0.0, image: 'veil_open', tag: 'contact' },
      { phase: 'recovery', at: 0.0, image: 'veil_sweep_contact', tag: 'followThrough' },
      { phase: 'recovery', at: 0.5, image: 'veil_follow', tag: 'followThrough' },
    ],
    veilSweepLink: [
      { phase: 'telegraph', at: 0.0, image: 'veil_link_snap_windup', hold: true, tag: 'anticipation' },
      { phase: 'active', at: 0.0, image: 'veil_open', hold: true, tag: 'contact' },
      { phase: 'recovery', at: 0.0, image: 'veil_sweep_contact', tag: 'followThrough' },
      { phase: 'recovery', at: 0.5, image: 'veil_follow', tag: 'followThrough' },
    ],
    veilBlinkTriple: brideBlinkTripleTimeline('blink_triple_charge'),
  },
}
let brideMotionArt: BrideMotionArt = 'a'
export function getBrideMotionArt(): BrideMotionArt { return brideMotionArt }
export function setBrideMotionArt(art: BrideMotionArt): void {
  brideMotionArt = art
  Object.assign(BOSS_ATTACK_TIMELINES['bride']!, BRIDE_MOTION_TIMELINE_VARIANTS[art])
}

// 花嫁・一閃を動画の見本 (issen_b の 0〜30 コマ) に寄せた比較版 (2026-10-22・作者「寄せてよい」＝答えるページ GMtbqKX8C5hYdm2xxgE95K の q1)。
//   ひねる溜め (veil_twist) の後に、両手でヴェールを頭上へ振り上げる → 振り下ろす → 振り切って深く沈む、の3枚を足した。
//   絵は動画のコマを姿勢の見本にして Codex が今の描き方で描いた物 (動画の絵そのものは入れない)。
//   10-23: 作者「大きさとか色々バラバラ。」で、立ち絵 idle を手本に描き直した *_1023 へ替えた (頭の大きさ・線・色を idle に合わせ、
//   置き場所は BRIDE_ISSEN_ANCHOR_X で裾の中心を idle と同じにする)。10-22 の3枚は public/sprites/archived/bride/。
//   秒数・判定・tag は配布版 a のまま＝描画だけ。開発版の ?brideIssen=1 でだけ入る。記録 = docs/research/2026-10-23-bride-issen-kaeshi.md
// 一閃の3枚と返しの一閃 (src/bride_kaeshi.ts) の2枚の横の置き場所 (絵の画素)。裾の帯の中心を idle の「裾の中心→外接箱の中心」と同じだけずらした所。
//   ヴェールの弧で外接箱の中心が動き、絵が切り替わるたびに横へ跳んでいた (10-22 の版)。
//   kaeshi_cut_1023 (返しの一閃の刀の絵) は切っ先が下の帯に入るので、裾の帯 (x 392〜1449) の中心 920 から同じだけずらした。
export const BRIDE_ISSEN_ANCHOR_X: Readonly<Record<string, number>> = {
  issen_raise_1023: 607, issen_swing_1023: 564, issen_sink_1023: 792,
  kaeshi_pray: 518, kaeshi_sweep: 846, kaeshi_cut_1023: 990,
  // 返しの一閃の間の絵 (開発版 ?brideKaeshi=tween・src/bride_kaeshi_tween.ts)。腰の黒い薔薇が前後の絵の間を通るように置いた。
  kaeshi_tween_rise_1023: 507, kaeshi_tween_reach_1023: 621, kaeshi_tween_settle_1023: 557,
}
export const BRIDE_VEILSWEEP_TIMELINE_ISSEN: BossAttackTimeline = [
  { phase: 'telegraph', at: 0.0, image: 'idle' },
  { phase: 'telegraph', at: 0.10, image: 'veil_coil' },
  { phase: 'telegraph', at: 0.40, image: 'veil_twist', hold: true },
  { phase: 'telegraph', at: 0.72, image: 'issen_raise_1023', hold: true, tag: 'anticipation' },
  { phase: 'telegraph', at: 0.94, image: 'issen_swing_1023', hold: true },
  { phase: 'active', at: 0.0, image: 'issen_sink_1023', hold: true, tag: 'contact' },
  { phase: 'recovery', at: 0.0, image: 'issen_sink_1023', hold: true, tag: 'followThrough' },
  { phase: 'recovery', at: 0.45, image: 'veil_follow', tag: 'followThrough' },
]
let brideIssen = false
export function getBrideIssen(): boolean { return brideIssen }
// setBrideMotionArt の後に呼ぶ (一閃の並びを上書きするため)。
export function setBrideIssen(raw: string | null): void {
  brideIssen = import.meta.env.DEV && raw === '1'
  if (brideIssen) BOSS_ATTACK_TIMELINES['bride']!.veilSweep = BRIDE_VEILSWEEP_TIMELINE_ISSEN
}

// 介錯・据えの突きの v3 (2026-10-09 夜に比較版 → 同日ユーザー採用「v3（溜め600）で本番へ」= 配布版の既定)。
//   切り替わった瞬間に突き当ての溜めの絵 (jab_coil) へ1コマで替え、突き出しは突き当てと同じ jab_lunge。
//   置き場所 (offset) は切り替わった瞬間の据えの構えの位置から足が跳ばないように組み直す (src/exec_brace_thrust.ts の braceThrustStartFrom)。
//   ここに置くのは BOSS_ATTACK_TIMELINES の初期値に使うため (exec_brace_thrust.ts は sprites.ts 経由でこのファイルを読むので、逆向きに読むと輪になる)。
//   旧 (据えの構えのまま → sweep_slash) = EXEC_PIERCE_TIMELINE_VARIANTS.old・DEV ?braceThrust=old。
export type BraceThrustStart = { offsetX: number; rot: number }
// 溜めの終わり近く (据えの一打の溜め 0.93 以降 = offset −26・縮み 0.955・傾き −4) で切り替わった時の置き方。
//   切り替わる前 (ラボで突きを単技で出した時・初期値) はこれを使う。
export const BRACE_THRUST_DEFAULT_START: BraceThrustStart = { offsetX: -37.2, rot: -4 }
// 突き出しの前進: 突き当て (lunge) と同じ +34 (−14→+20)。jab_lunge の置き場所は後ろ足が残るように作ってある。
const BRACE_THRUST_LUNGE_FORWARD_PX = 34
// 拍 (contact = active の 0.35) は旧と同じ。溜めの長さは attacks.ts の chargeMs (600) が決める。
export function braceThrustV3Timeline(start: BraceThrustStart): BossAttackTimeline {
  const b = start.offsetX
  return [
    // 1コマで刃を返す。直前の傾きは足元を軸に 0.25 までに起こす (体が据えから突きへ向き直る)。
    { phase: 'telegraph', at: 0.0, image: 'jab_coil', hold: true, tag: 'anticipation',
      ease: 'easeOut', offset: { x: b }, rot: start.rot },
    { phase: 'telegraph', at: 0.25, image: 'jab_coil', hold: true, ease: 'easeIn', offset: { x: b } },
    // 切っ先を向けたまま、わずかに沈んで溜める (足は動かさない)。
    { phase: 'telegraph', at: 0.995, image: 'jab_coil', offset: { x: b }, scale: { x: 1, y: 0.985 } },
    { phase: 'active', at: 0.0, image: 'jab_coil', hold: true, ease: 'easeOut', offset: { x: b }, scale: { x: 1, y: 0.985 } },
    { phase: 'active', at: 0.35, image: 'jab_lunge', hold: true, tag: 'contact', offset: { x: b + BRACE_THRUST_LUNGE_FORWARD_PX } },
    // 刺し残り → 戻る。隙 1500ms の前半は刺した絵のまま (旧の sweep_slash と同じ 0.5 まで)。
    { phase: 'recovery', at: 0.0, image: 'jab_lunge', hold: true, offset: { x: b + BRACE_THRUST_LUNGE_FORWARD_PX } },
    { phase: 'recovery', at: 0.3, image: 'jab_lunge', hold: true, ease: 'easeIn', offset: { x: b + BRACE_THRUST_LUNGE_FORWARD_PX } },
    { phase: 'recovery', at: 0.5, image: 'idle', ease: 'easeOut' },
  ]
}

export type AwokenBiteOrder = 'now' | 'tele' | 'reco'
const AWOKEN_BITE_NOW_TIMELINE: BossAttackTimeline = [
      { phase: 'telegraph', at: 0.0, image: 'idle', hold: true, ease: 'easeIn' },
      { phase: 'telegraph', at: 0.18, image: 'idle',
        offset: { y: 2 }, scale: { x: 1.02, y: 0.97 }, ease: 'easeIn' },
      { phase: 'telegraph', at: 0.3, image: 'bite_windup', hold: true, tag: 'anticipation',
        offset: { x: -3, y: 3 }, scale: { x: 1.03, y: 0.95 }, rot: -0.6, ease: 'easeIn' },
      { phase: 'telegraph', at: 0.625, image: 'bite_windup', hold: true,
        offset: { x: -5, y: 3 }, scale: { x: 1.04, y: 0.94 }, rot: -0.9, ease: 'easeIn' },
      { phase: 'telegraph', at: 0.875, image: 'bite_windup', hold: true,
        offset: { x: -6, y: 2 }, scale: { x: 1.04, y: 0.94 }, rot: -1.1, ease: 'easeIn' },
      { phase: 'active', at: 0.0, image: 'bite_contact', hold: true, tag: 'contact',
        offset: { x: 7 }, scale: { x: 1.05, y: 0.97 }, rot: 1.2, ease: 'easeOut' },
      { phase: 'active', at: 0.15, image: 'bite_step', hold: true,
        offset: { x: 4 }, scale: { x: 1.02, y: 0.99 }, rot: 0.8, ease: 'easeOut' },
      { phase: 'recovery', at: 0.0, image: 'bite_follow', hold: true, tag: 'followThrough',
        offset: { x: 4 }, rot: 0.8, ease: 'easeOut' },
      { phase: 'recovery', at: 0.25, image: 'bite_follow', hold: true,
        offset: { x: 1 }, rot: 0.2, ease: 'easeInOut' },
      { phase: 'recovery', at: 0.5, image: 'bite_recover', tag: 'recover', ease: 'easeOut' },
    ]

// 2026-09-12採用: 既定 reco。旧 now / tele は開発時の比較用。tele = 読み窓 336ms→240ms で速度試験の下限を割る／reco = 溜めと溜め噛みの同一性を保つ。
// 出所 = docs/handoffs/2026-09-10-awoken-bite-motion-redesign.md §1。
export const AWOKEN_BITE_TIMELINE_VARIANTS: Record<AwokenBiteOrder, BossAttackTimeline> = {
  now: AWOKEN_BITE_NOW_TIMELINE,
  tele: [
    { ...AWOKEN_BITE_NOW_TIMELINE[2]!, at: 0.0 },
    { ...AWOKEN_BITE_NOW_TIMELINE[3]!, at: 0.25 },
    { phase: 'telegraph', at: 0.5, image: 'bite_step', hold: true,
      offset: { x: 2, y: 1 }, scale: { x: 1.03, y: 0.96 }, rot: 0.2, ease: 'easeOut' },
    { phase: 'telegraph', at: 0.8, image: 'bite_step', hold: true,
      offset: { x: 4, y: 0 }, scale: { x: 1.03, y: 0.97 }, rot: 0.5, ease: 'easeIn' },
    ...AWOKEN_BITE_NOW_TIMELINE.filter((key) => key.phase === 'active' && key.at === 0),
    ...AWOKEN_BITE_NOW_TIMELINE.filter((key) => key.phase === 'recovery'),
  ],
  reco: [
    ...AWOKEN_BITE_NOW_TIMELINE.filter((key) => key.phase === 'telegraph'),
    ...AWOKEN_BITE_NOW_TIMELINE.filter((key) => key.phase === 'active' && key.at === 0),
    { phase: 'recovery', at: 0.0, image: 'bite_step', hold: true, tag: 'followThrough',
      offset: { x: 4 }, rot: 0.8, ease: 'easeOut' },
    ...AWOKEN_BITE_NOW_TIMELINE.filter((key) => key.phase === 'recovery' && key.at > 0),
  ],
}

let awokenBiteOrder: AwokenBiteOrder = 'reco'
export function getAwokenBiteOrder(): AwokenBiteOrder { return awokenBiteOrder }
export function setAwokenBiteOrder(order: AwokenBiteOrder): void {
  awokenBiteOrder = order
  BOSS_ATTACK_TIMELINES['crowned-king-awoken']!.feralBite = withAwokenBiteWindupImage(AWOKEN_BITE_TIMELINE_VARIANTS[order])
}

// 噛みの予備動作の絵の差し替え (DEV ?awokenNeckBack・src/awoken_neck_back.ts・2026-10-27)。null = 配布版 (bite_windup)。
//   暴噛と溜め噛みの予備動作の待機 (idle・頭の 150ms) と bite_windup を同じ絵に替える (溜め噛みは 300ms まで暴噛と同じに見せる決まり・tests/awoken_king_kit)。
//   待機も替えるのは、頭の 150ms の待機の絵の顔が 110px の間合いで主人公に重なっていたため (測り 2026-10-27)。
let awokenBiteWindupImage: string | null = null
let awokenBiteDelayedBase: BossAttackTimeline | null = null
function withAwokenBiteWindupImage(timeline: BossAttackTimeline): BossAttackTimeline {
  const image = awokenBiteWindupImage
  return image ? timeline.map(k => k.image === 'bite_windup' || (k.phase === 'telegraph' && k.image === 'idle') ? { ...k, image } : k) : timeline
}
export function setAwokenBiteWindupImage(image: string | null): void {
  awokenBiteWindupImage = image
  const tl = BOSS_ATTACK_TIMELINES['crowned-king-awoken']!
  awokenBiteDelayedBase ??= tl.feralBiteDelayed!
  tl.feralBiteDelayed = withAwokenBiteWindupImage(awokenBiteDelayedBase)
  tl.feralBite = withAwokenBiteWindupImage(AWOKEN_BITE_TIMELINE_VARIANTS[awokenBiteOrder])
}

export function setExecutionerFajinMotion(motion: FajinMotion): void {
  BOSS_ATTACK_TIMELINES['the-executioner']!.exec_bladeFajin = FAJIN_MOTION_TIMELINES[motion]
  getAttack('exec_bladeFajin').recoveryMs = FAJIN_RECOVERY_MS[motion]
}

// 大暴れ第5案の仮の時間割 (今ある絵の流用・完成絵は駅2)。台本 ROAM の区間から組み立てる。
//   09-20 ユーザー「移動時や噛みついてくると絵も１つ１つ違って見えたほうが生きている感じ」→ 3つで違いを出す:
//     ① 絵を順番に替える (跳び4種・構え3種・噛み3種を区間ごとに回す＝同じ絵が続かない)。
//     ② 体の傾きが飛ぶ向きに沿う (跳び出しは頭が上、落ちる時は頭が下。区間ごとに高さの差と山が違うので傾きも毎回違う)。
//     ③ 伸び縮み (蹴り出しで前へ伸び、着地・着壁で潰れて戻る)。
//   始まりは雄叫び1回 (roar_up)。倒れ込み＝staggered_enter→staggered_hold。contact 印は最初の噛みだけ (窓の正本は台本の biteWindows)。
// 宙を進む区間の絵。🔴 wall_kick (四肢を横へ揃えて壁を踏む絵) を混ぜない —
//   何も無い空中で壁に足を着けた姿に見える (2026-09-20 ユーザー指摘)。壁の絵は onWall の時だけ。
const V5_LEAPS = ['rampage_leap', 'rush_lunge', 'dash_form'] as const
const V5_WINDUPS = ['frenzy_open', 'bite_windup_deep', 'exec_windup'] as const
const V5_BITES = ['bite_contact', 'frenzy_bite2', 'exec_bite'] as const
// 2連続噛みつき (幕4・5) だけは絵を名指しする: 順繰りだと「深く沈む構え→床へ突っ伏す噛み」が当たり、
//   顔が床を向いて主人公の陰に隠れ、噛む瞬間も顔の光も読めなかった (2026-09-21 ユーザー「下むいてる感じで見えにくい」)。
//   幕4 = 頭を上げて口を開く → 前へ噛む。幕5 = 頭を跳ね上げる (追い噛みの合図) → 踏み込んで噛む。どれも顔が前を向く。
const V5_PAIR_ART: Readonly<Record<number, { windup: string; bite: string }>> = {
  4: { windup: 'bite_windup', bite: 'bite_contact' },
  5: { windup: 'chase_coil', bite: 'bite_step' },
}
// 高さの変化 (px/区間) を体の傾き (度) へ。横の距離は台本時点で決まらないので代表値で割る。上限つき。
const V5_PITCH_DIV_PX = 520
const V5_PITCH_MAX = 24
const v5Pitch = (slopePx: number) => Math.max(-V5_PITCH_MAX, Math.min(V5_PITCH_MAX, Math.atan2(slopePx, V5_PITCH_DIV_PX) * 180 / Math.PI))
function buildRampageV5Timeline(): BossAttackTimeline {
  const chargeSec = CHOREO_CHARGE_MS / 1000
  const tg = (sec: number) => Math.max(0, Math.min(0.999, sec / chargeSec))
  const keys: BossAttackTimeline = [
    { phase: 'telegraph', at: 0.0, image: 'idle', hold: true, ease: 'easeIn' },
    // 息を吸って沈む → 頭を上げて吠える (縦に伸びる) → 吠え切り → 跳ぶ構え。
    { phase: 'telegraph', at: tg(0.15), image: 'idle', offset: { y: 4 }, scale: { x: 1.04, y: 0.93 }, ease: 'easeIn' },
    { phase: 'telegraph', at: tg(0.3), image: 'roar_up', hold: true, tag: 'anticipation', scale: { x: 0.99, y: 1.02 }, ease: 'easeOut' },
    { phase: 'telegraph', at: tg(0.55), image: 'roar_up', hold: true, scale: { x: 0.96, y: 1.08 }, rot: -2, ease: 'easeOut' },
    { phase: 'telegraph', at: tg(0.9), image: 'roar_up', hold: true, scale: { x: 0.98, y: 1.04 }, ease: 'easeInOut' },
    { phase: 'telegraph', at: tg(CHOREO_ROAR_SEC), image: 'rush_windup', hold: true, offset: { y: 4 }, scale: { x: 1.05, y: 0.93 }, ease: 'easeIn' },
  ]
  const activeSec = CHOREO_ACTIVE_MS / 1000
  const at = (sec: number) => Math.max(0, Math.min(0.999, sec / activeSec))
  let t = -CHOREO_ACTIVE_START_SEC
  let biteIndex = 0, leapIndex = 0, windupIndex = 0
  let prevY = 0
  ROAM.forEach((leg, i) => {
    // 壁にしがみつく間は絵を傾ける (仮・壁の絵は駅2)。蹴り出し待ち＝壁を向いて背を部屋側へ (75°)、
    //   噛みの溜め＝主人公を向いて同じく縦に張り付く (75°)。符号は実画面で確かめた向き (2026-09-19)。
    //   壁の絵は rampage_leap／frenzy_open に固定 (wall_kick 等を75°回すと体が画面外へはみ出した・09-20 実画面)。ease 'step' = 着いた瞬間に傾き、跳ぶ瞬間に戻る。
    // 噛みつきの区間は到着点が主人公に合わせて変わるので、壁では終わらない (横切りの噛みの後は床すれすれの滑り)。
    const onWall = i > 0 && ROAM[i - 1]!.kind === 'run' && ROAM[i - 1]!.y > 0 && (ROAM[i - 1]!.x === WALL_R || ROAM[i - 1]!.x === WALL_L)
    const landed = i > 0 && !!ROAM[i - 1]!.wave   // 直前が床技の叩きつけ = 着地の絵を保つ
    const windup = V5_PAIR_ART[leg.phase]?.windup ?? V5_WINDUPS[windupIndex % V5_WINDUPS.length]!
    if (i > 0 && leg.warning > 0) {
      if (leg.kind !== 'run') windupIndex++
      // 着いた瞬間に潰れ (着壁・着地の衝撃)、構えの間に戻る。
      const wallImage = wallPoseUsesKickArt() ? 'wall_kick' : leg.kind === 'run' ? 'rampage_leap' : 'frenzy_open'
      const wallRot = wallPoseVariant === 'now' ? 75 : wallPoseVariant === 'neg' ? -75 : wallPoseVariant === 'kick20' ? 20 : 0
      keys.push(onWall
        ? { phase: 'active', at: at(t), image: wallImage, hold: true,
            rot: wallRot, scale: { x: 0.92, y: 1.06 }, ease: 'step' }
        : landed
          // 床へ叩きつけた直後は、着地の絵をしばらく保ってから次の構えへ移る。
          ? { phase: 'active', at: at(t), image: 'slam_landing_0920', hold: true, ease: 'step' }
          : { phase: 'active', at: at(t), image: leg.kind === 'run' ? 'dash_crouch' : windup, hold: true,
              offset: { y: 5 }, scale: { x: 1.07, y: 0.9 }, ease: 'easeOut' })
      if (landed) {
        // 叩きつけた姿を少し保ち、そこから起き上がって締めの構えへ。
        keys.push({ phase: 'active', at: at(t + leg.warning * 0.26), image: 'slam_landing_0920', hold: true,
          scale: { x: 1.02, y: 0.98 }, ease: 'easeInOut' })
        keys.push({ phase: 'active', at: at(t + leg.warning * 0.5), image: windup, hold: true,
          scale: { x: 1.03, y: 0.99 }, ease: 'easeOut' })
      }
      if (leg.warning > 0.2 && !landed) {
        keys.push(onWall
          ? { phase: 'active', at: at(t + Math.min(0.12, leg.warning / 2)), image: wallImage, hold: true,
              rot: wallRot, ease: 'step' }
          : { phase: 'active', at: at(t + Math.min(0.12, leg.warning / 2)), image: leg.kind === 'run' ? 'dash_crouch' : windup, hold: true,
              offset: { y: 2 }, scale: { x: 1.03, y: 0.96 }, ease: 'easeInOut' })
      }
    }
    // 床でかがむ間は水平を保ち、跳ぶ直前の一瞬で跳びの傾きへ移る。これが無いと、かがみの間じゅう次の跳びの傾きへ
    //   ゆっくり回り、足元を軸に尻 (旧符号では頭) が床の下へ沈んだ (2026-09-21 実画面)。
    if (i > 0 && !onWall && !landed && leg.kind === 'run' && leg.warning >= 0.15) {
      keys.push({ phase: 'active', at: at(t + leg.warning - 0.04), image: 'dash_crouch', hold: true,
        rot: 0, scale: { x: 1.04, y: 0.95 }, ease: 'easeIn' })
    }
    const travelStart = t + leg.warning
    // 放物線の傾き: 跳び出し = 高さの差 + 4×山、着く直前 = 高さの差 − 4×山 (区間ごとに違う)。
    const dh = leg.y - prevY
    const rise = v5Pitch(dh + 4 * leg.arc), fall = v5Pitch(dh - 4 * leg.arc)
    if (leg.kind === 'run') {
      // 跳びは軌道に沿って傾ける: 上がる時は頭が上、落ちる時は頭が下。絵は左向きで、正の回転 = 頭が上がる。
      //   09-21 まで符号が逆で、壁へ跳び上がる間ずっと頭が床を向いていた (実画面で確認)。噛みの区間は顔を読ませたいので触らない。
      const k = pitchLook === 'old' ? -1 : 1
      const airborne = leg.y > 0 || prevY > 0 || leg.arc > 60
      const leap = airborne ? V5_LEAPS[leapIndex++ % V5_LEAPS.length]! : 'dash_form'
      keys.push({ phase: 'active', at: at(travelStart), image: leap, hold: true, rot: k * rise, scale: { x: 1.1, y: 0.92 }, ease: 'easeOut' })
      keys.push({ phase: 'active', at: at(travelStart + leg.travel * 0.5), image: leap, hold: true, rot: k * (rise + fall) / 2, scale: { x: 1.02, y: 0.99 }, ease: 'easeInOut' })
      keys.push({ phase: 'active', at: at(travelStart + leg.travel * 0.92), image: leap, hold: true, rot: k * fall, scale: { x: 1.04, y: 0.97 }, ease: 'easeIn' })
      // 床技の着地は専用の絵 = 前脚を床へ突き立て、後脚はまだ宙 (2026-09-20 発注・③)。
      //   着いた瞬間だけ差し込み、次の区間の構えへ渡す。
      if (leg.wave) {
        keys.push({ phase: 'active', at: at(travelStart + leg.travel * 0.97), image: 'slam_landing_0920', hold: true,
          scale: { x: 1.06, y: 0.94 }, ease: 'step' })
      }
    } else {
      // 横切りの噛み (幕6の往復) は専用の絵 = 空中で水平に伸びて噛む (2026-09-20 発注・②)。
      //   🔴 跳び出しは口を開けた構え (牙あり) にする。跳びの絵にすると噛む瞬間まで移動と見分けがつかない
      //   (2026-09-21 ユーザー指摘)。比較は `?swoopLook=` (awoken_rampage_choreo.ts)。
      const swoop = leg.phase === 6
      const bite = swoop && swoopLook !== 'old' ? 'swoop_bite_0920' : V5_PAIR_ART[leg.phase]?.bite ?? V5_BITES[biteIndex % V5_BITES.length]!
      const wind = swoop && swoopLook === 'leap' ? 'rampage_leap' : windup
      keys.push({ phase: 'active', at: at(travelStart), image: wind, hold: true, rot: -rise, scale: { x: 1.1, y: 0.92 }, ease: 'easeOut' })
      keys.push({ phase: 'active', at: at(travelStart + leg.travel * BITE_STRIKE_PROGRESS), image: bite, hold: true, rot: -fall,
        ...(biteIndex === 0 ? { tag: 'contact' as const } : {}), scale: { x: 1.06, y: 0.95 }, ease: 'easeOut' })
      biteIndex++
    }
    t += leg.warning + leg.travel
    prevY = leg.y
  })
  keys.push(
    { phase: 'recovery', at: 0.0, image: 'staggered_enter', hold: true, tag: 'followThrough', ease: 'easeOut' },
    // 倒れ込みの間は荒い息 (0.4秒周期で体が上下)。09-21 総評 B-4「疲れに見えない・今殴れの合図が弱い」。
    { phase: 'recovery', at: 0.2, image: 'staggered_hold', hold: true, scale: { x: 1.02, y: 0.97 }, ease: 'easeInOut' },
    { phase: 'recovery', at: 0.325, image: 'staggered_hold', hold: true, scale: { x: 0.985, y: 1.035 }, ease: 'easeInOut' },
    { phase: 'recovery', at: 0.45, image: 'staggered_hold', hold: true, scale: { x: 1.02, y: 0.97 }, ease: 'easeInOut' },
    { phase: 'recovery', at: 0.575, image: 'staggered_hold', hold: true, scale: { x: 0.985, y: 1.035 }, ease: 'easeInOut' },
    { phase: 'recovery', at: 0.7, image: 'staggered_hold', hold: true, scale: { x: 1.02, y: 0.97 }, ease: 'easeInOut' },
    { phase: 'recovery', at: 0.85, image: 'bite_recover', tag: 'recover', ease: 'easeOut' },
  )
  return keys
}

// ===== 騎士・境界の薙ぎ (lanceSweep) のコマ割り (2026-09-27) =====
// ユーザー原文 (07-04版・09-04版・現行の見比べ):「フレームが多いせいで無理に動きを作ろうとしていて不自然な動きに見える。
//   キーポーズだけ作って間はエフェクトなどでいい感じにごまかして…構えているところと振り終わったところは確実に必要で
//   その間をエフェクトで埋めるような形」。
//   keypose = 採用 (既定): idle → 構え (windup・連の2拍目以降は linkWindup) → 振り終わり (contact) を隙の終わりまで保持。
//     07-04 版 (2a1592b9) と同じ2枚。当時の 150ms クロスフェードは 08-05 に全ボスで廃止済み = ハードカットのまま。
//     間は render.ts の SMEAR_ARTS.lanceSweep_contact (振りの軌跡) が埋める。
//   0912 = 09-12 版 (90ce0428 / 09e28b29): 溜め末尾に turn・cut、隙の18%以降に生成構え ready。DEV `?knightSweep=0912` で比較。
//     絵 (lanceSweep_turn / _cut / _ready) は削除せず public に残す (常時ルール4)。
//   攻撃の時間 (charge700/active150/recovery640)・接触 (active 0.0)・判定は両版で同じ。
export type KnightSweepVariant = 'keypose' | '0912'
export const KNIGHT_SWEEP_TIMELINES: Record<KnightSweepVariant, BossAttackTimeline> = {
  keypose: [
    { phase: 'telegraph', at: 0.0, image: 'idle' },
    { phase: 'telegraph', at: 0.45, image: 'lanceSweep_windup', hold: true, tag: 'anticipation' },
    { phase: 'active', at: 0.0, image: 'lanceSweep_contact', hold: true, tag: 'contact' },
    { phase: 'recovery', at: 0.0, image: 'lanceSweep_contact', hold: true, tag: 'followThrough' },
  ],
  '0912': [
    { phase: 'telegraph', at: 0.0, image: 'idle' },
    { phase: 'telegraph', at: 0.45, image: 'lanceSweep_windup', hold: true, tag: 'anticipation' },
    { phase: 'telegraph', at: 0.8, image: 'lanceSweep_turn', hold: true },
    { phase: 'telegraph', at: 0.92, image: 'lanceSweep_cut', hold: true },
    { phase: 'active', at: 0.0, image: 'lanceSweep_contact', hold: true, tag: 'contact' },
    // 振り終わりを保って制動し、顔と胸を起こしたまま構え直す。
    { phase: 'recovery', at: 0.0, image: 'lanceSweep_contact', hold: true, tag: 'followThrough' },
    { phase: 'recovery', at: 0.18, image: 'lanceSweep_ready', hold: true, tag: 'recover' },
  ],
}
export type KnightResetMode = 'off' | 'on' | 'single' | 'prev'
// 2026-09-30 既定 = on (ユーザー「構え直し：途中の1枚あり」)。振り切り→中間→構え直し→立ち姿。旧の既定 (振り切り→ready) は DEV ?knightReset=off。
let knightResetMode: KnightResetMode = 'on'
export function getKnightResetMode(): KnightResetMode { return knightResetMode }
export function setKnightResetMode(mode: KnightResetMode): void { knightResetMode = mode }
export function getKnightReset(): boolean { return knightResetMode !== 'off' }
// Preserve the original boolean API for existing callers and comparison tests.
export function setKnightReset(on: boolean): void { setKnightResetMode(on ? 'prev' : 'off') }
/** DEV re-ready image; the normal timeline is returned untouched when disabled. */
export function knightResetRecoveryImage(attackId: string | undefined, elapsedMs: number): 'lanceSweep_contact' | 'lanceSweep_reset' | 'lanceSweep_rearmMid' | 'lanceSweep_rearm' | 'idle' | null {
  if (knightResetMode === 'off' || attackId !== 'lanceSweep') return null
  if (elapsedMs < 280) return 'lanceSweep_contact'
  if (elapsedMs >= 640) return 'idle'
  if (knightResetMode === 'prev') return 'lanceSweep_reset'
  return knightResetMode === 'on' && elapsedMs < 400 ? 'lanceSweep_rearmMid' : 'lanceSweep_rearm'
}
let knightSweepVariant: KnightSweepVariant = 'keypose'
export function getKnightSweepVariant(): KnightSweepVariant { return knightSweepVariant }
// ?knightSweep=0912 から呼ぶ (game.ts・DEV のみ)。プリロード前 (モジュール初期化時) に差し替えるので、絵の自動収集にも乗る。
export function setKnightSweepVariant(variant: KnightSweepVariant): void {
  knightSweepVariant = variant
  applyKnightSweepTimeline()
}

// ===== 連の払いの拍間 (2026-09-27 夜・S-4) =====
// ユーザー原文 (09-27c S-4「固まって見える」):「振ったエフェクトと絵があっていない。屈伸？しているような動きをしているのに
//   薙ぎのいちにエフェクトが出ている」。実測 (scripts/knight_sweep_string_probe.mjs・五連の払い):
//   拍間 = 振り終わり (contact) を active 150ms + beatGap 360ms 止めたまま → 次拍の溜め 400ms は深い低姿勢 (linkWindup) で止めたまま
//   = 2枚の静止を交互に切り替えるだけ (立つ→しゃがむ→立つ = 屈伸)。
//   直し: 次も薙ぎの拍間は、前半 (振り抜きの余韻) だけ contact を保ち、後半は「いま振った向きのまま穂先が流れた絵」へ送る。
//     2拍目以降 (低い構えから前で振り上げる弧 = ?sweepLink=low の時だけ) の後 = 前傾のまま穂先が前上へ抜ける holdOn (前79 / 上114)。
//     頭上を越えて前へ振り下ろす弧 (初段・および 09-29〜 既定の 2拍目以降) の後 = 振り終わり (contact) のまま次の構えへ。
//   ⛔ lanceSweep_release (穂先を床へ垂らして棒立ち) は使用禁止 (2026-09-29 ユーザー「これは二度と使わないで。この絵はどう見ても疲れている絵」)。
//     絵は public/sprites/archived/vigil-knight/ へ退避。以前は初段の後の拍間に出していた。
//   'holdOn' = どの拍の後も holdOn (比較用)。'hold' = 拍間ずっと contact。DEV `?sweepGap=holdOn|hold`。
//   拍の時間 (gapMs)・判定には触れない (絵の選択だけ)。
export type KnightSweepGap = 'follow' | 'holdOn' | 'hold'
// 連の2拍目以降の溜めの絵。弧は render.ts knightSweepSmearArt がこれに合わせて選ぶ。
//   'windup' = 単発と同じ構え (後ろ上に担ぐ) + 頭上を越える弧。**既定 (2026-09-29)**。
//     ユーザー 09-29「連の時の振り方が前の絵になってるけど新しいやつにしてほしい」→ 絵を並べて聞いた答え「構えを①単発と同じに」。
//   'low' = 深い低姿勢 lanceSweep_linkWindup (07-25 ユーザー「単発と違う構え」を受けた旧既定)。DEV `?sweepLink=low` で戻る。
//   突きの連 (lanceThrust_linkWindup) は変えていない。
export type KnightSweepLink = 'low' | 'windup'
let knightSweepLink: KnightSweepLink = 'windup'
export function getKnightSweepLink(): KnightSweepLink { return knightSweepLink }
export function setKnightSweepLink(link: KnightSweepLink): void { knightSweepLink = link }
export const KNIGHT_SWEEP_GAP_FOLLOW = 0.35   // 拍間のこの割合までは振り終わりの余韻 (contact) を保つ
let knightSweepGap: KnightSweepGap = 'follow'
export function getKnightSweepGap(): KnightSweepGap { return knightSweepGap }
export function setKnightSweepGap(gap: KnightSweepGap): void { knightSweepGap = gap }
// prevBeatIndex = いま振り終えた拍 (0 = 初段)。
export function knightSweepGapImage(gapElapsedMs: number, gapMs: number, prevBeatIndex: number): string {
  if (knightSweepGap === 'hold' || gapMs <= 0 || gapElapsedMs < gapMs * KNIGHT_SWEEP_GAP_FOLLOW) return 'lanceSweep_contact'
  if (knightSweepGap === 'holdOn') return 'lanceSweep_holdOn'
  const overhead = prevBeatIndex === 0 || knightSweepLink === 'windup'   // 直前の弧が頭上から振り下ろす形か
  return overhead ? 'lanceSweep_contact' : 'lanceSweep_holdOn'
}

// ===== 振りの重さの候補 (2026-09-27 夜・S-1「重さはもっと欲しい」) =====
// DEV `?sweepWeight=a|b|c` (既定 off = いまの版)。どれも攻撃の時間 (溜め700/当たり150/隙640)・接触 (active 0.0)・判定は変えない。
//   a = 当たった瞬間の手応え: 振り終わりの絵が一瞬つぶれて戻る + 画面が縦に小さく揺れる (描画だけ・時間は止めない)。
//       揺れは src/knight_sweep_weight.ts knightSweepImpactShake。
//   b = 溜めを長く深く: 構えを早め (0.45→0.30) に出し、体が後ろへ沈み続け、振る直前の 0.82 からは止まって溜める → 一気に振り終わり。
//       ⚠ 構えの絵が出る時刻が約 100ms 早まる (見た目の予告が早く読める)。当たる時刻は同じ。
//   c = 振り抜いた後の流れ: 振り終わってから体が前へ流れて戻る (隙の前半) + 踏み込んだ前足から砂ぼこり。
//       砂ぼこりは src/knight_sweep_weight.ts drawKnightSweepDust。
//   bc = b の溜め + c の振り抜いた後の流れと砂ぼこり (2026-09-28 検収パック S-7 ユーザー「組み合わせたい b,c」)。
export type KnightSweepWeight = 'off' | 'a' | 'b' | 'c' | 'bc' | 'scrape' | 'sound'
export const KNIGHT_SWEEP_WEIGHT_TIMELINES: Record<Exclude<KnightSweepWeight, 'off'>, BossAttackTimeline> = {
  scrape: KNIGHT_SWEEP_TIMELINES.keypose,
  // 'sound' = scrape の音だけ (床の粉は見本の絵から作り直し中・検収パック 09-28c)。
  sound: KNIGHT_SWEEP_TIMELINES.keypose,
  a: [
    { phase: 'telegraph', at: 0.0, image: 'idle' },
    // ease step = 構えの間は変位 0 のまま (次の接触キーのつぶれを溜めの間へ滲ませない)。
    { phase: 'telegraph', at: 0.45, image: 'lanceSweep_windup', hold: true, tag: 'anticipation', ease: 'step' },
    // 当たった瞬間に横へ張り縦へ縮む → 90ms で戻る (重いものが止まった手応え)。
    { phase: 'active', at: 0.0, image: 'lanceSweep_contact', hold: true, tag: 'contact', scale: { x: 1.035, y: 0.965 }, ease: 'easeOut' },
    { phase: 'active', at: 0.6, image: 'lanceSweep_contact', hold: true },
    { phase: 'recovery', at: 0.0, image: 'lanceSweep_contact', hold: true, tag: 'followThrough' },
  ],
  b: [
    { phase: 'telegraph', at: 0.0, image: 'idle' },
    { phase: 'telegraph', at: 0.3, image: 'lanceSweep_windup', hold: true, tag: 'anticipation', ease: 'easeOut' },
    // 後ろへ 5px・後傾 2.5°・わずかに沈んだ所で止める (ease step = 次のキーまで動かない = 溜めの頂点で静止)。
    { phase: 'telegraph', at: 0.82, image: 'lanceSweep_windup', hold: true, tag: 'anticipation',
      offset: { x: -5 }, rot: -2.5, scale: { y: 0.985 }, ease: 'step' },
    { phase: 'active', at: 0.0, image: 'lanceSweep_contact', hold: true, tag: 'contact' },
    { phase: 'recovery', at: 0.0, image: 'lanceSweep_contact', hold: true, tag: 'followThrough' },
  ],
  c: [
    { phase: 'telegraph', at: 0.0, image: 'idle' },
    { phase: 'telegraph', at: 0.45, image: 'lanceSweep_windup', hold: true, tag: 'anticipation' },
    { phase: 'active', at: 0.0, image: 'lanceSweep_contact', hold: true, tag: 'contact' },
    // 弧が消えるまで (active 150ms) は止めておき、隙に入ってから前へ 7px・前傾 2° 流れて戻る。
    { phase: 'recovery', at: 0.0, image: 'lanceSweep_contact', hold: true, tag: 'followThrough', ease: 'easeOut' },
    { phase: 'recovery', at: 0.2, image: 'lanceSweep_contact', hold: true, offset: { x: 7 }, rot: 2, scale: { y: 0.985 }, ease: 'easeInOut' },
    { phase: 'recovery', at: 0.6, image: 'lanceSweep_contact', hold: true },
  ],
  bc: [
    { phase: 'telegraph', at: 0.0, image: 'idle' },
    { phase: 'telegraph', at: 0.3, image: 'lanceSweep_windup', hold: true, tag: 'anticipation', ease: 'easeOut' },
    { phase: 'telegraph', at: 0.82, image: 'lanceSweep_windup', hold: true, tag: 'anticipation',
      offset: { x: -5 }, rot: -2.5, scale: { y: 0.985 }, ease: 'step' },
    { phase: 'active', at: 0.0, image: 'lanceSweep_contact', hold: true, tag: 'contact' },
    { phase: 'recovery', at: 0.0, image: 'lanceSweep_contact', hold: true, tag: 'followThrough', ease: 'easeOut' },
    { phase: 'recovery', at: 0.2, image: 'lanceSweep_contact', hold: true, offset: { x: 7 }, rot: 2, scale: { y: 0.985 }, ease: 'easeInOut' },
    { phase: 'recovery', at: 0.6, image: 'lanceSweep_contact', hold: true },
  ],
}
let knightSweepWeight: KnightSweepWeight = 'off'
export function getKnightSweepWeight(): KnightSweepWeight { return knightSweepWeight }
// ?sweepWeight=a|b|c から呼ぶ (game.ts・DEV のみ)。09-12 版 (?knightSweep=0912) の時は効かない (キーポーズ版だけの候補)。
export function setKnightSweepWeight(weight: KnightSweepWeight): void {
  knightSweepWeight = weight
  applyKnightSweepTimeline()
}
function applyKnightSweepTimeline(): void {
  BOSS_ATTACK_TIMELINES['vigil-knight']!.lanceSweep = knightSweepVariant === 'keypose' && knightSweepWeight !== 'off'
    ? KNIGHT_SWEEP_WEIGHT_TIMELINES[knightSweepWeight] : KNIGHT_SWEEP_TIMELINES[knightSweepVariant]
}

export const BOSS_ATTACK_TIMELINES: Record<string, Record<string, BossAttackTimeline>> = {
  'dream-hound': {
    houndSwipe: [
      { phase: 'telegraph', at: 0, image: 'idle', hold: true },
      { phase: 'telegraph', at: 0.3, image: 'houndSwipe_windup', hold: true, tag: 'anticipation' },
      { phase: 'active', at: 0, image: 'houndSwipe_contact', hold: true, tag: 'contact' },
      { phase: 'recovery', at: 0, image: 'houndSwipe_contact', hold: true, tag: 'followThrough' },
      { phase: 'recovery', at: .6, image: 'idle', hold: true },
    ],
    houndTripleSwipe1: [
      { phase: 'telegraph', at: 0, image: 'idle', hold: true },
      { phase: 'telegraph', at: 0.2, image: 'houndTriple_ready', hold: true, tag: 'anticipation' },
      { phase: 'active', at: 0, image: 'houndTriple_swipe1', hold: true, tag: 'contact' },
      { phase: 'recovery', at: 0, image: 'houndTriple_swipe1', hold: true, tag: 'followThrough' },
      { phase: 'recovery', at: .6, image: 'idle', hold: true },
    ],
    houndTripleSwipe2: [
      { phase: 'telegraph', at: 0, image: 'houndTriple_swipe1', hold: true, tag: 'anticipation' },
      { phase: 'active', at: 0, image: 'houndTriple_swipe2', hold: true, tag: 'contact' },
      { phase: 'recovery', at: 0, image: 'houndTriple_swipe2', hold: true, tag: 'followThrough' },
      { phase: 'recovery', at: .6, image: 'idle', hold: true },
    ],
    houndTripleSlam: [
      { phase: 'telegraph', at: 0, image: 'houndTriple_rise', hold: true, tag: 'anticipation' },
      { phase: 'active', at: 0, image: 'houndTriple_slam', hold: true, tag: 'contact' },
      { phase: 'recovery', at: 0, image: 'houndTriple_slam', hold: true, tag: 'followThrough' },
      { phase: 'recovery', at: .6, image: 'idle', hold: true },
    ],
    // 幕2の三連 (新・2026-10-08・src/hound_kit.ts hound-triple2)。今ある絵の使い回し: 構え (三連の描き直し版の構え) →
    //   爪 (奥の前脚 houndTriple_swipeFar) → 牙 (噛みの溜め→噛み) → 両前脚の振り上げ → 手前の前脚の叩きつけ。
    //   offset.x は後ろ足の踵の位置合わせ (絵は外接矩形の中心で置かれる)。当たりの絵はずらさない (当たりの距離を守る)。
    //   後ろ足の踵 (外接矩形の中心から後ろへ・ゲーム px・目で測った値): 待機 108・構え 90・爪 116・噛みの溜め 155・噛み 132・
    //   振り上げ 124・叩きつけ 148。切り替えで踵が跳ぶのは 8px 以内 (tests/hound_triple2.test.ts)。
    houndTriple2Claw: [
      { phase: 'telegraph', at: 0, image: 'idle', hold: true, ease: 'step' },
      { phase: 'telegraph', at: 0.12, image: 'houndTriple_stance', hold: true, tag: 'anticipation', ease: 'easeInOut', offset: { x: -26 } },
      { phase: 'telegraph', at: 0.85, image: 'houndTriple_stance', hold: true, ease: 'step', offset: { x: -26 }, scale: { y: 0.98 } },
      { phase: 'active', at: 0, image: 'houndTriple_swipeFar', hold: true, tag: 'contact' },
      { phase: 'recovery', at: 0, image: 'houndTriple_swipeFar', hold: true, tag: 'followThrough' },
      { phase: 'recovery', at: 0.6, image: 'idle', hold: true },
    ],
    houndTriple2Fang: [
      { phase: 'telegraph', at: 0, image: 'houndTriple_swipeFar', hold: true, ease: 'step' },
      { phase: 'telegraph', at: 0.15, image: 'houndBite_windup_1007', hold: true, tag: 'anticipation', ease: 'easeInOut', offset: { x: 31 } },
      { phase: 'telegraph', at: 0.8, image: 'houndBite_windup_1007', hold: true, ease: 'step', offset: { x: 31 }, scale: { y: 0.98 } },
      { phase: 'active', at: 0, image: 'houndBite_contact', hold: true, tag: 'contact' },
      { phase: 'recovery', at: 0, image: 'houndBite_contact', hold: true, tag: 'followThrough' },
      { phase: 'recovery', at: 0.6, image: 'idle', hold: true },
    ],
    houndTriple2Slam: [
      { phase: 'telegraph', at: 0, image: 'houndBite_contact', hold: true, ease: 'step' },
      // 3発目の前のはっきりした溜め: 両前脚を冠の高さへ振り上げ、ゆっくりのけぞり続けて最後に一瞬止まる (約1秒見える)。
      { phase: 'telegraph', at: 0.1, image: 'houndTriple_riseBoth', hold: true, tag: 'anticipation', ease: 'easeOut', offset: { x: -16 } },
      { phase: 'telegraph', at: 0.8, image: 'houndTriple_riseBoth', hold: true, ease: 'step', offset: { x: -16 }, scale: { y: 1.03 }, rot: -2.5 },
      { phase: 'active', at: 0, image: 'houndTriple_slam', hold: true, tag: 'contact' },
      { phase: 'recovery', at: 0, image: 'houndTriple_slam', hold: true, tag: 'followThrough' },
      { phase: 'recovery', at: 0.6, image: 'idle', hold: true },
    ],
    houndTailSweep: [
      { phase: 'telegraph', at: 0, image: 'idle', hold: true },
      { phase: 'telegraph', at: 0.15, image: 'houndTail_windup', hold: true, tag: 'anticipation' },
      { phase: 'active', at: 0, image: 'houndTail_contact', hold: true, tag: 'contact' },
      { phase: 'recovery', at: 0, image: 'houndTail_contact', hold: true, tag: 'followThrough' },
      { phase: 'recovery', at: .6, image: 'idle', hold: true },
    ],
    houndGrab: [
      { phase: 'telegraph', at: 0, image: 'idle', hold: true },
      { phase: 'telegraph', at: 0.25, image: 'houndGrab_windup', hold: true, tag: 'anticipation' },
      { phase: 'active', at: 0, image: 'houndGrab_pin', hold: true, tag: 'contact' },
      { phase: 'recovery', at: 0, image: 'houndGrab_pin', hold: true, tag: 'followThrough' },
      { phase: 'recovery', at: 0.4, image: 'houndGrab_throw', hold: true },
      { phase: 'recovery', at: .8, image: 'idle', hold: true },
    ],
    houndPounce: [
      // 10-07: 立ち上がって吠える絵は待機を土台に描き直した R1 (旧 houndExecution_windup は冠が待機の79%・胸の毛の塊で別個体に見えた)。旧＝DEV ?houndArt=old。
      { phase: 'telegraph', at: 0, image: 'houndPounce_rearup', hold: true, tag: 'anticipation', ease: 'step', offset: { y: 0 } },
      // 10-07: 跳躍と着地は専用の絵 (見本 leap_A / land_A)。旧は既存の lunge_precontact / execution_contact の流用。
      { phase: 'telegraph', at: 1100 / 1420, image: 'houndPounce_leap', hold: true, offset: { y: 0 }, ease: 'easeOut' },
      { phase: 'telegraph', at: 1260 / 1420, image: 'houndPounce_leap', hold: true, offset: { y: -70 }, ease: 'easeIn' },
      { phase: 'active', at: 0, image: 'houndPounce_land', hold: true, tag: 'contact', offset: { y: 0 }, ease: 'step' },
      // 10-07 user: 「攻撃後の上を向いてる絵」「最後のよくわからない絵」が不評 → 着地の絵のまま重さを保ち、起き上がって待機へ戻す。
      { phase: 'recovery', at: 0, image: 'houndPounce_land', hold: true, tag: 'followThrough' },
      { phase: 'recovery', at: 0.6, image: 'idle', hold: true },
    ],
    houndRoar: [
      { phase: 'telegraph', at: 0, image: 'telegraph', hold: true, tag: 'anticipation' },
      { phase: 'active', at: 0, image: 'active', hold: true },
      { phase: 'recovery', at: 0, image: 'houndBite_follow', hold: true, tag: 'followThrough' },
    ],
    houndBite: [
      { phase: 'telegraph', at: 0.0, image: 'idle' },
      { phase: 'telegraph', at: 0.4, image: 'houndBite_windup_1007', hold: true, tag: 'anticipation' },
      { phase: 'active', at: 0.0, image: 'houndBite_contact', tag: 'contact' },
      { phase: 'recovery', at: 0.3, image: 'houndBite_follow', tag: 'followThrough' },
    ],
    houndLunge: [
      { phase: 'telegraph', at: 0.0, image: 'idle' },
      { phase: 'telegraph', at: 0.4, image: 'houndLunge_windup_1007', hold: true, tag: 'anticipation' },
      { phase: 'active', at: 0.0, image: 'houndLunge_precontact' },
      { phase: 'active', at: 0.25, image: 'houndLunge_contact', tag: 'contact' },
      { phase: 'recovery', at: 0.3, image: 'houndLunge_follow', tag: 'followThrough' },
    ],
    // 駆け抜け (2026-10-10・比較版 DEV ?houndPassThrough=1・src/hound_pass_through.ts)。当たり無し。
    //   溜め = 頭を床まで下げて腰を上げる (houndLunge_follow) = 突進牙 (高く立ち上がる)・噛み・のしかかりと違う「走り出す」構え。
    // 2026-10-11 ユーザー (答えるページ hound-pass)「この形で進める。振り返り・駆ける途中・滑り止まりの絵を足してから配布版へ」→ 専用の絵5枚
    //   (Codex・素材 そざい/boss-study/hound-pass-1011/)。旧は突進牙の絵の流用で、振り返りは待機に戻った所の鏡像の一瞬反転だった。
    //   走り = 後脚で蹴り出す (runPush) と四肢が伸びて宙にある (runExtend) を交互 = 床すれすれを跳ねるように駆ける。
    //   止まり = 前脚を突っ張って滑る (skid・recovery の頭 300ms で 60px 滑る) → 止まったまま背中を見せる (隙)。
    //   振り返り = 首だけ肩越しに振り返る (turnLook) → 体ごと斜め前を向いて前脚を踏み出す (turnFront・頭と胸は相手の方)
    //     → recovery の終わりで待機へ入り向きが反転 (turnFront の頭の向きと反転後の待機の頭の向きが揃うので、鏡の一瞬反転に見えない)。
    //   時間は旧と同じ (recovery 750ms・背中を見せる長さも同じ)。足の位置は sprites.ts HOUND_PASS_ANCHOR_X で揃える (足滑りの測定は同じ所)。
    houndPassRun: [
      { phase: 'telegraph', at: 0.0, image: 'idle' },
      { phase: 'telegraph', at: 0.25, image: 'houndLunge_follow', hold: true, tag: 'anticipation' },
      { phase: 'active', at: 0.0, image: 'houndPass_runPush', hold: true },
      { phase: 'active', at: 0.25, image: 'houndPass_runExtend', hold: true },
      { phase: 'active', at: 0.5, image: 'houndPass_runPush', hold: true },
      { phase: 'active', at: 0.75, image: 'houndPass_runExtend', hold: true },
      { phase: 'recovery', at: 0.0, image: 'houndPass_skid', hold: true, tag: 'followThrough' },
      { phase: 'recovery', at: HOUND_PASS_TURN_LOOK_AT, image: 'houndPass_turnLook', hold: true },
      { phase: 'recovery', at: HOUND_PASS_TURN_FRONT_AT, image: 'houndPass_turnFront', hold: true },
    ],
    // 必殺技「噛みつき固め」(体幹崩し中の致命)。 通常噛みと逆=高く振りかぶり↑→叩きつけ→固め→引き上げ。
    //   chargeMs500/activeMs250/recoveryMs700。 contact=active 0.0 (叩きつけ即発生)。 パリィ不能=見せ場。
    houndExecution: [
      { phase: 'telegraph', at: 0.0, image: 'idle' },
      { phase: 'telegraph', at: 0.45, image: 'houndPounce_rearup', hold: true, tag: 'anticipation' },
      { phase: 'active', at: 0.0, image: 'houndExecution_contact', tag: 'contact' },
      { phase: 'active', at: 0.5, image: 'houndExecution_hold' },
      { phase: 'recovery', at: 0.3, image: 'houndExecution_release', tag: 'followThrough' },
    ],
  },
  // ※ 花嫁の旧スライス・タイムライン (veil_windup/contact/follow) は E-9 で本番3枚絵
  //   (idle/active=ヴェール薙ぎ/defeat) へ移行し退役。 stateToKey の単純経路で active=薙ぎを表示。

  // 主なき近衛騎士 (2026-06-13 モーションバッチ1・9枚反映)。
  //   lanceThrust (背骨): idle→溜め始め→溜め切り(hold=長い読み窓)→刺突(contact)→穂先床(overextend=攻め時)→立ち直り。
  //     ★contact は新生成 strike で画風統一 (既存 active.png は汎用スロットに温存)。
  //   lanceSweep (弾きの出題): idle→斜め担ぎ(hold)→薙ぎ振り抜き(contact)。 弧残像は演出部 FX (絵には描かない)。
  //   lanceButt (張り付きの罰): 石突を腰だめに持ち替え→前下へ短く突き。 持ち替え=離れろ (§4.4)。
  'vigil-knight': {
    // lanceThrust (A-lite 2026-06-15): 判定正直性修正。
    //   hold は outgoing 指定 (「この絵から次へ溶かすな」) = 離散スナップ中割り実現。
    //   active 0.0 = lanceThrust_full (最大リーチ頂点) → 判定が生きる間ずっと頂点ポーズ。
    lanceThrust: [
      { phase: 'telegraph', at: 0.0, image: 'idle' },
      { phase: 'telegraph', at: 0.25, image: 'lanceThrust_coilStart', hold: true, tag: 'anticipation' },
      { phase: 'telegraph', at: 0.4, image: 'lanceThrust_windup', hold: true, tag: 'anticipation' },
      { phase: 'telegraph', at: 0.85, image: 'lanceThrust_strike', hold: true },
      { phase: 'active', at: 0.0, image: 'lanceThrust_full', tag: 'contact' },
      { phase: 'recovery', at: 0.0, image: 'lanceThrust_overextend', hold: true },
      { phase: 'recovery', at: 0.55, image: 'lanceThrust_recover', tag: 'followThrough' },
    ],
    // 2026-09-27 ユーザー裁定 (07-04版との見比べ): 構え (windup) と振り終わり (contact) の2枚だけ。
    //   間は描画側の軌跡FX (render.ts SMEAR_ARTS.lanceSweep_contact) で埋める。09-12 版は KNIGHT_SWEEP_TIMELINES['0912']。
    lanceSweep: KNIGHT_SWEEP_TIMELINES.keypose,
    lanceButt: [
      { phase: 'telegraph', at: 0.0, image: 'idle' },
      { phase: 'telegraph', at: 0.45, image: 'lanceButt_windup', hold: true, tag: 'anticipation' },
      { phase: 'active', at: 0.0, image: 'lanceButt_slam', tag: 'contact' },
      { phase: 'recovery', at: 0.3, image: 'lanceButt_slam', tag: 'followThrough' },
    ],
    // 致命 (崩し気絶への処刑・parryable:false=見せ場)。 charge550/active250/recovery850。
    //   槍を垂直↑に振りかぶり(windup・長い読み)→静かに刺し貫く(impale=contact)。 contact 即発生。
    knightExecution: [
      { phase: 'telegraph', at: 0.0, image: 'idle' },
      { phase: 'telegraph', at: 0.45, image: 'knightExecution_windup', hold: true, tag: 'anticipation' },
      { phase: 'active', at: 0.0, image: 'knightExecution_impale', tag: 'contact' },
      { phase: 'recovery', at: 0.4, image: 'knightExecution_impale', tag: 'followThrough' },
    ],
    // 2026-09-29 (ユーザー「切り下がりは薙ぎと似ていてタイミングをずらされる技」): 構えの絵が出る瞬間を単発の薙ぎ
    //   (700ms の 0.45 = 315ms) にそろえ、構えている時間だけ長い (薙ぎ 385ms / 跳び下がり 585ms)。
    //   見分けは構えの間の体 (render.ts knightMoveBodyPose・src/knight_spacing.ts KnightLeapTell)。
    // 2026-10-01 詰めて→払って→跳び下がる: 払い (active) は薙ぎと同じ振り終わりの絵 = 薙ぎの弧 (render.ts SMEAR_ARTS.lanceSweep_contact)
    //   がそのまま出る。跳び (10-01 夜 前に出ず active の頭から 300ms・払いの絵は挟まない) は leap_swing、着地して擦る所から scrape_stop。
    lanceCross: [
      { phase: 'telegraph', at: 0.0, image: 'idle' },
      { phase: 'telegraph', at: 315 / 900, image: 'lanceSweep_windup', hold: true, tag: 'anticipation' },
      // 10-01 夜 前に出ず、その場から跳び下がりながら払う = 構えから跳びの絵へ直接 (槍の高さ = 横に流れる帯の高さ)。
      { phase: 'active', at: 0.0, image: 'leap_swing', hold: true, tag: 'contact' },
      { phase: 'recovery', at: 0.0, image: 'leap_swing', hold: true },
      // 着地 = 跳び始め (10-02 から払い切った active の 70ms) から浮き 300ms = recovery の 150ms。
      { phase: 'recovery', at: 150 / 1250, image: 'scrape_stop', hold: true, tag: 'followThrough' },
    ],
    // 2026-10-01 柄の押し返し: 構えの絵は溜めの頭から (10-01 夜 溜め 700 → 180ms・それまでは薙ぎと同じ 315ms)。槍を縦に立てて胸元へ引き寄せる構え →
    //   両腕で立てた柄を前へ押し出す (ユーザー「押す絵を作った方がいい」・票 knight-shove-art-1001)。槍が縦なので
    //   突き (横に寝かせる)・石突の返し (斜めに持ち替える)・薙ぎ (担ぐ) と構えで見分けられる。
    //   最初の試作 (lanceCross_backPlant = 横構え) は「刺しているように見える」ので差し替えた。
    lanceShove: [
      // 10-01 夜 溜め 180ms (唐突に出す) = 構えの絵は溜めの頭から。
      { phase: 'telegraph', at: 0.0, image: 'lanceShove_windup', hold: true, tag: 'anticipation' },
      // 10-01 夜 踏み込みを 240ms の一歩にした (attacks.ts forwardWarp) = 寄る間は構えのまま、柄が届く頃 (165ms・残り約 20px) に押す絵へ。
      { phase: 'active', at: 165 / 300, image: 'lanceShove_contact', hold: true, tag: 'contact' },
      { phase: 'recovery', at: 0.6, image: 'idle', tag: 'followThrough' },
    ],
    knightWallCharge: [
      { phase: 'telegraph', at: 0.0, image: 'lanceThrust_windup', hold: true, tag: 'anticipation' },
      { phase: 'active', at: 0.0, image: 'charge_run1', hold: true, tag: 'contact' },
      { phase: 'recovery', at: 0.0, image: 'scrape_stop', hold: true },
      { phase: 'recovery', at: (327 + 300) / 1550, image: 'turn', hold: true },
      { phase: 'recovery', at: (327 + 300 + 220) / 1550, image: 'scrape_stop', hold: true },
    ],
  },

  // 待宵の花嫁 (2026-06-14 本番絵モーション Tier1・設計書 §5.3)。
  //   veilSweep (弾き・showpiece): idle→veil_coil→veil_twist(ひねって巻き付ける溜め)→前へ払う contact→振り抜き (10-21)。
  //   veilInvitation / thornAisle (位置どり=ゾーン): 差し伸べ/屈み の hold ポーズ。 ゾーン本体はコードFX。
  //   vowEmbrace (掴み): embrace_open(hold)→embrace_close(contact)→空振り崩れ(反撃窓)。
  //   brideExecution (気絶P掴み): exec_rise(hold)→exec_cover(contact)。 花嫁が殺される側の死=death_collapse は撃破描画側。
  //   ⚠️ 抽出キー (image) は public/sprites/bosses/bride/<key>.png と一致。 STATE_KEYS(idle/telegraph/active/
  //      parried/staggered/defeat) は preloadBossSprites が自動ロード。 at は attacks.ts の各 Ms と整合。
  'bride': {
    // 設計 (2026-06-27 白紙再設計): エンジンは contact フレームの前後と telegraph→active を必ずハードカット
    //   (shouldCrossfade=接触の読みを死守)。 ゆえに打点絵は active のみに置き、 見た目のピーク=当たり判定を厳密一致
    //   させる (理不尽=見た目と判定のズレを作らない・最優先)。 溜め(idle→coil)は同 phase で crossfade=滑らか、
    //   打点(coil→全開)は telegraph→active の強制スナップ=破裂の速さとして正しい。
    //   ⚠️ 残るカクつき=全開→片流れ (contact 隣接の強制ハードカット)。 布として完全に滑らかにするには
    //      中割り絵 (全開↔片流れ の間 1枚) が必要 = 別途発注で対応。 現状は honest 土台。
    // 横薙ぎ・返しの一閃・三重の返礼は 10-21 の手本合わせ (BRIDE_MOTION_TIMELINE_VARIANTS・前の版は ?brideMotion=old)。
    ...BRIDE_MOTION_TIMELINE_VARIANTS.a,
    // 喪の翻し: 開いたヴェール+共通赤オーラで「弾くな」を開始時点から見せる。
    veilSweepPerilous: [
      { phase: 'telegraph', at: 0.0, image: 'veil_open', hold: true, tag: 'anticipation' },
      { phase: 'active', at: 0.0, image: 'veil_sweep_contact', hold: true, tag: 'contact' },
      { phase: 'recovery', at: 0.0, image: 'veil_follow', hold: true, tag: 'followThrough' },
    ],
    veilInvitation: [
      { phase: 'telegraph', at: 0.0, image: 'idle' },
      // 2026-07-02 溜め厚化: idle→prep→reach→full の 3 段。 prep=両手が持ち上がり始めた予備動作
      //   (2026-07-02 発注・chargeMs 850ms のうち prep が 15-30% = ~128ms 見せ場・reach が 30-100% = 595ms 保持)。
      { phase: 'telegraph', at: 0.15, image: 'invitation_prep', tag: 'anticipation' },
      { phase: 'telegraph', at: 0.30, image: 'invitation_reach', hold: true, tag: 'anticipation' },
      { phase: 'active', at: 0.0, image: 'invitation_full', hold: true },
    ],
    // 1-C 修正 (2026-06-27): 旧版は 開く(溜め)→閉じる(ヒット)→崩れ(復帰) だが、 崩れ絵 embrace_collapse は
    //   腕を再び広げて見え「また掴みに行く」往復に読めた (ユーザー指摘)。 復帰は抱き込んだ姿勢
    //   (embrace_close) を保ったまま idle へ戻す = 再び腕を広げない。 embrace_collapse は当面不使用。
    vowEmbrace: [
      { phase: 'telegraph', at: 0.0, image: 'idle' },
      // 2026-07-02 溜め厚化: 腕を開く動作を早めに見せる (0.45→0.30)。
      { phase: 'telegraph', at: 0.30, image: 'embrace_open', hold: true, tag: 'anticipation' },
      { phase: 'active', at: 0.0, image: 'embrace_close', tag: 'contact' },
      { phase: 'recovery', at: 0.0, image: 'embrace_close', tag: 'followThrough' },
    ],
    thornAisle: [
      { phase: 'telegraph', at: 0.0, image: 'idle' },
      { phase: 'telegraph', at: 0.4, image: 'aisle_kneel', hold: true, tag: 'anticipation' },
      // active を屈み姿勢で明示 (汎用 active.png フォールバックを使わせない = 床技で翼全開を出さない)。
      { phase: 'active', at: 0.0, image: 'aisle_kneel', hold: true },
      { phase: 'recovery', at: 0.4, image: 'aisle_rise', tag: 'followThrough' },
    ],
    // brideExecution = 気絶したプレイヤーを掴んで殺す技 (boss.ts: player.state==='stunned' で発火・playerLethal)。
    //   = 花嫁が"花婿(LUNAIRE)"を抱き込む側 (攻め勝つ瞬間)。 rise(立ち上がり)→cover(覆い被さり contact) で完結。
    //   ※「花嫁が殺される側」の死の悲しみ (迎えに伸ばした手が落ち崩れ落ちる) は別物 = 撃破描画で扱う
    //     (sprites.ts BOSS_DEATH_COLLAPSE / death_collapse.png)。 ここ (timeline) には置かない。
    brideExecution: [
      { phase: 'telegraph', at: 0.0, image: 'idle' },
      // 2026-07-02 溜め厚化: idle→prep→rise→cover の 3 段。 prep=立ち上がる寸前・気配が満ちる瞬間
      //   (2026-07-02 発注・chargeMs 550ms のうち prep が 15-30% = ~83ms 見せ場・rise が 30-100% = 385ms 保持)。
      { phase: 'telegraph', at: 0.15, image: 'exec_prep', tag: 'anticipation' },
      { phase: 'telegraph', at: 0.30, image: 'exec_rise', hold: true, tag: 'anticipation' },
      { phase: 'active', at: 0.0, image: 'exec_cover', tag: 'contact' },
    ],
    // ⓪ 裾乱れ (裏取り連撃・2026-07-10 / 2026-07-11 改): 溜め(flurry_charge)→高速旋回連(右/正面/左を巡回)
    //   →振りかぶり(flurry_slam_windup=予備動作)→だん(flurry_slam)。 comboHits=9。
    //   ★回転の見せ方 (2026-07-11 ユーザー裁定A): 体は正面のまま右/正面/左の薙ぎ3枚を巡回し、
    //     "回ってる"感は procedural の回り込みヴェールアーク (game.ts spin 角 → render drawVeilSlashArc)
    //     が担う。 白い塊のピルエット(横/背面作画)は読みにくいので採らない (design_malenia_readability)。
    //   🔵 2026-10-09 決め直し: 回る途中の絵 (真横/斜め後ろ/真後ろ) を体の向きの角で差し替える版をユーザーが動画で選び配布版へ。
    //     差し替えは描画側 (src/bride_spin_fx.ts brideTurnPick・render.ts)。この表の右/正面/左は正面の区間で使う。
    //   comboGap 中の hitIndex = 「直前に完了したヒット番号」(既存文法通り)。
    veilFlurry: [
      { phase: 'telegraph', at: 0.0, image: 'idle', hold: true },
      { phase: 'telegraph', at: 0.3, image: 'idle' },
      { phase: 'telegraph', at: 0.35, image: 'flurry_charge', hold: true, tag: 'anticipation' },
      // hit0-6 = 高速旋回連 (右→正面→左を巡回)。 各 active = 単一絵 hold=true + 回り込みアークFX。
      { phase: 'active', at: 0.0, image: 'flurry_swing_right', hold: true, tag: 'contact', hitIndex: 0 },
      { phase: 'comboGap', at: 0.0, image: 'flurry_swing_right', hold: true, hitIndex: 0 },
      { phase: 'active', at: 0.0, image: 'flurry_swing_cross', hold: true, tag: 'contact', hitIndex: 1 },
      { phase: 'comboGap', at: 0.0, image: 'flurry_swing_cross', hold: true, hitIndex: 1 },
      { phase: 'active', at: 0.0, image: 'flurry_swing_left', hold: true, tag: 'contact', hitIndex: 2 },
      { phase: 'comboGap', at: 0.0, image: 'flurry_swing_left', hold: true, hitIndex: 2 },
      { phase: 'active', at: 0.0, image: 'flurry_swing_right', hold: true, tag: 'contact', hitIndex: 3 },
      { phase: 'comboGap', at: 0.0, image: 'flurry_swing_right', hold: true, hitIndex: 3 },
      { phase: 'active', at: 0.0, image: 'flurry_swing_cross', hold: true, tag: 'contact', hitIndex: 4 },
      { phase: 'comboGap', at: 0.0, image: 'flurry_swing_cross', hold: true, hitIndex: 4 },
      { phase: 'active', at: 0.0, image: 'flurry_swing_left', hold: true, tag: 'contact', hitIndex: 5 },
      { phase: 'comboGap', at: 0.0, image: 'flurry_swing_left', hold: true, hitIndex: 5 },
      { phase: 'active', at: 0.0, image: 'flurry_swing_right', hold: true, tag: 'contact', hitIndex: 6 },
      // hit6→hit7 の gap (440ms): 振り終わり(right)を保持 → 後半で振りかぶりへ移行 = だんの予兆開始
      { phase: 'comboGap', at: 0.0, image: 'flurry_swing_right', hold: true, hitIndex: 6 },
      { phase: 'comboGap', at: 0.55, image: 'flurry_slam_windup', hold: true, hitIndex: 6 },
      // hit7 = 振りかぶり (判定なしの予備動作・頂点で静止=「来る」を見せて早出し防御を釣る)
      { phase: 'active', at: 0.0, image: 'flurry_slam_windup', hold: true, hitIndex: 7 },
      // hit7→hit8 の gap (640ms): 振りかぶりを保持したまま「間」= 最長のタメ
      { phase: 'comboGap', at: 0.0, image: 'flurry_slam_windup', hold: true, hitIndex: 7 },
      // hit8 = だん (叩きつけ・本命・唯一の重い一撃)
      { phase: 'active', at: 0.0, image: 'flurry_slam', hold: true, tag: 'contact', hitIndex: 8 },
      { phase: 'recovery', at: 0.0, image: 'flurry_slam', hold: true },
    ],
  },

  // 処刑人 バッチ1+2 (2026-07-05/06 特大槌キーポーズ・14+14枚) 配線 v2。
  //   🔴 この設計(特大槌)は2026-08-15〜18に刃方式へ全面白紙化され、下記の実装はもう特大槌時代のものではない。
  //   設計正本(archive済・当時の記録): docs/_archived/departments/art/boss-motion-design/2026-07-05-executioner-hammer-motion-design.md
  //   背骨=「同じ縦振りシルエットを溜めの長さで読む」(§2 共有ポーズ語彙 S1-S3)。
  //   バッチ2 (2026-07-06) で B4/B5/C7/G11/E10/A2/D9 の専用絵が揃い、A1 流用配線を解消。
  'the-executioner': {
    // 振り下ろし（半引き）。弓と同じく、重く当てるほど体ごと深く引き、引き切って止まったら「離れ」。
    // 3技の引き絞り量 -18 / -30 / -46 は 🟨ラボ判定前の仮値。外付けの印ではなく体で見せる。
    // 正本 = docs/candidates/2026-08-06-executioner-aim-charge-redesign.md
    exec_maulDrop: [
      { phase: 'telegraph', at: 0.0, image: 'idle', hold: true,
        offset: { y: 0 }, rot: 0, scale: { x: 1, y: 1 } },
      { phase: 'telegraph', at: 0.2, image: 'idle',
        offset: { y: 0 }, rot: 0, scale: { x: 1, y: 1 } },
      // ★2026-08-20: ユーザー実機「振り下ろしだけ攻撃の瞬間、絵の切り替わりが飛んでる感じ」。
      //   測ったら**体の落ちる量が兄弟技の半分以下**だった (この技 24px/3° に対し
      //   待ち落とし 36px/9°・据えの一打 52px/17°。絵の並びは3本とも同じ smear→overhead_contact)。
      //   落ちる量が少ないと絵の切り替わりを隠すものが無く、カットがむき出しになる。
      //   さらに 2026-08-20 に overhead_repull を全廃したことで、この技の溜めは
      //   **1枚の絵を888ms保つだけ・動きは12px** になっていた = 3本の中で一番痩せていた。
      //   → **ユーザーが「OK」と言った待ち落としの帯 (36px/9°) へ寄せる。** 数値の出所はその2本。
      //   ⚠️ 秒数・判定・tag は不変 = 描画の動きだけ。
      // 🔵 2026-09-02 見た目の棚卸し (docs/reports/2026-09-02-executioner-visual-audit.md §C1):
      //   offset.y を -30 まで上げると絵ごと持ち上がり、実舞台で**靴が床から約16px浮いて影との間に床が見えた**
      //   (絵は足元ピボットで -12px 沈めてあるので、それ以上の -y は「浮く」)。
      //   → 持ち上がる量 (30px/8°) は変えず、出し方を変える: offset.y は沈み分の予算 -12 まで、残りは
      //   足元ピボットの scale.y (伸び上がり) で出す。頭の到達点は旧とほぼ同じ (12 + 240×0.075 ≒ 30px)。
      //   ~~offset.y -8 / -18 / -26 / -30・scale.y 1.003 / 1.008 / 1.015 / 1.02~~
      { phase: 'telegraph', at: 0.26, image: 'overhead_raise', hold: true, tag: 'anticipation',
        ease: 'easeOut', offset: { y: -4 }, rot: -1, scale: { x: 0.99, y: 1.02 } },
      { phase: 'telegraph', at: 0.6, image: 'overhead_raise', ease: 'easeIn',
        offset: { y: -8 }, rot: -4, scale: { x: 0.98, y: 1.045 } },
      { phase: 'telegraph', at: 0.84, image: 'overhead_raise', hold: true, tag: 'anticipation',
        ease: 'easeIn', offset: { y: -11 }, rot: -7, scale: { x: 0.975, y: 1.065 } },
      // 静止は作らず、最後まで引き切ったらそのまま active へ流す。
      { phase: 'telegraph', at: 0.995, image: 'overhead_raise', ease: 'easeIn',
        offset: { y: -12 }, rot: -8, scale: { x: 0.97, y: 1.075 } },
      { phase: 'active', at: 0.0, image: 'downswing_smear', hold: true, ease: 'easeIn',
        offset: { y: -12 }, rot: -8, scale: { x: 0.97, y: 1.075 } },
      { phase: 'active', at: 0.35, image: 'overhead_contact', hold: true, tag: 'contact',
        offset: { y: 6 }, scale: { x: 1.05, y: 0.93 } },   // 着弾の潰れ
      { phase: 'active', at: 0.75, image: 'overhead_contact', hold: true, ease: 'easeOut' },
      { phase: 'recovery', at: 0.0, image: 'overhead_recover', hold: true },
    ],
    // 斬り上げ。刃を地面に擦らせて引き切り、下から上へ掬い上げる。
    // 🔴 2026-08-26 廃止: ~~待ち落とし（七分引き）＝弓のように引き絞って上から落とす~~。
    //   振り下ろし（exec_maulDrop）と同じ絵を共有していて**技が見分けられなかった**のが理由。
    // 🔴 2026-08-26 廃止: ~~3技の引き絞り量 -18 / -30 / -46~~（縦振り前提の仮値）。
    // 🔴 2026-08-26 廃止: ~~入り口 = set_place（膝をつく）~~ → **set_backstep**。
    //   膝をつく姿勢は「地割り」の合図と紛らわしく、次のコマで立っていて動きが切れていた（ユーザー判定）。
    //   ⚠️ set_place の絵そのものは変形（morph）の入りで使うので消さない（src/sprites.ts）。
    // 🔴 振り切りの絵は作らない = 接触の後に繋ぐと壊れる（ユーザー判定）。硬直は scoop_smear を据え置く。
    //   slash_followthrough を流用しないこと（横薙ぎ用で軌道が横）。
    // 正本 = docs/candidates/2026-08-26-executioner-parry-three-motions-design.md §3b
    //   （旧 = docs/candidates/2026-08-06-executioner-aim-charge-redesign.md）
    // 🔵 2026-09-02: 5キー化の候補へ差し替え (上の EXEC_MAULHOLD_TIMELINE_VARIANTS)。08-26 版は 'old' として退避。
    //   🔵 2026-09-02 夜 ユーザー裁定 = **a3 を採用** (振り抜きの絵へ早く切り替え + 勢いを残す + 頭上の弧 FX)。
    //   🔴 廃止 (2026-09-02): ~~振り切りの絵は作らない = 接触の後に繋ぐと壊れる (08-26)~~ → 振り抜きの絵 scoop_through を
    //     硬直の頭に置く形で採用された。壊れたのは「絵だけ差して体を止めた」からで、勢いと弧で繋げば成立する。
    //   正本 = docs/candidates/2026-09-01-executioner-drag-scoop-motion-spec.md §5f
    exec_maulHold: EXEC_MAULHOLD_TIMELINE_VARIANTS.a3,

    // --- 骨格B: 腰を落として溜める ---
    exec_maulQuake: [
      { phase: 'telegraph', at: 0.0, image: 'idle', hold: true },
      { phase: 'telegraph', at: 0.15, image: 'idle' },
      // 沈む = 骨格Bの合図。骨格A (持ち上がる) と上下が逆 = シルエットの前に「向き」で読める
      { phase: 'telegraph', at: 0.2, image: 'slam_windup', hold: true, tag: 'anticipation',
        ease: 'easeOut', offset: { y: 5 } },
      { phase: 'telegraph', at: 0.6, image: 'slam_hold', hold: true, ease: 'easeInOut', offset: { y: 9 } },
      { phase: 'active', at: 0.0, image: 'slam_windup', hold: true, ease: 'easeIn', offset: { y: 9 } },
      // 🔵 2026-09-02 深夜 当たりだけ突き立ての絵へ（溜めの絵は据え置き＝沈む合図は保つ）
      { phase: 'active', at: 0.35, image: 'quake_contact', hold: true, tag: 'contact',
        offset: { y: 10 }, scale: { x: 1.08, y: 0.9 } },   // 地面へ打ち込む = 一番潰れる
      { phase: 'active', at: 0.8, image: 'quake_contact', hold: true, ease: 'easeOut' },
      { phase: 'recovery', at: 0.0, image: 'slam_hold', hold: true },
      { phase: 'recovery', at: 0.55, image: 'overhead_recover', hold: true },
    ],
    // 発勁の突き — 作り直し (2026-09-09)。地割りの直後だけ繋がる専用技 (src/bosses.ts の
    //   string 'exec-maul-quake-fajin')。既存絵の使い回し (§5): 静止に近い構え (step_crouch) →
    //   引き絞り (fajin_draw) → 突き出し (fajin_thrust)。2026-09-11 プリビズから専用絵を生成。
    //   逐語 (2026-09-09)＝「突きをした後は普通に他の技同様 介錯は戦闘に戻る。それに関わらず
    //   衝撃波が飛んでくる」。突き出しは他の技と同じく active (contact) で出し、そこから
    //   recovery→idle へ普通に戻る。「一拍」はここ (timeline) には無い＝
    //   castProjectile.launchDelayMs (波が刃先に留まる時間) が担う。
    //   🔴 旧「突き出しを telegraph 内で終わらせ active/recovery で静止する」形は却下
    //   (commit 1114768b)＝ボスが待つ作りが逆だった。
    //   正本 = docs/handoffs/2026-09-07-executioner-fajin-thrust-motion-spec.md §5
    // 2026-09-12: 膝・腰で荷重を受ける専用絵を追加。旧版はラボで比較できる。
    // 同日追加: 戻り300msで突き切り→刃を下ろす→idle。接触120msと波の時計は維持。
    // 同日ユーザーが縦振りを採用。専用6絵と緩急へ更新し、技全体と波の時計を保持。
    exec_bladeFajin: FAJIN_MOTION_TIMELINES.sparse,
    exec_cannonFajin: CANNON_FAJIN_TIMELINE,

    // --- 骨格C: 踏み込んで伸ばす ---
    // 🔴 2026-09-05 ユーザー指示で 'old' へ差し戻した。理由 = 専用絵 (pierce_draw / pierce_thrust) が
    //   絵として壊れている ("腕が3本あるように見える"・腕の左右)。09-04 の腕の直し (e218b02e) は
    //   pierce_draw a/b の2枚にしか当たっておらず、pierce_thrust a/b は一度も直していなかった。
    //   絵とタイムライン候補は消さずに残す (常時ルール4)。作り直しの判断が出るまで既定は 'old'。
    //   2026-09-11: 'old' シーケンスの shove_contact を候補Aへ更新し、刃の細身化に対処。
    //      溜めの膝上げは変更していない。thrust の腕の問題もこの更新では扱わない。
    //   経緯 = docs/current-state.md の介錯の節 / 発注カード docs/departments/art/the-executioner-appearance-now.md
    // 🔵 (旧) 2026-09-04: 上の2点を理由に 'thrust' を既定にしていた。
    // ✅ 2026-09-22 ユーザー「既定をこの絵に切り替えて」＝ A-1 踏み込み突き (jab_coil → jab_lunge) を既定へ。
    //   旧 old は ?jabArt=old で残す。砲形態では絵が無いので sprites.ts resolveBossTimelinePose が old へ落とす。
    exec_maulJab: EXEC_JAB_TIMELINE_VARIANTS.lunge,

    // ===== 新技3本 (2026-07-30) — 既存の骨格へ相乗り =====
    // 票 = docs/candidates/2026-07-30-executioner-three-new-attacks-ticket.md
    // 相乗り先と**同じ骨格の語彙**を使う (でないと「入りで読む」が崩れる)。
    // ⚠️ 刃は `public/sprites/bosses/the-executioner/blade/` に本番の絵がある。
    //    そこに無いキー (massive_windup / bigcharge_raise 等) を混ぜると平置きの旧デザインへ
    //    フォールバックし、**攻撃の途中で1枚だけ別人の絵が出る**。据えの一打は blade/ に在る
    //    キーだけで組んでいる (set_place / set_backstep / overhead_raise / overhead_repull /
    //    downswing_smear / overhead_contact / overhead_recover)。新2キーは当て絵ゲート中につき blade 限定。
    //    枷と車輪はバリアントの絵が1枚も無く全部フォールバックなので、この制約は今は効かない。

    // 🔴 2026-08-26 廃止: 骨格A 相乗りの縦振り（満月）。弓と同じく、重く当てるほど体ごと深く引く旧前提。
    // 🔴 2026-08-26 廃止: 引き切って止まったら「離れ」。3技の -18 / -30 / -46 は 🟨ラボ判定前の仮値だった。
    // 🔴 2026-08-26 廃止: 旧「深紫オーラ＋頂点の微振動」は却下済み。理由は外付けせず体そのものに出す旧設計。
    //   正本 = docs/candidates/2026-08-26-executioner-parry-three-motions-design.md §3c
    exec_maulSet: [
      { phase: 'telegraph', at: 0.0, image: 'idle', hold: true },
      // 🔴 2026-08-26 廃止: ~~0.10 と 0.35 で exec_hold（据える＝狙いを定める）~~。
      //   exec_hold は**刃を相手へ真っ直ぐ突き付けている絵**なので、そのあと巻きで引くと
      //   「突こうとしてやめた」＝1つの技の中に振りが2回あるように読めた
      //   （ユーザー実機「上に武器を掲げて、手前に引いて、溜めて、突き出したと思ったら振り払う」）。
      //   → 巻きを1本の動きにするため、頭は idle のまま sweep_coil へ入る。拍は1msも変えていない。
      { phase: 'telegraph', at: 0.10, image: 'sweep_coil', hold: true, tag: 'anticipation',
        ease: 'easeOut', offset: { x: 6, y: 0 } },
      { phase: 'telegraph', at: 0.35, image: 'sweep_coil', hold: true,
        ease: 'easeInOut', offset: { x: 10 }, rot: 2 },
      // 0.50 以降 = 巻きの深まり。offset / rot / scale / ease / at は1つも変えていない。
      { phase: 'telegraph', at: 0.50, image: 'sweep_coil', hold: true, ease: 'easeIn',
        offset: { x: -10 }, rot: -2, scale: { x: 0.99, y: 1.0 } },
      { phase: 'telegraph', at: 0.68, image: 'sweep_coil', ease: 'easeIn',
        offset: { x: -18 }, rot: -3, scale: { x: 0.97, y: 1.0 } },
      { phase: 'telegraph', at: 0.83, image: 'sweep_coil', ease: 'easeIn',
        offset: { x: -24 }, rot: -4, scale: { x: 0.96, y: 1.01 } },
      { phase: 'telegraph', at: 0.93, image: 'sweep_coil', ease: 'easeIn',
        offset: { x: -26 }, rot: -4, scale: { x: 0.955, y: 1.01 } },
      // 同じ値を重ね、約200msの完全静止を「離れ」の合図にする。
      { phase: 'telegraph', at: 0.995, image: 'sweep_coil', ease: 'easeIn',
        offset: { x: -26 }, rot: -4, scale: { x: 0.955, y: 1.01 } },
      // 🔵 2026-08-28 ユーザー裁定「パリィ可能な3連斬りにして」= 単発 active → 3段 (hitIndex 0/1/2)。
      //   telegraph は上まで1msも変えていない。正本 = docs/candidates/2026-08-28-executioner-lunge-triple-slash.md §2。
      //   絵の割り当て: 段0=horizontal_slash(横一閃) / 段1=scoop_smear(返しの斬り上げ) /
      //   段2=overhead_contact(締めの振り下ろし)。
      //   🔴 票 §2 は段2を exec_contact としていたが、exec_contact は執行 (致命) 専用の絵で混同を避けるため
      //   overhead_contact (振り下ろしの打点・実績あり) へ変更した (このワーカーの判断・2026-08-28/29)。
      //   段間 (comboGap 360ms) は sweep_coil (巻き直し・段0→1) → overhead_raise (振りかぶり・段1→2)。
      //   🔴 horizontal_slash / slash_followthrough は他技 (exec_yokeSweep 等) も読むため残す注意は不変。

      // ===== 段0 (hitIndex 0): 横一閃 =====
      { phase: 'active', at: 0.0, image: 'sweep_coil', hold: true, hitIndex: 0, ease: 'easeIn',
        offset: { x: -26 }, rot: -4 },
      { phase: 'active', at: 0.40, image: 'horizontal_slash', hold: true, tag: 'contact', hitIndex: 0,
        offset: { x: 12, y: 0 }, scale: { x: 1.04, y: 0.97 } },
      { phase: 'active', at: 0.85, image: 'horizontal_slash', hold: true, ease: 'easeOut', hitIndex: 0,
        offset: { x: 18 } },
      // 段0→1 の繋ぎ = sweep_coil の巻き直し。絵は即座に切り替え (hold) つつ、offset だけ滑らかに引き戻す。
      { phase: 'comboGap', at: 0.0, image: 'sweep_coil', hold: true, hitIndex: 0,
        offset: { x: 18 } },
      { phase: 'comboGap', at: 0.7, image: 'sweep_coil', hold: true, hitIndex: 0, ease: 'easeIn',
        offset: { x: -20 }, rot: -3 },

      // ===== 段1 (hitIndex 1): 返しの斬り上げ =====
      { phase: 'active', at: 0.0, image: 'sweep_coil', hold: true, hitIndex: 1, ease: 'easeIn',
        offset: { x: -20, y: 6 }, rot: -3 },
      { phase: 'active', at: 0.40, image: 'scoop_smear', hold: true, tag: 'contact', hitIndex: 1,
        ease: 'easeOut', offset: { x: 14, y: -10 }, rot: 6 },
      { phase: 'active', at: 0.85, image: 'scoop_smear', hold: true, ease: 'easeOut', hitIndex: 1,
        offset: { x: 20, y: -12 }, rot: 6 },
      // 段1→2 の繋ぎ = overhead_raise の振りかぶり。斬り上げの勢いのまま振り上げへ繋ぐ。
      { phase: 'comboGap', at: 0.0, image: 'scoop_smear', hold: true, hitIndex: 1,
        offset: { x: 20, y: -12 }, rot: 6 },
      { phase: 'comboGap', at: 0.7, image: 'overhead_raise', hold: true, hitIndex: 1, ease: 'easeIn',
        offset: { y: -14 }, rot: 2 },

      // ===== 段2 (hitIndex 2): 締めの振り下ろし =====
      { phase: 'active', at: 0.0, image: 'overhead_raise', hold: true, hitIndex: 2, ease: 'easeIn',
        offset: { y: -14 }, rot: 2 },
      // 🔵 2026-09-02 見た目の棚卸し (docs/reports/2026-09-02-executioner-visual-audit.md §C2):
      //   offset.y +10〜+12 は足元の -12px 沈みに足し込まれ、実舞台で靴が床の線より 24px 下 (めり込み) に見えた。
      //   振り下ろしの「着弾の潰れ」と同じ +6 を上限にする。~~y: 10 / 12 / 12 / 6~~ → 5 / 6 / 6 / 3。
      { phase: 'active', at: 0.35, image: 'overhead_contact', hold: true, tag: 'contact', hitIndex: 2,
        ease: 'easeOut', offset: { x: 10, y: 5 }, rot: -4 },
      { phase: 'active', at: 0.85, image: 'overhead_contact', hold: true, ease: 'easeOut', hitIndex: 2,
        offset: { x: 14, y: 6 } },

      // 🔵 2026-08-30 検収気づき: 振り下ろし(段2)の後に slash_followthrough(横の振り抜き)が出て
      //   『当たった後にもう一振り』に読めた → 打点の絵のまま余韻→idle。slash_followthrough は
      //   他技 (exec_yokeSweep 等) が読むので絵自体は残す。
      //   出所 = docs/playtest/packs/2026-08-28-executioner-branch-art-fx-pack.md 第2回結果
      { phase: 'recovery', at: 0.0, image: 'overhead_contact', hold: true, offset: { x: 14, y: 6 } },
      { phase: 'recovery', at: 0.45, image: 'overhead_contact', hold: true, ease: 'easeOut',
        offset: { x: 10, y: 3 } },
      { phase: 'recovery', at: 0.75, image: 'idle', ease: 'easeOut' },
    ],

    // ひねり薙ぎの近距離派生 = 赤い突き (2026-08-27 夜)。巻き (sweep_coil) の姿勢を親技の末尾
    //   (offset x:-26, rot:-4) から引き継ぎ、さらに一段引き絞ってから sweep_slash で突き出す。
    //   sweep_slash = 刃を前へ突き出した絵。親技の横薙ぎでは「突きに見える」と誤読された絵を、
    //   本物の突きとしてここで使う (絵と中身の食い違い解消・ユーザー発案)。
    // 🔵 2026-09-04 夜 ユーザー裁定「据えの突きは元のやつで問題ない」= この流用版 (EXEC_PIERCE_TIMELINE_VARIANTS.old) を既定にしていた。
    //   専用絵 (pierce_draw/pierce_thrust) は 突き当て (exec_maulJab) へ回した。候補 a/b は比較用に残す
    //   (?braceThrust=old&pierceArt=a|b / __setPierceArt)。
    // 🔵 2026-10-09 ユーザー採用「v3（溜め600）で本番へ」= 既定は v3 (jab_coil → jab_lunge)。旧の流用版は DEV ?braceThrust=old。
    //   切り替わった tick に src/exec_brace_thrust.ts の onBraceThrustSwitch が置き場所を組み直す。ここは切り替わる前の初期値。
    exec_maulSetPierce: braceThrustV3Timeline(BRACE_THRUST_DEFAULT_START),

    // 腕砲3本は砲口の光の脈と合わせ、体の予備動作も分ける。直射・速射は専用の構え／反動絵を使う。
    exec_cannonShot: [
      { phase: 'telegraph', at: 0.0, image: 'idle', hold: true },
      { phase: 'telegraph', at: 0.18, image: 'aim_hold', hold: true, tag: 'anticipation',
        ease: 'easeOut', offset: { y: 2 } },
      // 直射は発射まで一方向に沈み続ける。一度の大きな圧縮で単発を宣言する。
      { phase: 'telegraph', at: 0.72, image: 'aim_hold', hold: true, ease: 'easeOut',
        offset: { y: 14 }, scale: { x: 1.04, y: 0.94 } },
      { phase: 'telegraph', at: 0.98, image: 'aim_hold', hold: true,
        offset: { y: 14 }, scale: { x: 1.04, y: 0.94 } },
      { phase: 'active', at: 0.0, image: 'fire_recoil', hold: true, tag: 'contact',
        ease: 'easeOut', offset: { x: 10, y: 10 }, scale: { x: 1.04, y: 0.96 } },
      { phase: 'recovery', at: 0.0, image: 'overhead_recover', tag: 'followThrough' },
    ],
    exec_cannonVolley: [
      { phase: 'telegraph', at: 0.0, image: 'idle', hold: true },
      { phase: 'telegraph', at: 0.14, image: 'aim_hold', hold: true, tag: 'anticipation',
        ease: 'easeOut', offset: { y: 6 } },
      // 速射は沈めず、砲身を支える体を前後2pxだけ細かく震わせる。
      { phase: 'telegraph', at: 0.28, image: 'aim_hold', hold: true, offset: { x: -2, y: 6 } },
      { phase: 'telegraph', at: 0.40, image: 'aim_hold', hold: true, offset: { x: 2, y: 6 } },
      { phase: 'telegraph', at: 0.52, image: 'aim_hold', hold: true, offset: { x: -2, y: 6 } },
      { phase: 'telegraph', at: 0.64, image: 'aim_hold', hold: true, offset: { x: 2, y: 6 } },
      { phase: 'telegraph', at: 0.76, image: 'aim_hold', hold: true, offset: { x: -2, y: 6 } },
      { phase: 'telegraph', at: 0.88, image: 'aim_hold', hold: true, offset: { x: 2, y: 6 } },
      { phase: 'telegraph', at: 0.98, image: 'aim_hold', hold: true, offset: { y: 6 } },
      // 🔵 2026-09-03 第4便: 反動は「撃った瞬間に即・大きく、0.3 秒で戻る」。間 (comboGap) は
      //   ease 'step' で変位を止め、撃つ前に傾かない (旧: comboGap 末尾が次段の反動へゆっくり
      //   補間され、撃つ前から後ろへ傾いていた＝「わざとのけぞっている」ように見えた)。
      { phase: 'active', at: 0.0, image: 'fire_recoil', hold: true, tag: 'contact', hitIndex: 0,
        ease: 'easeOut', offset: { x: -10, y: 8 }, rot: -3, scale: { x: 1.02, y: 0.98 } },
      { phase: 'comboGap', at: 0.0, image: 'fire_recoil', hold: true, hitIndex: 0,
        ease: 'easeOut', offset: { x: -9, y: 8 }, rot: -3 },
      { phase: 'comboGap', at: 0.45, image: 'aim_hold', hold: true, hitIndex: 0, ease: 'step', offset: { x: -2, y: 8 } },
      { phase: 'active', at: 0.0, image: 'fire_recoil', hold: true, tag: 'contact', hitIndex: 1,
        ease: 'easeOut', offset: { x: -14, y: 8 }, rot: -4, scale: { x: 1.03, y: 0.97 } },
      { phase: 'comboGap', at: 0.0, image: 'fire_recoil', hold: true, hitIndex: 1,
        ease: 'easeOut', offset: { x: -13, y: 8 }, rot: -4 },
      { phase: 'comboGap', at: 0.35, image: 'aim_hold', hold: true, hitIndex: 1, ease: 'step', offset: { x: -3, y: 8 } },
      { phase: 'active', at: 0.0, image: 'fire_recoil', hold: true, tag: 'contact', hitIndex: 2,
        ease: 'easeOut', offset: { x: -30, y: 12 }, rot: -9, scale: { x: 1.05, y: 0.95 } },
      { phase: 'comboGap', at: 0.0, image: 'fire_recoil', hold: true, hitIndex: 2,
        offset: { x: -28, y: 12 }, rot: -8 },
      // 🔵 2026-09-03 第5便 B-3: 撃ち終わりは volley_after (のけぞり立ち絵) を使わず、
      //   撃った絵 fire_recoil の傾きが戻ってから構え aim_hold に落ち着く。volley_after の絵は残す (未使用)。
      { phase: 'recovery', at: 0.0, image: 'fire_recoil', hold: true, tag: 'followThrough',
        ease: 'easeOut', offset: { x: -22, y: 10 }, rot: -6 },
      { phase: 'recovery', at: 0.3, image: 'fire_recoil', hold: true, ease: 'easeOut', offset: { x: -5, y: 4 }, rot: -1 },
      { phase: 'recovery', at: 0.55, image: 'aim_hold', hold: true, ease: 'step', offset: { x: -2 } },
    ],
    exec_cannonMortar: [
      { phase: 'telegraph', at: 0.0, image: 'idle', hold: true },
      // 砲身を斜め上へ向けた専用の絵 (mortar_aim) で、横へ広がって深く踏ん張る。
      { phase: 'telegraph', at: 0.15, image: 'mortar_aim', hold: true, tag: 'anticipation',
        ease: 'easeOut', offset: { y: 6 }, scale: { x: 1.02, y: 0.98 } },
      // 🔵 2026-09-03 第4便: 撃つ絵は弾の出発 (0.64 = CANNON_MORTAR_DEPARTURE_MS 450 / chargeMs 700)。
      //   上へ撃った反動で少し後傾し、すぐ戻る。
      { phase: 'telegraph', at: 0.64, image: 'mortar_fire', hold: true, ease: 'easeOut',
        offset: { x: -6, y: 12 }, scale: { x: 1.05, y: 0.95 }, rot: -4 },
      { phase: 'active', at: 0.0, image: 'mortar_fire', hold: true, tag: 'contact',
        offset: { x: -3, y: 10 }, scale: { x: 1.03, y: 0.97 }, rot: -2 },
      // 🔴 2026-09-03 第4便 廃止: ~~active＝着弾まで構えのまま踏ん張る・recovery は overhead_recover~~
      //   → 技は撃ったら終わり、弾は独立して落ちる。
      { phase: 'recovery', at: 0.0, image: 'mortar_aim', hold: true, ease: 'easeOut', offset: { y: 6 } },
      { phase: 'recovery', at: 0.6, image: 'idle', hold: true },
    ],
    // 🔵 2026-09-03 第8便: 構えの本番絵 sweep_windup (候補 low_a/low_b/twist_a/twist_b・`?sweepWindup=`)。mortar は比較用に残す。
    //   定義本体 = 上の EXEC_CANNONSWEEP_TIMELINE_VARIANTS。既定 = art (低さ低め案b)。
    exec_cannonSweep: EXEC_CANNONSWEEP_TIMELINE_VARIANTS.art,
    // G11 執行の咆哮→突進斬り: 咆哮の構え (roar_pose) → 踏み切りの沈み (kick_windup)
    //   → 突進 (dash_travel) → 横一閃 (horizontal_slash) → 振り抜き (slash_followthrough)。
    //
    // ★2026-08-20 是正 (出所 = docs/candidates/2026-08-20-executioner-roar-dash-resync-ticket.md):
    //   旧配線は**絵と実際の動きが入れ替わっていた**。
    //     ・dash_travel (走る絵) が telegraph 0.68 = **その場に立っている 736ms** に出ていた
    //     ・実際の 300px 突進は active に入ってから動く (src/boss.ts:525 の lungeDistance 分岐) ので、
    //       **走っている 200ms のあいだ horizontal_slash (斬る絵) が出ていた**
    //     ・contact タグが active 0.0 = contactDelayMs 0ms なので、**突進の開始と同時に当たっていた**
    //   → ユーザー実機 2026-08-20「刃を突き刺すように突進した後、刃を振っている。
    //     突進してるタイミングで攻撃判定があったように思った」= そのとおりだった。
    //   ⚠️ 判定が開く瞬間が active 0.0 → 0.45 へ動く = **当たる時間が 200ms → 110ms へ短くなる**
    //     (突進の入りは避けられるようになる)。ユーザー裁定済みの意図した変更。
    //   ⚠️ 秒数・踏み込み距離・strike・咆哮3波は不変 = 描画の並びと contact 位置だけ。
    executioner_phaseTransition: [
      { phase: 'telegraph', at: 0.0, image: 'idle', hold: true },
      { phase: 'telegraph', at: 0.085, image: 'idle' },
      { phase: 'telegraph', at: 0.13, image: 'roar_pose', hold: true, tag: 'anticipation' },
      { phase: 'telegraph', at: 0.63, image: 'roar_pose' },
      // 🔵2026-08-20: ここは dash_travel (走る絵) だった。立ち止まっている区間なので構えのまま保つ。
      { phase: 'telegraph', at: 0.68, image: 'roar_pose', hold: true },
      // 踏み切り: 波3 (1800ms) の後の「トラップ間」の終わりで沈む = 来るぞ、の合図。
      { phase: 'telegraph', at: 0.91, image: 'kick_windup', hold: true },
      // ★ここから実際に 300px 走る。走っている間は走る絵。
      { phase: 'active', at: 0.0, image: 'dash_travel', hold: true },
      // ★斬る絵が出る瞬間に当たる (contact)。突進の後半を斬りながら詰める。
      { phase: 'active', at: 0.45, image: 'horizontal_slash', hold: true, tag: 'contact' },
      { phase: 'active', at: 0.8, image: 'slash_followthrough', hold: true },
      { phase: 'recovery', at: 0.0, image: 'slash_followthrough', hold: true },
      { phase: 'recovery', at: 0.55, image: 'idle', hold: true },
    ],
    // 致命 執行 (bossFinisher・プレイヤー体幹崩し時): バッチ3の専用3拍。
    //   ⚠️検収 2026-07-06 発見: この entry が無いと generic state 絵 (telegraph/active=処刑人では退役済) へ
    //   落ち、壊れ画像の drawImage で致命中ボスが描画不能になる。処刑人の技は必ずタイムラインを持たせる
    //   (機械ガード = tests/executioner_sprite_wiring.test.ts)。
    //   charge/active/recovery の長さと contact tag=active 35% は既存契約を維持し、画像だけを差し替える。
    //
    //   ★2026-07-20 見た目検収の是正 (正本=docs/reports/2026-07-20-executioner-batch3-visual-review.md):
    //     旧配線は telegraph 1キー (0.0 で既に掲げ切り) → active 0.0 で既に接地 = 「上から下へ瞬間移動」。
    //     600ms掲げ→900ms保持が実質「静止画2枚の入れ替え」で、振り下ろす絵が1枚も無かった。
    //     設計正本 §8.3 の騎士パリティ「telegraph 2段以上」「弧を描く技はスメア必須」を致命だけ未達
    //     (A1/C6 等 他の縦振りは §8.5c 適用済)。→ A1 と同じ文法へ揃える:
    //       telegraph = overhead_raise (持ち上げる過程) → exec_windup (掲げ切り) の2段
    //       active    = downswing_smear (振ってる最中に判定) → 0.35 で exec_contact (打点)
    //     ⚠️秒数・判定・contact tag 位置は一切変えていない = 描画配線のみ (戦闘コア凍結に抵触しない)。
    //     絵は全て既存 (downswing_smear/overhead_raise は A1・C6 と共有 §8.4) = 再発注ゼロ。
    executioner_finisher: [
      // ★2026-08-19 改修 (出所 = docs/reports/2026-08-19-executioner-exec-motion-diagnosis.md /
      //   票 = docs/candidates/2026-08-19-executioner-finisher-motion-rewrite-ticket.md):
      //   旧配線は手続きチャンネルが9キー全部で0本 = 絵の入替だけで進み、実測で
      //     ①振り下ろしの弧 103° が repull→smear の1カットに丸ごと省略されていた
      //     ②smear と contact の刃の角度差 0.2° = 同じ構えが2回出ていた
      //     ③姿勢差の大きい αディゾルブ2本が幽霊絵 (2姿勢が半透明で重なる) になっていた
      //   → ユーザー実機 2026-08-19「何をしているか判らず、ただ絵が切り替わっていく」。
      //   ⚠️ 秒数・判定・contact/anticipation tag の位置は不変 = 描画のみ。
      //   ディゾルブは全廃 (全キー hold:true)。動きは offset/rot/scale で作る (文法の見本 = exec_maulSet)。
      //
      // ★2026-08-20 是正 (出所 = docs/candidates/2026-08-20-executioner-finisher-6keys-ticket.md /
      //   docs/handoffs/2026-08-20-morning-handoff.md §6):
      //   上の 08-19 改修は**ユーザー実機で逆効果**と判明した。逐語 =「何してんのかあんまよくわかんない、
      //   なんかはしゃいでるようにすら見える」「重力の方向みたいなのが正しくないような感じ」
      //   「途中で武器が折れたような絵が挟まってる」。測って出た原因は2つ:
      //     ①絵の種類が多すぎた = 致命で使う絵は 騎士3 / 花嫁4 / 覚醒王4 / 王5 に対し **介錯8**。
      //       → **8種→6種**、さらに同日の実機で **6種→4種** へ。外した4枚と理由:
      //         ・exec_windup (掲げ切り) = 溜めが raise→windup→repull と3枚続き「何をしているか判らない」
      //         ・downswing_trail (軌跡つき) = 軌跡を刃と同じ太さで描いたため武器が三日月に曲がって見える
      //         ・overhead_repull (振りかぶり) = **刃が柄の付け根で直角に折れて見える**。生成も繰り返し失敗。
      //           →この絵は介錯の**全技から全廃**した (振り下ろし/待ち落とし/据えの一打/介錯を請う/致命)。
      //         ・downswing_mid (弧の中割り) = 「絵として完成度も低いし動きとしても不要」(ユーザー実機)
      //       🔴 画像ファイル・POSE_SCALE_FIX の項目・types.ts の型は残す(常時ルール4)。配線を外しただけ。
      //     ②全身の傾きが桁違いだった = rot は足元ピボットの全身傾き (このファイル先頭の PoseKey 定義)。
      //       騎士 knightExecution / 王 houndExecution は **rot ゼロ**、覚醒王でも **±1.5°**。
      //       介錯だけ -14°→+13° (計27°) = 頭が横に100px以上振れていた。→ **計12°** (-6°→+6°) へ。
      //       上下の offset も 34px → 20px。ゼロにはしない (絵が6種に減るぶん、繋ぎを隠す気配は要る)。
      //   ⚠️ 秒数・判定・contact/anticipation tag の位置は 08-19 と同じく不変 = 描画のみ。
      // 溜め: わずかに沈んでから持ち上がる (idle からのハードカットを動きで隠す)
      { phase: 'telegraph', at: 0.0, image: 'overhead_raise', hold: true,
        ease: 'easeOut', offset: { x: 0, y: 6 }, rot: 2, scale: { x: 1, y: 1 } },
      { phase: 'telegraph', at: 0.14, image: 'overhead_raise', hold: true,
        ease: 'easeIn', offset: { x: 0, y: 0 }, rot: 0, scale: { x: 1, y: 1 } },
      // 掲げていく過程: 同じ絵を3キー目まで引っぱり、offset/rot だけで伸び上がりを作る
      //   (🔵2026-08-20: ここは exec_windup = 掲げ切りの絵だった。溜めの絵を1枚減らした代役)
      { phase: 'telegraph', at: 0.42, image: 'overhead_raise', hold: true,
        ease: 'easeIn', offset: { x: -2, y: -8 }, rot: -3, scale: { x: 1, y: 1 } },
      // ★振りかぶり: これが無いと「掲げ切り→接地」= 上に伸びるだけで、振り下ろしに見えない
      //   (ユーザー実機 2026-07-20「振り下ろしてるというより上に伸びてるだけ」)。
      //   🔴 2026-08-20: ~~overhead_repull (穂先が頭より後ろへ落ち、体が反る絵)~~ は**全廃**。
      //     ユーザー実機「この絵は武器が途中で折れているし、ずっと生成も失敗しているからそもそもなくそう」
      //     = 刃が柄の付け根で直角に折れて見える。反りは overhead_raise を保ったまま
      //     offset/rot/scale で作る (2026-08-19 以降の文法)。詳細 = このファイル冒頭の 2026-08-20 の節。
      { phase: 'telegraph', at: 0.7, image: 'overhead_raise', hold: true, tag: 'anticipation',
        ease: 'easeIn', offset: { x: -5, y: -14 }, rot: -6, scale: { x: 0.99, y: 1.02 } },
      // 🔴 2026-08-19 に足した弧の中割り ~~downswing_mid~~ は 2026-08-20 に**撤去**。
      //   ユーザー実機「この2つ (overhead_repull / downswing_mid) は絵として完成度も低いし
      //   動きとしても不要な絵に見えるから取り除いて」。
      //   ⚠️ 08-19 の「コードの回転25°では弧103°は作れない」は測定として正しいままだが、
      //     **弧を絵で足すより、姿勢の絵を1枚に絞るほうが読める**というのが実機の答え。
      //     致命で使う絵 = 騎士3 / 花嫁4 / 覚醒王4 / 王5 に対し、介錯もこれで **4種**。
      //   最後の溜め: 同じ絵のまま、いちばん深く反った所で 96ms 保つ (「離れ」の合図)。
      { phase: 'telegraph', at: 0.86, image: 'overhead_raise', hold: true,
        ease: 'easeIn', offset: { x: -5, y: -14 }, rot: -6, scale: { x: 0.99, y: 1.02 } },
      // 振り下ろし: smear を2キーに置き、rot を -4→+4 へ振って弧の入りを作る (絵は増やさない)
      { phase: 'active', at: 0.0, image: 'downswing_smear', hold: true,
        ease: 'easeIn', offset: { x: 0, y: -2 }, rot: 0, scale: { x: 0.99, y: 1.02 } },
      { phase: 'active', at: 0.18, image: 'downswing_smear', hold: true,
        ease: 'easeOut', offset: { x: 4, y: -3 }, rot: 4, scale: { x: 1.02, y: 0.98 } },
      // 打点: 潰し (scale) と前への踏み込み (offset) で smear と別物に見せる
      { phase: 'active', at: 0.35, image: 'exec_contact', hold: true, tag: 'contact',
        ease: 'easeOut', offset: { x: 9, y: 0 }, rot: 6, scale: { x: 1.05, y: 0.94 } },
      { phase: 'active', at: 0.6, image: 'exec_contact', hold: true,
        ease: 'easeInOut', offset: { x: 7, y: 0 }, rot: 5, scale: { x: 0.99, y: 1.01 } },
      // 🔴旧 recovery=exec_hold (槌を頭上に掲げたまま静止) は無効。
      // 処刑は仕事なので掲げない。打点に留まり、引きずって立つ (台帳 = docs/decision-ledger.md §常打ちの刑吏 2026-07-31)。
      { phase: 'recovery', at: 0.0, image: 'exec_contact', hold: true,
        ease: 'easeOut', offset: { x: 7, y: 0 }, rot: 5, scale: { x: 1.02, y: 0.98 } },
      { phase: 'recovery', at: 0.18, image: 'exec_contact', hold: true,
        ease: 'easeInOut', offset: { x: 4, y: 0 }, rot: 3, scale: { x: 1, y: 1 } },
      { phase: 'recovery', at: 0.45, image: 'overhead_recover', hold: true,
        ease: 'easeOut', offset: { x: 2, y: 0 }, rot: 1, scale: { x: 1, y: 1 } },
      { phase: 'recovery', at: 0.78, image: 'overhead_recover', hold: true,
        ease: 'easeInOut', offset: { x: 0, y: 0 }, rot: 0, scale: { x: 1, y: 1 } },
    ],
  },

  // 覚醒王 (2026-07-07 Fable5 ビルド)。 絵 = DEVプロキシ (src/awoken_king_proxy.ts が実行時生成) →
  //   最終絵は同名 PNG を public/sprites/bosses/crowned-king-awoken/ へ置き、プロキシフラグ
  //   (awoken_king_proxy.ts の AWOKEN_PROXY_ENABLED) を切ると、このタイムラインのまま差し替わる
  //   (戦闘ロジック不変)。 拍は richness-grammar §1 の6拍表準拠。
  //   ⚠️罠ペア (frenzy/frenzyExtend・rush/rushChase) は telegraph を意図的に同一にする =
  //   入りで見分けさせない。 分岐サインは gap/recovery 側 (extend_coil / chase_coil) が担う。
  'crowned-king-awoken': {
    // 暴噛 (480/140/520): 既定 now は速い弾きの基準点。windup 保持 ~336ms。比較候補は上の表を参照。
    // 回転（10-02 5枚版・見本 spin_mock.py の時間割）: ①wind 0-430 → ②rear 430-500 | ③sweep 500-600（当たり 520）→ ④front 600-670 | ⑤settle 670-1000 → 待機。
    //   at は各 phase（charge 500 / active 170 / recovery 560）の割合。ms で書いて割る（時間を変えたら全部引き直す）。
    feralPocketSpin: [
      { phase: 'telegraph', at: 0, image: 'pocket_spin_wind_1001', hold: true },
      { phase: 'telegraph', at: 430 / 500, image: 'pocket_spin_rear_1002', hold: true },
      { phase: 'active', at: 0, image: 'pocket_spin_sweep_1001', hold: true, tag: 'contact' },
      { phase: 'active', at: 100 / 170, image: 'pocket_spin_front_1002', hold: true },
      { phase: 'recovery', at: 0, image: 'pocket_spin_settle_1002', hold: true },
      { phase: 'recovery', at: 330 / 560, image: 'idle', hold: true },
    ],
    feralBite: AWOKEN_BITE_TIMELINE_VARIANTS.reco,
    // 暴噛・溜め (880/140/560): 二段沈み = 「深く沈んだら一拍遅い」を体で読ませる
    //   (2026-07-07 パックA-5 裁定「構えも変える」。 通常噛みと同じ入り→0.5 で bite_windup_deep へ
    //   沈み直す = 見切った人は事前に予測でき、被弾しない)。 deep の保持 396ms ≥ 読みの床。
    feralBiteDelayed: [
      { phase: 'telegraph', at: 0.0, image: 'idle', hold: true, ease: 'easeIn' },
      // ⚠️ at は chargeMs(1300) の割合。暴噛(480) と **ミリ秒で**揃えるためにこの値になっている
      //   (87/1300 / 150/1300)。chargeMs を変えたら全部引き直すこと。
      { phase: 'telegraph', at: 87 / 1300, image: 'idle',
        offset: { y: 2 }, scale: { x: 1.02, y: 0.97 }, ease: 'easeIn' },
      { phase: 'telegraph', at: 150 / 1300, image: 'bite_windup', hold: true, tag: 'anticipation',
        offset: { x: -3, y: 3 }, scale: { x: 1.03, y: 0.95 }, rot: -0.6, ease: 'easeIn' },
      // 🔴 見分け点 = 300ms。2026-08-10 ユーザー実プレイ指摘の修正。
      //   旧 = 484ms (at 0.55) で沈んでいた = **通常の暴噛が当たる 480ms の4ms後** = 合図が
      //   意思決定の後に来るので予測に使えず、「見分ける方法が無い」状態だった。
      //   弾きに押す時間帯は 280〜480ms (受付窓200msが480msの当たりを覆う範囲) なので、
      //   その入り口 300ms で沈むと「押そうとした指が止まる」。158ms まで早めると
      //   押す気になる前に答えが出て別の技に見える (= リズム外しの面白さが消える)。
      //   実測と経緯 = docs/reports/2026-07-07-awoken-king-fable5-build-state.md 2026-08-10 節。
      //   ⚠️ ここより前 (0/88/158ms) は暴噛と ms で同一に保つこと (tests/awoken_king_kit が担保)。
      { phase: 'telegraph', at: 300 / 1300, image: 'bite_windup_deep', hold: true, tag: 'anticipation',
        offset: { x: -4, y: 6 }, scale: { x: 1.06, y: 0.90 }, rot: -0.4, ease: 'easeInOut' },
      // 沈んだまま 1000ms 持つので、こらえている体を動かし続ける (静止画にしない)。
      { phase: 'telegraph', at: 0.438, image: 'bite_windup_deep', hold: true,
        offset: { x: -6, y: 5 }, scale: { x: 1.06, y: 0.91 }, rot: -0.9, ease: 'easeIn' },
      { phase: 'telegraph', at: 0.610, image: 'bite_windup_deep', hold: true,
        offset: { x: -7, y: 4 }, scale: { x: 1.055, y: 0.92 }, rot: -1.1, ease: 'easeInOut' },
      { phase: 'telegraph', at: 0.781, image: 'bite_windup_deep', hold: true,
        offset: { x: -6, y: 5 }, scale: { x: 1.06, y: 0.91 }, rot: -0.8, ease: 'easeInOut' },
      { phase: 'telegraph', at: 0.914, image: 'bite_windup_deep', hold: true,
        offset: { x: -8, y: 4 }, scale: { x: 1.05, y: 0.92 }, rot: -1.2, ease: 'easeInOut' },
      { phase: 'telegraph', at: 0.965, image: 'bite_windup_deep', hold: true,
        offset: { x: -9, y: 3 }, scale: { x: 1.05, y: 0.93 }, rot: -1.4, ease: 'easeIn' },
      { phase: 'active', at: 0.0, image: 'bite_contact', hold: true, tag: 'contact',
        offset: { x: 8 }, scale: { x: 1.06, y: 0.96 }, rot: 1.4, ease: 'easeOut' },
      { phase: 'active', at: 0.15, image: 'bite_step', hold: true,
        offset: { x: 4 }, scale: { x: 1.02, y: 0.99 }, rot: 0.8, ease: 'easeOut' },
      { phase: 'recovery', at: 0.0, image: 'bite_follow', hold: true, tag: 'followThrough',
        offset: { x: 4 }, rot: 0.8, ease: 'easeOut' },
      { phase: 'recovery', at: 0.25, image: 'bite_follow', hold: true,
        offset: { x: 1 }, rot: 0.2, ease: 'easeInOut' },
      { phase: 'recovery', at: 0.5, image: 'bite_recover', tag: 'recover', ease: 'easeOut' },
    ],
    // 暴連噛 (750 / 3hit gap320 / 850): 構え = frenzy_open (大口)。 2拍目だけ深い噛み (単調回避)。
    feralFrenzy: [
      { phase: 'telegraph', at: 0.0, image: 'idle', hold: true, ease: 'easeIn' },
      { phase: 'telegraph', at: 0.15, image: 'idle',
        offset: { y: 2 }, scale: { x: 1.02, y: 0.97 }, ease: 'easeIn' },
      { phase: 'telegraph', at: 0.25, image: 'frenzy_open', hold: true, tag: 'anticipation',
        offset: { y: 3 }, scale: { x: 1.04, y: 0.95 }, rot: 0.4, ease: 'easeInOut' },
      { phase: 'telegraph', at: 0.50, image: 'frenzy_open', hold: true,
        offset: { y: 0 }, scale: { x: 1.01, y: 1.0 }, rot: 0.1, ease: 'easeInOut' },
      { phase: 'telegraph', at: 0.72, image: 'frenzy_open', hold: true,
        offset: { y: 4 }, scale: { x: 1.05, y: 0.94 }, rot: 0.7, ease: 'easeIn' },
      { phase: 'telegraph', at: 0.90, image: 'frenzy_open', hold: true,
        offset: { x: 2, y: 2 }, scale: { x: 1.05, y: 0.96 }, rot: 1.2, ease: 'easeIn' },
      { phase: 'active', at: 0.0, image: 'bite_contact', hold: true, tag: 'contact', hitIndex: 0,
        offset: { x: 6 }, scale: { x: 1.04, y: 0.97 }, rot: 1.2, ease: 'easeOut' },
      { phase: 'active', at: 0.15, image: 'bite_step', hold: true, hitIndex: 0,
        offset: { x: 3 }, scale: { x: 1.02, y: 0.99 }, rot: 0.7, ease: 'easeOut' },
      { phase: 'active', at: 0.0, image: 'frenzy_bite2', hold: true, tag: 'contact', hitIndex: 1,
        offset: { x: 8 }, scale: { x: 1.05, y: 0.96 }, rot: 1.5, ease: 'easeOut' },
      { phase: 'active', at: 0.15, image: 'bite_step', hold: true, hitIndex: 1,
        offset: { x: 4 }, scale: { x: 1.02, y: 0.99 }, rot: 0.8, ease: 'easeOut' },
      { phase: 'active', at: 0.0, image: 'bite_contact', hold: true, tag: 'contact', hitIndex: 2,
        offset: { x: 6 }, scale: { x: 1.04, y: 0.97 }, rot: 1.2, ease: 'easeOut' },
      { phase: 'active', at: 0.15, image: 'bite_step', hold: true, hitIndex: 2,
        offset: { x: 3 }, scale: { x: 1.02, y: 0.99 }, rot: 0.7, ease: 'easeOut' },
      // gap 0.7 = follow 224ms / open 96ms (山型の谷を作る・アニメレビューP1: 0.6 だと 192/128 で平坦)
      { phase: 'comboGap', at: 0.0, image: 'bite_follow', hold: true,
        offset: { x: 2 }, rot: 0.4, ease: 'easeOut' },
      { phase: 'comboGap', at: 0.7, image: 'frenzy_open', hold: true,
        offset: { y: 1 }, scale: { x: 1.03, y: 0.97 }, rot: 0.8, ease: 'easeIn' },
      { phase: 'recovery', at: 0.0, image: 'bite_follow', hold: true, tag: 'followThrough',
        offset: { x: 3 }, rot: 0.9, ease: 'easeOut' },
      { phase: 'recovery', at: 0.22, image: 'bite_follow', hold: true,
        rot: 0.2, ease: 'easeInOut' },
      { phase: 'recovery', at: 0.5, image: 'bite_recover', tag: 'recover', ease: 'easeInOut' },
      { phase: 'recovery', at: 0.72, image: 'bite_recover', hold: true,
        offset: { y: 1 }, scale: { y: 0.995 }, ease: 'easeInOut' },
    ],
    // 暴連噛・伸び (4hit): 入り〜3拍目まで暴連噛と同一。 3拍目後の gap (hitIndex2・500ms) にだけ
    //   extend_coil (後肢の沈み直し) が出る = 見分けの窓 ~300ms。
    feralFrenzyExtend: [
      { phase: 'telegraph', at: 0.0, image: 'idle', hold: true, ease: 'easeIn' },
      { phase: 'telegraph', at: 0.15, image: 'idle',
        offset: { y: 2 }, scale: { x: 1.02, y: 0.97 }, ease: 'easeIn' },
      { phase: 'telegraph', at: 0.25, image: 'frenzy_open', hold: true, tag: 'anticipation',
        offset: { y: 3 }, scale: { x: 1.04, y: 0.95 }, rot: 0.4, ease: 'easeInOut' },
      { phase: 'telegraph', at: 0.50, image: 'frenzy_open', hold: true,
        offset: { y: 0 }, scale: { x: 1.01, y: 1.0 }, rot: 0.1, ease: 'easeInOut' },
      { phase: 'telegraph', at: 0.72, image: 'frenzy_open', hold: true,
        offset: { y: 4 }, scale: { x: 1.05, y: 0.94 }, rot: 0.7, ease: 'easeIn' },
      { phase: 'telegraph', at: 0.90, image: 'frenzy_open', hold: true,
        offset: { x: 2, y: 2 }, scale: { x: 1.05, y: 0.96 }, rot: 1.2, ease: 'easeIn' },
      { phase: 'active', at: 0.0, image: 'bite_contact', hold: true, tag: 'contact', hitIndex: 0,
        offset: { x: 6 }, scale: { x: 1.04, y: 0.97 }, rot: 1.2, ease: 'easeOut' },
      { phase: 'active', at: 0.15, image: 'bite_step', hold: true, hitIndex: 0,
        offset: { x: 3 }, scale: { x: 1.02, y: 0.99 }, rot: 0.7, ease: 'easeOut' },
      { phase: 'active', at: 0.0, image: 'frenzy_bite2', hold: true, tag: 'contact', hitIndex: 1,
        offset: { x: 8 }, scale: { x: 1.05, y: 0.96 }, rot: 1.5, ease: 'easeOut' },
      { phase: 'active', at: 0.15, image: 'bite_step', hold: true, hitIndex: 1,
        offset: { x: 4 }, scale: { x: 1.02, y: 0.99 }, rot: 0.8, ease: 'easeOut' },
      { phase: 'active', at: 0.0, image: 'bite_contact', hold: true, tag: 'contact', hitIndex: 2,
        offset: { x: 6 }, scale: { x: 1.04, y: 0.97 }, rot: 1.2, ease: 'easeOut' },
      { phase: 'active', at: 0.15, image: 'bite_step', hold: true, hitIndex: 2,
        offset: { x: 3 }, scale: { x: 1.02, y: 0.99 }, rot: 0.7, ease: 'easeOut' },
      { phase: 'active', at: 0.0, image: 'frenzy_bite2', hold: true, tag: 'contact', hitIndex: 3,
        offset: { x: 10 }, scale: { x: 1.07, y: 0.95 }, rot: 1.8, ease: 'easeOut' },
      { phase: 'active', at: 0.15, image: 'bite_step', hold: true, hitIndex: 3,
        offset: { x: 4 }, scale: { x: 1.02, y: 0.99 }, rot: 0.9, ease: 'easeOut' },
      { phase: 'comboGap', at: 0.0, image: 'bite_follow', hold: true, hitIndex: 0,
        offset: { x: 2 }, rot: 0.4, ease: 'easeOut' },
      { phase: 'comboGap', at: 0.7, image: 'frenzy_open', hold: true, hitIndex: 0,
        offset: { y: 1 }, scale: { x: 1.03, y: 0.97 }, rot: 0.8, ease: 'easeIn' },
      { phase: 'comboGap', at: 0.0, image: 'bite_follow', hold: true, hitIndex: 1,
        offset: { x: 2 }, rot: 0.4, ease: 'easeOut' },
      { phase: 'comboGap', at: 0.7, image: 'frenzy_open', hold: true, hitIndex: 1,
        offset: { y: 1 }, scale: { x: 1.03, y: 0.97 }, rot: 0.8, ease: 'easeIn' },
      // ★サインの窓 (gap 500ms): 息継ぎに見せて、後肢が再び沈む。
      { phase: 'comboGap', at: 0.0, image: 'bite_follow', hold: true, hitIndex: 2,
        offset: { x: 2 }, rot: 0.4, ease: 'easeOut' },
      { phase: 'comboGap', at: 0.4, image: 'extend_coil', hold: true, hitIndex: 2,
        offset: { y: 4 }, scale: { x: 1.05, y: 0.93 }, rot: -0.5, ease: 'easeIn' },
      { phase: 'comboGap', at: 0.75, image: 'extend_coil', hold: true, hitIndex: 2,
        offset: { y: 5 }, scale: { x: 1.06, y: 0.92 }, rot: 0.6, ease: 'easeIn' },
      { phase: 'recovery', at: 0.0, image: 'bite_follow', hold: true, tag: 'followThrough',
        offset: { x: 3 }, rot: 0.9, ease: 'easeOut' },
      { phase: 'recovery', at: 0.18, image: 'bite_follow', hold: true,
        rot: 0.2, ease: 'easeInOut' },
      { phase: 'recovery', at: 0.45, image: 'bite_recover', tag: 'recover', ease: 'easeInOut' },
      { phase: 'recovery', at: 0.62, image: 'bite_recover', hold: true,
        offset: { y: 2 }, scale: { y: 0.99 }, ease: 'easeInOut' },
      { phase: 'recovery', at: 0.80, image: 'bite_recover', hold: true, ease: 'easeInOut' },
    ],
    // 暴走突進 (700/260/800): 弓なりの溜め🔴 → 一直線 → 頭が泳ぐ伸び切り = 本命の穴。
    feralRush: [
      { phase: 'telegraph', at: 0.0, image: 'idle', hold: true, ease: 'easeIn' },
      { phase: 'telegraph', at: 0.12, image: 'idle',
        offset: { x: 2 }, rot: 0.3, ease: 'easeOut' },
      { phase: 'telegraph', at: 0.22, image: 'rush_windup', hold: true, tag: 'anticipation',
        offset: { x: -6 }, scale: { x: 0.98, y: 1.01 }, rot: -0.8, ease: 'easeIn' },
      { phase: 'telegraph', at: 0.55, image: 'rush_windup', hold: true,
        offset: { x: -16 }, scale: { x: 0.96, y: 1.02 }, rot: -1.5, ease: 'easeInOut' },
      { phase: 'telegraph', at: 0.70, image: 'rush_windup', hold: true,
        offset: { x: -13, y: -1 }, rot: -1.1, ease: 'easeInOut' },
      { phase: 'telegraph', at: 0.81, image: 'rush_windup', hold: true,
        offset: { x: -17, y: 1 }, rot: -1.5, ease: 'easeInOut' },
      { phase: 'telegraph', at: 0.90, image: 'rush_windup', hold: true,
        offset: { x: -13 }, rot: -1.0, ease: 'easeIn' },
      { phase: 'telegraph', at: 0.97, image: 'rush_windup', hold: true,
        offset: { x: -19 }, scale: { x: 0.95, y: 1.02 }, rot: -1.5, ease: 'easeIn' },
      { phase: 'active', at: 0.0, image: 'rush_lunge', hold: true, tag: 'contact',
        offset: { x: 10 }, scale: { x: 1.06, y: 0.96 }, rot: 1.2, ease: 'easeOut' },
      { phase: 'active', at: 0.5, image: 'rush_lunge', hold: true,
        offset: { x: 4 }, scale: { x: 1.02, y: 0.99 }, rot: 0.6, ease: 'easeOut' },
      { phase: 'recovery', at: 0.0, image: 'rush_overextend', hold: true, tag: 'followThrough',
        offset: { x: 6 }, rot: 1.4, ease: 'easeOut' },
      { phase: 'recovery', at: 0.20, image: 'rush_overextend', hold: true,
        offset: { x: 2 }, rot: 0.2, ease: 'easeInOut' },
      { phase: 'recovery', at: 0.36, image: 'rush_overextend', hold: true,
        offset: { x: 4 }, rot: 0.9, ease: 'easeInOut' },
      { phase: 'recovery', at: 0.50, image: 'rush_overextend', hold: true,
        offset: { x: 2 }, rot: 0.3, ease: 'easeInOut' },
      { phase: 'recovery', at: 0.62, image: 'bite_recover', tag: 'recover', ease: 'easeOut' },
    ],
    // 追い噛み (罠): telegraph/active は突進と同一。 gap (620ms) 前半は伸び切り (偽の穴) →
    //   0.35 で chase_coil (牙が向き直る) = サイン ~400ms → 二の噛み。
    //   ⚠️ feralRush と telegraph キー列を同一に保つこと (罠の前提。 tests/awoken_king_kit が機械担保。
    //   片方だけ hitIndex や at を編集しない — 変えるなら両方セットで)。
    feralRushChase: [
      { phase: 'telegraph', at: 0.0, image: 'idle', hold: true, ease: 'easeIn' },
      { phase: 'telegraph', at: 0.12, image: 'idle',
        offset: { x: 2 }, rot: 0.3, ease: 'easeOut' },
      { phase: 'telegraph', at: 0.22, image: 'rush_windup', hold: true, tag: 'anticipation',
        offset: { x: -6 }, scale: { x: 0.98, y: 1.01 }, rot: -0.8, ease: 'easeIn' },
      { phase: 'telegraph', at: 0.55, image: 'rush_windup', hold: true,
        offset: { x: -16 }, scale: { x: 0.96, y: 1.02 }, rot: -1.5, ease: 'easeInOut' },
      { phase: 'telegraph', at: 0.70, image: 'rush_windup', hold: true,
        offset: { x: -13, y: -1 }, rot: -1.1, ease: 'easeInOut' },
      { phase: 'telegraph', at: 0.81, image: 'rush_windup', hold: true,
        offset: { x: -17, y: 1 }, rot: -1.5, ease: 'easeInOut' },
      { phase: 'telegraph', at: 0.90, image: 'rush_windup', hold: true,
        offset: { x: -13 }, rot: -1.0, ease: 'easeIn' },
      { phase: 'telegraph', at: 0.97, image: 'rush_windup', hold: true,
        offset: { x: -19 }, scale: { x: 0.95, y: 1.02 }, rot: -1.5, ease: 'easeIn' },
      { phase: 'active', at: 0.0, image: 'rush_lunge', hold: true, tag: 'contact', hitIndex: 0,
        offset: { x: 10 }, scale: { x: 1.06, y: 0.96 }, rot: 1.2, ease: 'easeOut' },
      { phase: 'active', at: 0.5, image: 'rush_lunge', hold: true, hitIndex: 0,
        offset: { x: 4 }, scale: { x: 1.02, y: 0.99 }, rot: 0.6, ease: 'easeOut' },
      { phase: 'comboGap', at: 0.0, image: 'rush_overextend', hold: true,
        offset: { x: 6 }, rot: 1.4, ease: 'easeOut' },
      { phase: 'comboGap', at: 0.258, image: 'rush_overextend', hold: true,
        offset: { x: 2 }, rot: 0.2, ease: 'easeInOut' },
      { phase: 'comboGap', at: 0.35, image: 'chase_coil', hold: true,
        offset: { x: -2 }, scale: { x: 1.03, y: 0.98 }, rot: -0.6, ease: 'easeIn' },
      { phase: 'comboGap', at: 0.75, image: 'chase_coil', hold: true,
        offset: { x: -5 }, scale: { x: 1.04, y: 0.97 }, rot: -1.0, ease: 'easeIn' },
      { phase: 'active', at: 0.0, image: 'bite_contact', hold: true, tag: 'contact', hitIndex: 1,
        offset: { x: 8 }, scale: { x: 1.05, y: 0.96 }, rot: 1.5, ease: 'easeOut' },
      { phase: 'active', at: 0.55, image: 'bite_contact', hold: true, hitIndex: 1,
        offset: { x: 4 }, scale: { x: 1.02, y: 0.99 }, rot: 0.8, ease: 'easeOut' },
      { phase: 'recovery', at: 0.0, image: 'bite_follow', hold: true, tag: 'followThrough',
        offset: { x: 4 }, rot: 0.9, ease: 'easeOut' },
      { phase: 'recovery', at: 0.2, image: 'bite_follow', hold: true,
        offset: { x: 1 }, rot: 0.2, ease: 'easeInOut' },
      { phase: 'recovery', at: 0.45, image: 'rush_overextend', hold: true,
        offset: { x: 3 }, rot: 0.8, ease: 'easeInOut' },
      { phase: 'recovery', at: 0.62, image: 'rush_overextend', hold: true,
        offset: { x: 1 }, rot: 0.2, ease: 'easeInOut' },
      { phase: 'recovery', at: 0.85, image: 'bite_recover', tag: 'recover', ease: 'easeOut' },
    ],
    // 大暴れ (850/500/950): 跳ね上がり🔴 → 踏み砕き → 着地の沈み (grammar 5.7 land_settle)。
    rampage: [
      { phase: 'telegraph', at: 0.0, image: 'idle', hold: true, ease: 'easeIn' },
      { phase: 'telegraph', at: 0.12, image: 'idle',
        offset: { y: 4 }, scale: { x: 1.03, y: 0.94 }, rot: 0.3, ease: 'easeIn' },
      { phase: 'telegraph', at: 0.3, image: 'rampage_leap', hold: true, tag: 'anticipation',
        offset: { y: 5 }, scale: { x: 1.04, y: 0.92 }, ease: 'easeOut' },
      { phase: 'telegraph', at: 0.55, image: 'rampage_leap', hold: true,
        offset: { y: -40 }, scale: { x: 0.97, y: 1.05 }, ease: 'easeOut' },
      { phase: 'telegraph', at: 0.80, image: 'rampage_leap', hold: true,
        offset: { y: -66 }, scale: { x: 0.96, y: 1.06 }, rot: -1.0, ease: 'easeIn' },
      { phase: 'telegraph', at: 0.93, image: 'rampage_leap', hold: true,
        offset: { y: -48 }, scale: { x: 0.97, y: 1.0 }, ease: 'easeIn' },
      { phase: 'active', at: 0.0, image: 'rampage_slam', hold: true, tag: 'contact',
        offset: { y: 0 }, scale: { x: 1.10, y: 0.88 }, rot: 0.6, ease: 'easeOut' },
      { phase: 'active', at: 0.18, image: 'rampage_slam', hold: true,
        scale: { x: 1.02, y: 0.99 }, rot: 0.2, ease: 'easeOut' },
      { phase: 'active', at: 0.5, image: 'rampage_slam', hold: true, ease: 'easeInOut' },
      { phase: 'active', at: 0.8, image: 'rampage_slam', hold: true,
        offset: { y: 1 }, scale: { y: 0.995 }, ease: 'easeInOut' },
      // settle 0.15 = slam の静止を 142ms に短縮 (active 500ms と合わせ同一絵 785ms 連続の紙芝居化を防ぐ)
      { phase: 'recovery', at: 0.0, image: 'rampage_slam', hold: true,
        offset: { y: 1 }, scale: { y: 0.995 }, ease: 'easeInOut' },
      { phase: 'recovery', at: 0.15, image: 'slam_settle', hold: true, tag: 'followThrough',
        offset: { y: 2 }, scale: { x: 1.01, y: 0.985 }, ease: 'easeInOut' },
      { phase: 'recovery', at: 0.35, image: 'slam_settle', hold: true, ease: 'easeInOut' },
      { phase: 'recovery', at: 0.52, image: 'slam_settle', hold: true,
        offset: { y: 2 }, scale: { x: 1.01, y: 0.985 }, ease: 'easeInOut' },
      { phase: 'recovery', at: 0.7, image: 'bite_recover', tag: 'recover' },
    ],
    rampageV5: buildRampageV5Timeline(),
    // 壁跳ね・往 (720/620/500): 低い疾走の構え (突進の弓なりと別シルエット) → 疾走の形。
    feralWallDash: [
      { phase: 'telegraph', at: 0.0, image: 'idle', hold: true, ease: 'easeIn' },
      { phase: 'telegraph', at: 0.1, image: 'idle',
        offset: { y: 3 }, scale: { x: 1.02, y: 0.96 }, ease: 'easeIn' },
      { phase: 'telegraph', at: 0.25, image: 'dash_crouch', hold: true, tag: 'anticipation',
        offset: { y: 5 }, scale: { x: 1.05, y: 0.92 }, rot: 0.5, ease: 'easeInOut' },
      { phase: 'telegraph', at: 0.60, image: 'dash_crouch', hold: true,
        offset: { x: -4, y: 6 }, scale: { x: 1.06, y: 0.90 }, rot: -0.4, ease: 'easeIn' },
      { phase: 'telegraph', at: 0.88, image: 'dash_crouch', hold: true,
        offset: { x: 0, y: 4 }, scale: { x: 1.04, y: 0.94 }, rot: 1.0, ease: 'easeIn' },
      { phase: 'active', at: 0.0, image: 'dash_form', hold: true, tag: 'contact',
        offset: { y: 0 }, scale: { x: 1.06, y: 0.96 }, rot: 1.4, ease: 'easeOut' },
      { phase: 'active', at: 0.12, image: 'dash_form', hold: true,
        offset: { y: -5 }, scale: { x: 1.03, y: 1.0 }, rot: 1.0, ease: 'easeInOut' },
      { phase: 'active', at: 0.28, image: 'dash_form', hold: true,
        offset: { y: 1 }, scale: { x: 1.05, y: 0.97 }, rot: 1.6, ease: 'easeInOut' },
      { phase: 'active', at: 0.44, image: 'dash_form', hold: true,
        offset: { y: -5 }, scale: { x: 1.03, y: 1.0 }, rot: 1.0, ease: 'easeInOut' },
      { phase: 'active', at: 0.60, image: 'dash_form', hold: true,
        offset: { y: 1 }, scale: { x: 1.05, y: 0.97 }, rot: 1.6, ease: 'easeInOut' },
      { phase: 'active', at: 0.76, image: 'dash_form', hold: true,
        offset: { y: -4 }, scale: { x: 1.03, y: 1.0 }, rot: 1.1, ease: 'easeInOut' },
      { phase: 'active', at: 0.92, image: 'dash_form', hold: true,
        offset: { y: 0 }, scale: { x: 1.04, y: 0.98 }, rot: 1.4, ease: 'easeInOut' },
      { phase: 'recovery', at: 0.0, image: 'dash_form', hold: true,
        offset: { y: 0 }, scale: { x: 1.03, y: 0.98 }, rot: 1.0, ease: 'easeOut' },
      { phase: 'recovery', at: 0.35, image: 'dash_form', hold: true,
        offset: { y: -2 }, rot: 0.5, ease: 'easeInOut' },
      { phase: 'recovery', at: 0.7, image: 'dash_form', hold: true, ease: 'easeOut' },
    ],
    // 壁跳ね・復: 後ろ足を壁に置いたまま溜め、蹴り出してから床へ戻る。
    feralWallDashBack: [
      // 壁にいる間は足の位置を固定。床の構えへ落ちてから発進する旧キーを外す。
      { phase: 'telegraph', at: 0.0, image: 'wall_kick', hold: true, tag: 'anticipation',
        offset: { y: -58 } },
      { phase: 'active', at: 0.0, image: 'dash_form', hold: true, tag: 'contact',
        offset: { y: -58 }, ease: 'easeIn' },
      { phase: 'active', at: 0.28, image: 'dash_form', hold: true, offset: { y: 0 } },
      { phase: 'recovery', at: 0.0, image: 'rush_overextend', hold: true, tag: 'followThrough' },
      { phase: 'recovery', at: 0.6, image: 'bite_recover', tag: 'recover' },
    ],
    // 軋む咆哮 (2900/300/900): 頭を上げ胸を張る最大の縦。 衝撃波は roarWave hazard (コードFX)。
    //   溜め中の3波 (1900/2300/2700ms = 0.655/0.793/0.931) ごとに roar_up の伸びを強める。
    //   offset は描画専用。波の時刻・判定・boss.x・影は動かさない。
    feralRoar: [
      { phase: 'telegraph', at: 0.0, image: 'idle', hold: true },
      { phase: 'telegraph', at: 0.08, image: 'idle',
        offset: { y: 2 }, scale: { x: 1.015, y: 0.985 }, rot: 0.15, ease: 'easeIn' },
      // 🔴 伸び上がりの主役は scale.y (足元ピボットで縦に伸びる = 足は床に残る)。
      //   offset.y を主役にすると獣ごと宙に浮く (2026-08-08 実機で確認して差し替えた)。
      //   offset.y は「沈み込み」と「解放の突き上げ」の味付けだけに使う。
      { phase: 'telegraph', at: 0.15, image: 'roar_up', hold: true, tag: 'anticipation',
        offset: { y: 0 }, scale: { x: 1, y: 1 }, rot: 0, ease: 'easeOut' },
      // 切り替わった roar_up が深く沈み (力を溜める)、そこから重く伸び上がる。
      { phase: 'telegraph', at: 0.20, image: 'roar_up', hold: true,
        offset: { y: 5 }, scale: { x: 1.05, y: 0.93 }, rot: 0.4, ease: 'easeIn' },
      // 「軋む」= 伸びながら震える。溜めの後半ずっと続く (一瞬の震えでは読めない)。
      //   乱数は使えないので、同じ1枚を往復させるキーを等間隔で置く。
      //   往復幅は前へ進むほど大きく = 限界が近づく。
      { phase: 'telegraph', at: 0.34, image: 'roar_up', hold: true,
        offset: { y: 1 }, scale: { x: 1.02, y: 0.98 }, rot: -0.5 },
      { phase: 'telegraph', at: 0.42, image: 'roar_up', hold: true,
        offset: { y: -2, x: 2 }, scale: { x: 1.0, y: 1.01 }, rot: 0.7 },
      { phase: 'telegraph', at: 0.49, image: 'roar_up', hold: true,
        offset: { y: -4, x: -2 }, scale: { x: 0.99, y: 1.03 }, rot: -0.9 },
      { phase: 'telegraph', at: 0.55, image: 'roar_up', hold: true,
        offset: { y: -5, x: 3 }, scale: { x: 0.985, y: 1.05 }, rot: 1.1 },
      { phase: 'telegraph', at: 0.60, image: 'roar_up', hold: true,
        offset: { y: -6, x: -3 }, scale: { x: 0.98, y: 1.06 }, rot: -1.2, ease: 'easeInOut' },
      // 🔴 一声ぶんの「突き上げ」は、助走 → 頂点 → 戻り の3キーで作る。
      //   波の発火点にいきなり頂点のキーを置くと、直前キーとの差が 15ms = 1フレームで跳ね、
      //   カクッと見える (2026-08-08 ユーザー指摘)。頂点の 120ms 手前から溜め直して加速する。
      // 1波目 (床の波 1900ms)。
      { phase: 'telegraph', at: 0.612, image: 'roar_up', hold: true,
        offset: { y: -4, x: 2 }, scale: { x: 1.0, y: 1.03 }, rot: 0.9, ease: 'easeIn' },
      { phase: 'telegraph', at: 0.655, image: 'roar_up', hold: true,
        offset: { y: -8 }, scale: { x: 0.955, y: 1.12 }, rot: -0.4, ease: 'easeOut' },
      { phase: 'telegraph', at: 0.717, image: 'roar_up', hold: true,
        offset: { y: -2 }, scale: { x: 1.01, y: 1.045 }, rot: 0.3, ease: 'easeInOut' },
      // 2波目 (2300ms)。息を吸っている間も止めない。
      { phase: 'telegraph', at: 0.752, image: 'roar_up', hold: true,
        offset: { y: -3 }, scale: { x: 1.005, y: 1.05 }, rot: 0.1, ease: 'easeIn' },
      { phase: 'telegraph', at: 0.793, image: 'roar_up', hold: true,
        offset: { y: -11 }, scale: { x: 0.94, y: 1.16 }, rot: -0.6, ease: 'easeOut' },
      { phase: 'telegraph', at: 0.855, image: 'roar_up', hold: true,
        offset: { y: -4 }, scale: { x: 1.0, y: 1.065 }, rot: 0.3, ease: 'easeInOut' },
      // 3波目 (2700ms)。最大まで伸び、active 中は同じ roar_up のままわずかに落ちる。
      { phase: 'telegraph', at: 0.890, image: 'roar_up', hold: true,
        offset: { y: -5 }, scale: { x: 0.995, y: 1.075 }, rot: 0.1, ease: 'easeIn' },
      { phase: 'telegraph', at: 0.931, image: 'roar_up', hold: true,
        offset: { y: -15 }, scale: { x: 0.92, y: 1.22 }, rot: -0.9 },
      { phase: 'active', at: 0.0, image: 'roar_up', hold: true, tag: 'contact',
        offset: { y: -15 }, scale: { x: 0.92, y: 1.22 }, rot: -0.9, ease: 'easeIn' },
      { phase: 'active', at: 1.0, image: 'roar_up', hold: true,
        offset: { y: -8 }, scale: { x: 0.96, y: 1.12 }, rot: -0.3 },
      // 🔴 締めは四つ足へ降りる (bite_recover)。
      //   ~~slam_settle~~ は「腹ばいに寝そべる」絵で、大暴れで床を叩き潰した後のもの。
      //   立ち上がって吠えた後に出すと**おすわりに見える** (2026-08-08 ユーザー指摘)。
      //   降りてきた衝撃は絵を替えずに潰し (縦を縮め横を広げる) で出す。
      { phase: 'recovery', at: 0.0, image: 'bite_recover', hold: true,
        offset: { y: 4 }, scale: { x: 1.06, y: 0.93 }, rot: 0.2, ease: 'easeOut' },
      { phase: 'recovery', at: 0.18, image: 'bite_recover', hold: true,
        offset: { y: 0 }, scale: { x: 1.0, y: 1.01 }, rot: 0, ease: 'easeInOut' },
      { phase: 'recovery', at: 0.6, image: 'bite_recover', tag: 'recover' },
    ],
    // 暴噛・終 (致命 450/250/750): 錯乱の噛み固め。
    kingExecutionAwoken: [
      { phase: 'telegraph', at: 0.0, image: 'idle', hold: true, ease: 'easeIn' },
      { phase: 'telegraph', at: 0.15, image: 'idle', hold: true,
        offset: { x: -4 }, scale: { x: 0.98, y: 1.02 }, rot: -0.8, ease: 'easeIn' },
      { phase: 'telegraph', at: 0.3, image: 'exec_windup', hold: true, tag: 'anticipation',
        offset: { x: -8 }, scale: { x: 0.97, y: 1.03 }, rot: -1.3, ease: 'easeIn' },
      { phase: 'telegraph', at: 0.72, image: 'exec_windup', hold: true,
        offset: { x: -11 }, scale: { x: 0.96, y: 1.04 }, rot: -1.5, ease: 'easeIn' },
      { phase: 'active', at: 0.0, image: 'exec_bite', hold: true, tag: 'contact',
        offset: { x: 9 }, scale: { x: 1.05, y: 0.96 }, rot: 1.5, ease: 'easeOut' },
      { phase: 'active', at: 0.35, image: 'exec_bite', hold: true,
        offset: { x: 6 }, scale: { x: 1.02, y: 0.99 }, rot: 1.1, ease: 'easeInOut' },
      { phase: 'active', at: 0.75, image: 'exec_bite', hold: true,
        offset: { x: 7, y: -2 }, rot: 1.5, ease: 'easeInOut' },
      { phase: 'recovery', at: 0.0, image: 'exec_bite', hold: true,
        offset: { x: 4, y: 1 }, rot: 0.4, ease: 'easeInOut' },
      { phase: 'recovery', at: 0.16, image: 'exec_bite', hold: true,
        offset: { x: 8, y: -3 }, rot: 1.5, ease: 'easeInOut' },
      { phase: 'recovery', at: 0.30, image: 'exec_bite', hold: true,
        offset: { x: 3, y: 1 }, rot: 0.2, ease: 'easeInOut' },
      { phase: 'recovery', at: 0.42, image: 'exec_bite', hold: true,
        offset: { x: 7, y: -2 }, rot: 1.4, ease: 'easeInOut' },
      { phase: 'recovery', at: 0.5, image: 'bite_recover', tag: 'recover', ease: 'easeOut' },
    ],
  },
}

// Adopted 2026-09-24. Keep the earlier timelines for the DEV before/after lab.
export const AWOKEN_PREVIOUS_RUSH_TIMELINE = BOSS_ATTACK_TIMELINES['crowned-king-awoken']!.feralRush!
export const AWOKEN_PREVIOUS_CHASE_TIMELINE = BOSS_ATTACK_TIMELINES['crowned-king-awoken']!.feralRushChase!
BOSS_ATTACK_TIMELINES['crowned-king-awoken']!.feralRush = AWOKEN_RUSH_STUDY_TIMELINE
BOSS_ATTACK_TIMELINES['crowned-king-awoken']!.feralRushChase = awokenChaseStudyTimeline(AWOKEN_PREVIOUS_CHASE_TIMELINE)

// 戴冠獣の溜めの絵 (2026-10-07 ユーザー「全ての絵に対してidleとの連続性があるか確認してほしい」→「待機を手本に描き直す」)。
//   新＝待機を土台に姿勢だけ変えた3枚。旧＝DEV ?houndArt=old (game.ts) で戻す比較用。旧の絵のファイルは消さない。
const HOUND_ART_OLD: Record<string, SpriteKey> = {
  houndPounce_rearup: 'houndExecution_windup',
  houndBite_windup_1007: 'houndBite_windup',
  houndLunge_windup_1007: 'houndLunge_windup',
}
let houndArtOld = false
export function getHoundArtOld(): boolean { return houndArtOld }
export function setHoundArtOld(old: boolean): void {
  if (old === houndArtOld) return
  houndArtOld = old
  const from = old ? HOUND_ART_OLD : Object.fromEntries(Object.entries(HOUND_ART_OLD).map(([n, o]) => [o, n as SpriteKey]))
  for (const timeline of Object.values(BOSS_ATTACK_TIMELINES['dream-hound']!)) {
    for (const key of timeline) if (from[key.image]) key.image = from[key.image]!
  }
}


// 介錯・山なり榴弾の反動 (2026-10-19・検収パック 1021 の E-4 ユーザー「迫撃砲は高く掲げて打ち上げるのに、反動で砲身が下がり、撃った後に肩をすくめた絵が出る」)。
//   今の版は撃つ瞬間に mortar_fire (砲を水平近くへ下ろし肩をすくめた絵) へ替わり、また mortar_aim へ戻る＝砲が上下する。
//   1021 版 = 絵は mortar_aim のまま替えない。撃つ前に膝で踏ん張り、撃った瞬間に反動で体が押し下げられて沈み、
//   膝で押し上げてから構えを解く (1022 問3「打った球に引っ張られるように上に跳ねちゃってる…打ったら体が沈むはず」で上へ跳ねる形から直した)。弾の出発 0.64 と技の尺は1msも変えない。
//   1022 で「直した版の方が良い」→ 配布版へ。前の版＝DEV ?mortarRecoil=now。研究 = docs/research/2026-10-19-awoken-executioner-1021-fixes.md
export const EXEC_CANNONMORTAR_TIMELINE_NOW = BOSS_ATTACK_TIMELINES['the-executioner']!.exec_cannonMortar!
export const EXEC_CANNONMORTAR_TIMELINE_1021: BossAttackTimeline = [
  { phase: 'telegraph', at: 0.0, image: 'idle', hold: true },
  { phase: 'telegraph', at: 0.15, image: 'mortar_aim', hold: true, tag: 'anticipation',
    ease: 'easeOut', offset: { y: 6 }, scale: { x: 1.02, y: 0.98 } },
  // 撃つ直前の踏ん張り: 膝を少し曲げ、前へ体重を掛けて撃つまで止める。
  { phase: 'telegraph', at: 0.5, image: 'mortar_aim', hold: true, ease: 'step',
    offset: { x: 2, y: 8 }, scale: { x: 1.03, y: 0.97 } },
  { phase: 'telegraph', at: 0.63, image: 'mortar_aim', hold: true,
    offset: { x: 2, y: 8 }, scale: { x: 1.03, y: 0.97 } },
  // 撃った瞬間: 上へ撃った反動で体が押し下げられ、一気に沈む (10-19 検収パック 1022 問3「打ったら体が沈むはず」)。
  //   砲口は上を向いたまま、体は少し後ろへずれて膝で潰れる。上へは跳ねない。
  { phase: 'telegraph', at: 0.64, image: 'mortar_aim', hold: true, ease: 'easeOut',
    offset: { x: -8, y: 20 }, scale: { x: 1.08, y: 0.9 }, rot: -2 },
  { phase: 'telegraph', at: 0.85, image: 'mortar_aim', hold: true, ease: 'easeInOut',
    offset: { x: -7, y: 16 }, scale: { x: 1.06, y: 0.93 }, rot: -2 },
  // 弾が落ちる間: 沈んだ体を膝でゆっくり押し上げていく。
  { phase: 'active', at: 0.0, image: 'mortar_aim', hold: true, tag: 'contact', ease: 'easeInOut',
    offset: { x: -6, y: 11 }, scale: { x: 1.04, y: 0.95 }, rot: -1 },
  { phase: 'recovery', at: 0.0, image: 'mortar_aim', hold: true, ease: 'easeInOut',
    offset: { x: -4, y: 7 }, scale: { x: 1.02, y: 0.98 } },
  { phase: 'recovery', at: 0.5, image: 'mortar_aim', hold: true, ease: 'easeInOut', offset: { x: -2, y: 3 } },
  { phase: 'recovery', at: 0.8, image: 'idle', hold: true },
]
export type ExecutionerMortarRecoil = 'now' | '1021'
let executionerMortarRecoil: ExecutionerMortarRecoil = '1021'
export function getExecutionerMortarRecoil(): ExecutionerMortarRecoil { return executionerMortarRecoil }
export function setExecutionerMortarRecoil(raw: string | null): void {
  executionerMortarRecoil = import.meta.env.DEV && raw === 'now' ? 'now' : '1021'
  BOSS_ATTACK_TIMELINES['the-executioner']!.exec_cannonMortar =
    executionerMortarRecoil === '1021' ? EXEC_CANNONMORTAR_TIMELINE_1021 : EXEC_CANNONMORTAR_TIMELINE_NOW
}

// 介錯の「長く映る不自然な絵」を減らす比較版 (2026-10-19・検収パック 1021 の E-4 ユーザー
//   「叩きつけ・叩きつけた後の絵・溜めている間の絵…不自然な絵が視界に映っている時間が長いので、作りが甘い絵に見える」)。
//   測ると、振り下ろしの硬直は overhead_recover (刃先を床へ預けた棒立ちに近い絵) を 1200ms、
//   地割りの硬直は slam_hold 688ms → overhead_recover 563ms を出し続けていた。どちらも技で一番長く映る絵。
//   1021 版 = 硬直は当たった絵 (overhead_contact / quake_contact) のまま体を起こしていき、途中で待機へ戻す。
//   棒立ちの絵を出さない。秒数・判定・tag は不変＝描画だけ。溜めの絵 (slam_windup / slam_hold) は描き直しが要るので
//   ここでは触らない。1022 で「直した版の方が良い」→ 配布版へ。前の版＝DEV ?execHolds=now。
//   研究 = docs/research/2026-10-19-awoken-executioner-1021-fixes.md
export const EXEC_MAULDROP_TIMELINE_NOW = BOSS_ATTACK_TIMELINES['the-executioner']!.exec_maulDrop!
export const EXEC_MAULQUAKE_TIMELINE_NOW = BOSS_ATTACK_TIMELINES['the-executioner']!.exec_maulQuake!
export const EXEC_MAULDROP_TIMELINE_1021: BossAttackTimeline = [
  ...EXEC_MAULDROP_TIMELINE_NOW.filter((k) => k.phase !== 'recovery'),
  // 刃を床に打ち込んだまま、潰れた体を起こす (重い物を引き抜く間)。
  { phase: 'recovery', at: 0.0, image: 'overhead_contact', ease: 'easeInOut',
    offset: { y: 4 }, scale: { x: 1.03, y: 0.96 } },
  { phase: 'recovery', at: 0.38, image: 'overhead_contact', hold: true, ease: 'easeInOut',
    offset: { y: 0 }, scale: { x: 1, y: 1 } },
  { phase: 'recovery', at: 0.42, image: 'idle', ease: 'easeOut', offset: { y: 3 }, scale: { x: 1.01, y: 0.985 } },
  { phase: 'recovery', at: 0.7, image: 'idle', hold: true, offset: { y: 0 }, scale: { x: 1, y: 1 } },
]
export const EXEC_MAULQUAKE_TIMELINE_1021: BossAttackTimeline = [
  ...EXEC_MAULQUAKE_TIMELINE_NOW.filter((k) => k.phase !== 'recovery'),
  { phase: 'recovery', at: 0.0, image: 'quake_contact', ease: 'easeInOut',
    offset: { y: 8 }, scale: { x: 1.05, y: 0.94 } },
  { phase: 'recovery', at: 0.4, image: 'quake_contact', hold: true, ease: 'easeInOut',
    offset: { y: 1 }, scale: { x: 1, y: 1 } },
  { phase: 'recovery', at: 0.44, image: 'idle', ease: 'easeOut', offset: { y: 3 }, scale: { x: 1.01, y: 0.985 } },
  { phase: 'recovery', at: 0.72, image: 'idle', hold: true, offset: { y: 0 }, scale: { x: 1, y: 1 } },
]
export type ExecutionerHolds = 'now' | '1021'
let executionerHolds: ExecutionerHolds = '1021'
export function getExecutionerHolds(): ExecutionerHolds { return executionerHolds }
export function setExecutionerHolds(raw: string | null): void {
  executionerHolds = import.meta.env.DEV && raw === 'now' ? 'now' : '1021'
  const t = BOSS_ATTACK_TIMELINES['the-executioner']!
  t.exec_maulDrop = executionerHolds === '1021' ? EXEC_MAULDROP_TIMELINE_1021 : EXEC_MAULDROP_TIMELINE_NOW
  t.exec_maulQuake = executionerHolds === '1021' ? EXEC_MAULQUAKE_TIMELINE_1021 : EXEC_MAULQUAKE_TIMELINE_NOW
}
// 介錯の振り下ろしに、着弾後の沈みの絵を1枚足した比較版 (2026-10-22)。
//   作者の答え：引き絞りの絵は「武器が太くなったり、体の大きさが変わったりする」ので入れない (溜めは 1021 版のまま)。
//   沈みは「武器の太さがおかしい…直せるなら入れた方が重みが出る」→ 当たった絵 (overhead_contact) から刃・服・足を変えずに
//   体だけ沈めた絵 exec_key_settle_1022 に描き直した。絵そのものが沈んでいるので、変形 (縮め・ずらし) はかけない。
//   秒数・判定・tag は 1021 版のまま＝描画だけ。開発版の ?execTwoKeys=1 でだけ入る。記録 = docs/research/2026-10-22-exec-settle-redo.md
export const EXEC_MAULDROP_TIMELINE_TWOKEYS: BossAttackTimeline = [
  ...EXEC_MAULDROP_TIMELINE_1021.filter((k) => k.phase === 'telegraph'),
  ...EXEC_MAULDROP_TIMELINE_1021.filter((k) => k.phase === 'active' && k.at < 0.5),
  { phase: 'active', at: 0.55, image: 'exec_key_settle_1022', hold: true, ease: 'easeOut' },
  { phase: 'recovery', at: 0.0, image: 'exec_key_settle_1022', ease: 'easeInOut' },
  { phase: 'recovery', at: 0.38, image: 'exec_key_settle_1022', hold: true, ease: 'easeInOut' },
  { phase: 'recovery', at: 0.42, image: 'idle', ease: 'easeOut', offset: { y: 3 }, scale: { x: 1.01, y: 0.985 } },
  { phase: 'recovery', at: 0.7, image: 'idle', hold: true, offset: { y: 0 }, scale: { x: 1, y: 1 } },
]
let executionerTwoKeys = false
export function getExecutionerTwoKeys(): boolean { return executionerTwoKeys }
// setExecutionerHolds の後に呼ぶ (振り下ろしの並びを上書きするため)。
export function setExecutionerTwoKeys(raw: string | null): void {
  executionerTwoKeys = import.meta.env.DEV && raw === '1'
  if (executionerTwoKeys) BOSS_ATTACK_TIMELINES['the-executioner']!.exec_maulDrop = EXEC_MAULDROP_TIMELINE_TWOKEYS
}
// 配布版は URL を読まないので、読み込んだ時点で採用した版 (1021) を入れておく。
setExecutionerMortarRecoil(null)
setExecutionerHolds(null)

// 介錯の致命「執行」= 据え斬り (2026-10-20・作者の答え exec-finisher-1019「A 真上から一太刀」「斬った後は刃を払って構えへ」)。
//   刃を真上へ掲げて止まり (sue_raise)、一太刀で振り下ろして床際で止め (sue_contact)、刃を後ろへ払って (sue_flick) 立ち姿へ戻る。
//   拍 (600/250/900ms)・当たり・contact の 35%・anticipation の 70% は前のまま = 描画だけ。前の並びは ?execFinisherFx=old。
//   絵は刃の形にしか無いので、砲を持っている間は前の並びへ落とす (sprites.ts resolveBossTimelinePose)。
export const EXEC_FINISHER_TIMELINE_OLD: BossAttackTimeline = BOSS_ATTACK_TIMELINES['the-executioner']!.executioner_finisher!
export const EXEC_FINISHER_TIMELINE_SUE: BossAttackTimeline = [
  // 掲げる: 少し沈んでから伸び上がり、いちばん高い所で止める (止めの長さが「来る」の合図)
  { phase: 'telegraph', at: 0.0, image: 'sue_raise', hold: true, ease: 'easeOut', offset: { x: 0, y: 6 }, scale: { x: 1.01, y: 0.98 } },
  { phase: 'telegraph', at: 0.14, image: 'sue_raise', hold: true, ease: 'easeOut', offset: { x: 0, y: 0 }, scale: { x: 1, y: 1 } },
  { phase: 'telegraph', at: 0.7, image: 'sue_raise', hold: true, tag: 'anticipation', ease: 'easeIn', offset: { x: -2, y: -6 }, scale: { x: 0.99, y: 1.02 } },
  { phase: 'telegraph', at: 0.86, image: 'sue_raise', hold: true, ease: 'easeIn', offset: { x: -2, y: -6 }, scale: { x: 0.99, y: 1.02 } },
  // 一太刀: 掲げた絵のまま一段伸びてから、打点の絵へ切る (中割りを挟まない = 真上から一息に落ちる)
  { phase: 'active', at: 0.0, image: 'sue_raise', hold: true, ease: 'easeIn', offset: { x: -2, y: -8 }, scale: { x: 0.99, y: 1.03 } },
  { phase: 'active', at: 0.35, image: 'sue_contact', hold: true, tag: 'contact', ease: 'easeOut', offset: { x: 6, y: 0 }, scale: { x: 1.04, y: 0.96 } },
  { phase: 'active', at: 0.6, image: 'sue_contact', hold: true, ease: 'easeInOut', offset: { x: 5, y: 0 }, scale: { x: 1, y: 1 } },
  // 床際で止めたまま間を置き、刃を後ろへ払って構えへ戻る
  { phase: 'recovery', at: 0.0, image: 'sue_contact', hold: true, ease: 'easeOut', offset: { x: 5, y: 0 }, scale: { x: 1.01, y: 0.99 } },
  { phase: 'recovery', at: 0.25, image: 'sue_contact', hold: true, ease: 'easeInOut', offset: { x: 4, y: 0 }, scale: { x: 1, y: 1 } },
  { phase: 'recovery', at: 0.35, image: 'sue_flick', hold: true, ease: 'easeOut', offset: { x: -3, y: 2 }, scale: { x: 1.02, y: 0.98 } },
  { phase: 'recovery', at: 0.65, image: 'sue_flick', hold: true, ease: 'easeInOut', offset: { x: 0, y: 0 }, scale: { x: 1, y: 1 } },
  { phase: 'recovery', at: 0.8, image: 'idle', hold: true, ease: 'easeOut', offset: { x: 0, y: 0 }, scale: { x: 1, y: 1 } },
]
export type ExecFinisherFx = 'sue' | 'old'
let execFinisherFx: ExecFinisherFx = 'sue'
export function getExecFinisherFx(): ExecFinisherFx { return execFinisherFx }
export function setExecFinisherFx(raw: string | null): void {
  execFinisherFx = raw === 'old' ? 'old' : 'sue'
  BOSS_ATTACK_TIMELINES['the-executioner']!.executioner_finisher =
    execFinisherFx === 'old' ? EXEC_FINISHER_TIMELINE_OLD : EXEC_FINISHER_TIMELINE_SUE
}
setExecFinisherFx(null)
